import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useLazyQuery, gql } from '@apollo/client';
import { useOutletContext } from 'react-router-dom';
import {
  Box, Typography, Radio, Divider, Button, Popper, Grow, Paper, Checkbox, FormControlLabel,
  ClickAwayListener, IconButton, ListSubheader, TextField, InputAdornment, Tooltip, Chip
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LabelFilterMenu, { LabelFilterPair } from './LabelFilterMenu';

// --- MODIFIED GraphQL Query Definition ---
const GET_BUDGET_FILTER_OPTIONS = gql`
  query GetBudgetFilterOptions($input: BudgetFilterOptionsInput!) {
    getBudgetFilterOptions(input: $input) {
      dataSources { id, name, providerType, isSyncing }
      activeRegions, availableRegions, activeServices, availableServices, projectIds
      labelKeys, k8sClusters, k8sNamespaces, skus
    }
  }
`;

const GET_LABEL_VALUES = gql`
  query GetBudgetLabelValues($input: BudgetLabelValuesInput!) {
    getBudgetLabelValues(input: $input) {
      values
    }
  }
`;

// --- MODIFIED Interface ---
export interface ServiceState {
  id: string;
  dataSources: string[];
  projectIds: string[];
  regions: string[];
  serviceTypes: string[];
  label: LabelFilterPair[];
  cluster: string[];
  namespace: string[];
  deployment: string[];
  skus: string[]; // Added for LANGFUSE models
}

interface ScopeConfigProps {
    provider: string;
    setProvider: (provider: string) => void;
    services: ServiceState[];
    setServices: React.Dispatch<React.SetStateAction<ServiceState[]>>;
    getInitialService: () => Omit<ServiceState, 'id' | 'serviceTypes'> & { serviceTypes: string[] };
    errors: { [key: string]: string };
    disabled?: boolean;
    period: 'Monthly' | 'Quarterly' | 'Yearly';
}

interface AppOutletContext {
  currentTenant?: { id: string; name: string; };
}

interface DataSourceOption {
  id: string;
  name: string;
  providerType: string;
  isSyncing: boolean;
}

// SectionedMultiSelectDropdown remains unchanged, but is now unused in the main component
const SectionedMultiSelectDropdown = React.forwardRef<
  HTMLButtonElement,
  {
    label: string;
    options: { active: string[]; available: string[] };
    selectedValues: string[];
    onChange: (selected: string[]) => void;
    disabled?: boolean;
  }
>(({ label, options, selectedValues, onChange, disabled = false }, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const anchorRef = useRef<HTMLButtonElement>(null);

  const handleRef = useCallback((node: HTMLButtonElement) => {
    (anchorRef as React.MutableRefObject<HTMLButtonElement | null>).current = node;
    if (typeof ref === 'function') {
      ref(node);
    } else if (ref) {
      (ref as React.MutableRefObject<HTMLButtonElement | null>).current = node;
    }
  }, [ref]);

  const handleOpen = () => setIsOpen(true);
  const handleClose = () => {
    setIsOpen(false);
    setSearchTerm('');
  };

  const filteredActive = useMemo(() => options.active.filter(opt => opt.toLowerCase().includes(searchTerm.toLowerCase())), [options.active, searchTerm]);
  const filteredAvailable = useMemo(() => options.available.filter(opt => opt.toLowerCase().includes(searchTerm.toLowerCase())), [options.available, searchTerm]);
  
  const isKubeSelected = useMemo(() => selectedValues.includes('Kubernetes Engine'), [selectedValues]);

  const isAnyActiveSelected = useMemo(() => options.active.some(opt => selectedValues.includes(opt) && opt !== 'Kubernetes Engine'), [options.active, selectedValues]);
  const isAnyAvailableSelected = useMemo(() => options.available.some(opt => selectedValues.includes(opt) && opt !== 'Kubernetes Engine'), [options.available, selectedValues]);

  const nonKubeFilteredActive = useMemo(() => filteredActive.filter(s => s !== 'Kubernetes Engine'), [filteredActive]);
  const nonKubeFilteredAvailable = useMemo(() => filteredAvailable.filter(s => s !== 'Kubernetes Engine'), [filteredAvailable]);

  const isAllActiveSelected = nonKubeFilteredActive.length > 0 && nonKubeFilteredActive.every(s => selectedValues.includes(s));
  const isSomeActiveSelected = nonKubeFilteredActive.some(s => selectedValues.includes(s)) && !isAllActiveSelected;

  const isAllAvailableSelected = nonKubeFilteredAvailable.length > 0 && nonKubeFilteredAvailable.every(s => selectedValues.includes(s));
  const isSomeAvailableSelected = nonKubeFilteredAvailable.some(s => selectedValues.includes(s)) && !isAllAvailableSelected;

  const handleToggle = (value: string) => {
    if (value === 'Kubernetes Engine') {
      onChange(isKubeSelected ? [] : ['Kubernetes Engine']);
      return;
    }
    if (isKubeSelected) return;

    const isTogglingActive = options.active.includes(value);

    if (isTogglingActive && isAnyAvailableSelected) {
      onChange([value]);
      return;
    }
    if (!isTogglingActive && isAnyActiveSelected) {
      onChange([value]);
      return;
    }

    const newSelected = selectedValues.includes(value)
      ? selectedValues.filter(v => v !== value)
      : [...selectedValues, value];
    onChange(newSelected);
  };

  const handleSelectAll = (itemsToToggle: string[], isSelecting: boolean) => {
    if (isSelecting) {
      const nonKubeItems = itemsToToggle.filter(v => v !== 'Kubernetes Engine');
      onChange(nonKubeItems);
    } else {
      onChange([]);
    }
  };

  const allOptions = [...options.active, ...options.available];
  
  const buttonText =
    selectedValues.length === 0 ? `Select ${label.toLowerCase()}`
    : selectedValues.length === allOptions.length && allOptions.length > 0 ? 'All selected'
    : `${selectedValues.length} selected`;

    const renderOption = (option: string) => {
        const isThisOptionActive = options.active.includes(option);
        const isThisOptionAvailable = options.available.includes(option);
        
        const isDisabled = 
            (isKubeSelected && option !== 'Kubernetes Engine') || 
            ((isAnyActiveSelected || isAnyAvailableSelected) && option === 'Kubernetes Engine') ||
            (isThisOptionAvailable && isAnyActiveSelected) ||
            (isThisOptionActive && isAnyAvailableSelected);

        return (
          <FormControlLabel
              key={option}
              sx={{ width: '100%', m: 0, px: 2, py: 0.5 }}
              control={
                  <Checkbox
                      size="small"
                      checked={selectedValues.includes(option)}
                      onChange={() => handleToggle(option)}
                      disabled={isDisabled}
                      sx={{ '&.Mui-checked': { color: '#0064FF' } }}
                  />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  <Typography sx={{ fontSize: '13px', color: isDisabled ? '#B0B0B0' : selectedValues.includes(option) ? '#333333' : '#666666' }}>
                    {option}
                  </Typography>
                  {option === 'Kubernetes Engine' && isDisabled && (
                    <Tooltip
                      title="As you have selected other services, Kubernetes Engine is disabled. To select it, please clear other services."
                      arrow
                      componentsProps={{
                        tooltip: {
                          sx: { backgroundColor: '#FFFFFF', color: '#000000', border: '1px solid #E0E0E0', fontSize: '12px', },
                        },
                        arrow: { sx: { color: '#FFFFFF', }, },
                      }}
                    >
                      <InfoOutlinedIcon sx={{ color: '#0064FF', ml: 1, fontSize: '16px', }} />
                    </Tooltip>
                  )}
                </Box>
              }
          />
        );
      };

  return (
    <Box sx={{ width: '100%' }}>
      <Typography sx={{ fontFamily: 'Inter', fontWeight: 400, fontSize: '13px', color: disabled ? '#A0A0A0' : '#333333', mb: 0.5 }}>
        {label}<span style={{ color: 'red' }}>*</span>
      </Typography>
      <Button
        ref={handleRef} variant="outlined" disabled={disabled} onClick={handleOpen}
        endIcon={isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        sx={{
          width: '100%', height: '36px', backgroundColor: disabled ? '#F3F3F3' : '#FFFFFF',
          border: `1px solid ${disabled ? '#D3D3D3' : '#E0E0E0'}`, borderRadius: '8px',
          fontFamily: 'Inter', fontWeight: selectedValues.length > 0 ? 500 : 400, fontSize: '13px',
          color: selectedValues.length > 0 ? '#333333' : '#999999', lineHeight: '18px',
          letterSpacing: '0%', textTransform: 'none', justifyContent: 'space-between',
          '&:hover': { borderColor: disabled ? '#D3D3D3' : '#0064FF' },
        }}
      >
        {buttonText}
      </Button>
      <Popper
        open={isOpen} anchorEl={anchorRef.current} placement="bottom-start"
        transition sx={{ zIndex: 1300, width: anchorRef.current?.offsetWidth }}
      >
        {({ TransitionProps }) => (
          <ClickAwayListener onClickAway={handleClose}>
            <Grow {...TransitionProps} style={{ transformOrigin: 'top left' }}>
              <Paper sx={{ borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.1)', maxHeight: '300px', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ p: 2, borderBottom: '1px solid #f0f0f0' }}>
                    <TextField
                        autoFocus placeholder="Search by service name.." value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyDown={(e) => e.stopPropagation()} fullWidth variant="outlined"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start" sx={{ ml: -0.5 }}>
                                <img src="/icons/search_icon.svg" alt="Search" style={{ width: '16px', height: '16px' }} />
                                </InputAdornment>
                            ),
                            sx: { height: '32px', borderRadius: '10px', backgroundColor: 'rgba(0, 0, 0, 0.04)', '& .MuiOutlinedInput-notchedOutline': { border: 'none' }, '& .MuiInputBase-input::placeholder': { fontSize: '14px', fontWeight: 300 } },
                        }}
                    />
                </Box>
                <Box sx={{ overflowY: 'auto' }}>
                    {filteredActive.length === 0 && filteredAvailable.length === 0 ? (
                        <Typography sx={{ p: 2, color: '#999', fontSize: '13px', textAlign: 'center' }}>No results found</Typography>
                    ) : (
                        <>
                            {filteredActive.length > 0 && (
                                <>
                                    <ListSubheader sx={{ bgcolor: '#F9FAFB', lineHeight: '32px', fontSize: '13px', fontWeight: 600 }}>Active Services</ListSubheader>
                                    <FormControlLabel
                                        sx={{ width: '100%', m: 0, px: 2, py: 0.5 }}
                                        label={<Typography sx={{ fontSize: '13px' }}>All</Typography>}
                                        control={<Checkbox size="small" checked={isAllActiveSelected} indeterminate={isSomeActiveSelected} onChange={(e) => handleSelectAll(filteredActive, e.target.checked)} disabled={isKubeSelected || isAnyAvailableSelected} sx={{'&.Mui-checked, &.MuiCheckbox-indeterminate': { color: '#0064FF' }}} />}
                                    />
                                    {filteredActive.map(renderOption)}
                                </>
                            )}
                            {filteredAvailable.length > 0 && (
                                <>
                                    <ListSubheader sx={{ bgcolor: '#F9FAFB', lineHeight: '32px', fontSize: '13px', fontWeight: 600 }}>Available Services</ListSubheader>
                                    <FormControlLabel
                                        sx={{ width: '100%', m: 0, px: 2, py: 0.5 }}
                                        label={<Typography sx={{ fontSize: '13px' }}>All</Typography>}
                                        control={<Checkbox size="small" checked={isAllAvailableSelected} indeterminate={isSomeAvailableSelected} onChange={(e) => handleSelectAll(filteredAvailable, e.target.checked)} disabled={isKubeSelected || isAnyActiveSelected} sx={{'&.Mui-checked, &.MuiCheckbox-indeterminate': { color: '#0064FF' }}} />}
                                    />
                                    {filteredAvailable.map(renderOption)}
                                </>
                            )}
                        </>
                    )}
                </Box>
                <Divider />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1, flexShrink: 0 }}>
                  <Button onClick={() => onChange([])} sx={{ textTransform: 'none', color: '#999999' }}>Clear</Button>
                  <Button onClick={handleClose} sx={{ textTransform: 'none', color: '#0064FF' }}>Apply</Button>
                </Box>
              </Paper>
            </Grow>
          </ClickAwayListener>
        )}
      </Popper>
    </Box>
  );
});

// MultiSelectDropdown remains unchanged
const MultiSelectDropdown: React.FC<{
  label: string;
  options: string[];
  selectedValues: string[];
  onChange: (selected: string[]) => void;
  disabled?: boolean;
  width?: string | number;
}> = ({ label, options, selectedValues, onChange, disabled = false, width = '100%' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);

  const handleOpen = () => setIsOpen(true);
  const handleClose = () => setIsOpen(false);

  const handleToggle = (value: string) => {
    const newSelected = selectedValues.includes(value)
      ? selectedValues.filter((v) => v !== value)
      : [...selectedValues, value];
    onChange(newSelected);
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.checked ? options : []);
  };

  const isAllSelected = options.length > 0 && selectedValues.length === options.length;
  const buttonText =
    selectedValues.length === 0 ? `Select ${label.toLowerCase()}`
    : selectedValues.length === options.length && options.length > 0 ? 'All selected'
    : `${selectedValues.length} selected`;

  return (
    <Box sx={{ width }}>
      <Typography sx={{ fontFamily: 'Inter', fontWeight: 400, fontSize: '13px', color: disabled ? '#A0A0A0' : '#333333', mb: 0.5 }}>
        {label}<span style={{ color: 'red' }}>*</span>
      </Typography>
      <Button
        ref={anchorRef}
        variant="outlined"
        disabled={disabled}
        onClick={handleOpen}
        endIcon={isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        sx={{
          width: '100%', height: '36px', backgroundColor: disabled ? '#F3F3F3' : '#FFFFFF',
          border: `1px solid ${disabled ? '#D3D3D3' : '#E0E0E0'}`, borderRadius: '8px',
          fontFamily: 'Inter',
          fontWeight: selectedValues.length > 0 ? 500 : 400,
          fontSize: '13px',
          color: selectedValues.length > 0 ? '#333333' : '#999999',
          lineHeight: '18px',
          letterSpacing: '0%',
          textTransform: 'none',
          justifyContent: 'space-between',
          '&:hover': { borderColor: disabled ? '#D3D3D3' : '#0064FF' },
        }}
      >
        {buttonText}
      </Button>
      <Popper
        open={isOpen}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        transition
        sx={{ zIndex: 1300, width: anchorRef.current?.offsetWidth }}
      >
        {({ TransitionProps }) => (
          <ClickAwayListener onClickAway={handleClose}>
            <Grow {...TransitionProps} style={{ transformOrigin: 'top left' }}>
              <Paper sx={{ borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
                <FormControlLabel
                  label={<Typography sx={{ fontSize: '13px', color: isAllSelected ? '#333333' : '#666666' }}>All</Typography>}
                  sx={{ width: '100%', m: 0, px: 2, py: 0.5 }}
                  control={
                    <Checkbox size="small" checked={isAllSelected}
                      indeterminate={selectedValues.length > 0 && !isAllSelected}
                      onChange={handleSelectAll}
                      sx={{ '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: '#0064FF' } }}
                    />
                  }
                />
                <Box sx={{ maxHeight: '200px', overflowY: 'auto' }}>
                  {options.map((option) => (
                    <FormControlLabel
                      key={option} sx={{ width: '100%', m: 0, px: 2, py: 0.5 }}
                      control={
                        <Checkbox size="small" checked={selectedValues.includes(option)}
                          onChange={() => handleToggle(option)}
                          sx={{ '&.Mui-checked': { color: '#0064FF' } }}
                        />
                      }
                      label={<Typography sx={{ fontSize: '13px', color: selectedValues.includes(option) ? '#333333' : '#666666' }}>{option}</Typography>}
                    />
                  ))}
                </Box>
                <Divider />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
                  <Button onClick={() => onChange([])} sx={{ textTransform: 'none', color: '#999999' }}>Clear</Button>
                  <Button onClick={handleClose} sx={{ textTransform: 'none', color: '#0064FF' }}>Apply</Button>
                </Box>
              </Paper>
            </Grow>
          </ClickAwayListener>
        )}
      </Popper>
    </Box>
  );
};


// --- Main ScopeConfig Component ---
const ScopeConfig: React.FC<ScopeConfigProps> = ({
  provider, setProvider, services, setServices, getInitialService,
  errors, disabled = false, period,
}) => {
    const { currentTenant } = useOutletContext<AppOutletContext>();
    const [openPopper, setOpenPopper] = useState<{ type: 'dataSources' | 'projectIds' | 'regions' | 'label' | 'langfuseDataSources' | 'skus' | 'traces' | 'gcpServices', serviceId: string } | null>(null);
    const [tempSelections, setTempSelections] = useState<string[]>([]);
    const [regionSearchTerm, setRegionSearchTerm] = useState('');
    const [serviceSearchTerm, setServiceSearchTerm] = useState('');

    const buttonRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({});
    const [popperAnchor, setPopperAnchor] = useState<HTMLButtonElement | null>(null);
    const [labelMenuAnchor, setLabelMenuAnchor] = useState<HTMLElement | null>(null);
    const popperPaperRef = useRef(null);
    //const hasOpenedAutomatically = useRef(false);

    const [dataSourceOptions, setDataSourceOptions] = useState<DataSourceOption[]>([]);
    const [projectOptions, setProjectOptions] = useState<string[]>([]);
    const [activeRegions, setActiveRegions] = useState<string[]>([]);
    const [availableRegions, setAvailableRegions] = useState<string[]>([]);
    const [activeServices, setActiveServices] = useState<string[]>([]);
    const [availableServices, setAvailableServices] = useState<string[]>([]);
    const [labelKeys, setLabelKeys] = useState<string[]>([]);
    const [k8sClusters, setK8sClusters] = useState<string[]>([]);
    const [k8sNamespaces, setK8sNamespaces] = useState<string[]>([]);
    const [skuOptions, setSkuOptions] = useState<string[]>([]); // For LANGFUSE Models
    const [labelValuesData, setLabelValuesData] = useState<{ [key: string]: string[] }>({});
    const [loadingLabelKey, setLoadingLabelKey] = useState<string | null>(null);

    const dataSourceDependency = useMemo(() => {
        const sortedIds = Array.from(new Set(services.flatMap(s => s.dataSources))).sort();
        return JSON.stringify(sortedIds);
    }, [services]);

    const [getOptions, { loading: optionsLoading }] = useLazyQuery(GET_BUDGET_FILTER_OPTIONS, {
        onCompleted: (data) => {
            if (data?.getBudgetFilterOptions) {
                const {
                    dataSources, projectIds, activeRegions, availableRegions,
                    activeServices, availableServices, labelKeys, k8sClusters,
                    k8sNamespaces, skus
                } = data.getBudgetFilterOptions;

                setDataSourceOptions(dataSources || []);
                setProjectOptions(projectIds || []);
                setActiveRegions(activeRegions || []);
                setAvailableRegions(availableRegions || []);
                setActiveServices(activeServices || []);
                setAvailableServices(availableServices || []);
                setLabelKeys(labelKeys || []);
                setK8sClusters(k8sClusters || []);
                setK8sNamespaces(k8sNamespaces || []);
                setSkuOptions(skus || []);
            }
        }
    });

    const [getLabelValues] = useLazyQuery(GET_LABEL_VALUES, {
        onCompleted: (data) => {
            if (loadingLabelKey && data?.getBudgetLabelValues?.values) {
                setLabelValuesData(prev => ({ ...prev, [loadingLabelKey]: data.getBudgetLabelValues.values }));
            }
            setLoadingLabelKey(null);
        }
    });

    useEffect(() => {
        if (!provider || !currentTenant?.id || !period) return;
        const allSelectedDataSources = JSON.parse(dataSourceDependency);
        getOptions({
            variables: {
                input: {
                    tenantId: currentTenant.id,
                    provider,
                    period: period.toUpperCase(),
                    dataSourceIds: allSelectedDataSources,
                }
            }
        });
    }, [provider, currentTenant, period, dataSourceDependency, getOptions]);

    // useEffect(() => {
    //     if (provider === 'GCP' && services.length === 1 && services[0].dataSources.length === 0 &&
    //         !disabled && !optionsLoading && !hasOpenedAutomatically.current && dataSourceOptions.length > 0) {
    //         const firstServiceId = services[0].id;
    //         const dataSourceButton = buttonRefs.current[`${firstServiceId}-dataSources`];
    //         if (dataSourceButton) {
    //             setPopperAnchor(dataSourceButton);
    //             setTempSelections(services[0].dataSources);
    //             setOpenPopper({ type: 'dataSources', serviceId: firstServiceId });
    //             hasOpenedAutomatically.current = true;
    //         }
    //     }
    //     if (provider !== 'GCP' || services.length === 0) {
    //         hasOpenedAutomatically.current = false;
    //     }
    // }, [provider, services, disabled, optionsLoading, dataSourceOptions]);

    const providerBoxStyle = (isSelected: boolean, isDisabled: boolean) => ({
      p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      backgroundColor: isSelected ? '#F2F7FF' : (isDisabled ? '#F3F3F3' : '#FFFFFF'),
      border: `1px solid ${isSelected ? '#0064FF' : '#E0E0E0'}`,
      borderRadius: '8px', cursor: isDisabled ? 'not-allowed' : 'pointer', flex: 1, height: '36px',
      opacity: isDisabled ? 0.6 : 1,
      '&:hover': isDisabled ? {} : { borderColor: '#0064FF' }
    });
    
    const handleProviderChange = (newProvider: string) => {
        if (disabled || provider === newProvider) return;
        setProvider(newProvider);
        // Reset services when provider changes
        setServices([{ id: `service_${Date.now()}`, ...getInitialService() }]);
    };

    const handlePopperOpen = (event: React.MouseEvent<HTMLButtonElement>, type: 'dataSources' | 'projectIds' | 'regions' | 'langfuseDataSources' | 'skus' | 'traces' | 'gcpServices', serviceId: string, currentValues: string[]) => {
      setPopperAnchor(event.currentTarget);
      setTempSelections(currentValues);
      setOpenPopper({ type, serviceId });
    };

    const handlePopperClose = () => {
      setOpenPopper(null);
      setRegionSearchTerm('');
      setServiceSearchTerm('');
      setPopperAnchor(null);
    };

    const handleUpdateService = useCallback((serviceId: string, field: keyof Omit<ServiceState, 'id'>, value: any) => {
        setServices(prev => prev.map(s => {
            if (s.id === serviceId) {
                const updatedService = { ...s, [field]: value };
                // GCP specific reset logic
                if (provider === 'GCP') {
                  if (field === 'dataSources') {
                      updatedService.projectIds = []; updatedService.regions = []; updatedService.serviceTypes = [];
                      updatedService.label = []; updatedService.cluster = []; updatedService.namespace = [];
                  }
                  if (field === 'projectIds') {
                      updatedService.regions = []; updatedService.serviceTypes = []; updatedService.label = [];
                      updatedService.cluster = []; updatedService.namespace = [];
                  }
                  if (field === 'regions') {
                      updatedService.serviceTypes = []; updatedService.label = [];
                      updatedService.cluster = []; updatedService.namespace = [];
                  }
                  if (field === 'serviceTypes' && !value.includes('Kubernetes Engine')) {
                     updatedService.cluster = []; updatedService.namespace = [];
                  }
                }
                // LANGFUSE specific reset logic
                if (provider === 'LANGFUSE') {
                    if (field === 'dataSources') {
                        updatedService.skus = [];
                        updatedService.serviceTypes = []; // Traces
                    }
                }
                return updatedService;
            }
            return s;
        }));
    }, [setServices, provider]);

    // const handleApply = () => {
    //     if (!openPopper) return;
    //     const { serviceId, type } = openPopper;
    //     handleUpdateService(serviceId, type as any, tempSelections);
    //     handlePopperClose();
    // };
    const handleApply = () => {
        if (!openPopper) return;
        const { serviceId, type } = openPopper;

        // Declare the variable with the correct target type: a key of ServiceState
        let fieldToUpdate: keyof Omit<ServiceState, 'id'>;

        // Map the popper's temporary type to the correct ServiceState field name
        if (type === 'gcpServices' || type === 'traces') {
            fieldToUpdate = 'serviceTypes';
        } else if (type === 'langfuseDataSources') {
            fieldToUpdate = 'dataSources';
        } else {
            // For types where the name already matches a key in ServiceState (e.g., 'projectIds', 'regions').
            // We use a type assertion to tell TypeScript this is safe.
            fieldToUpdate = type as keyof Omit<ServiceState, 'id'>;
        }

        // Now 'fieldToUpdate' is correctly typed, and no 'as any' is needed here.
        handleUpdateService(serviceId, fieldToUpdate, tempSelections);
        handlePopperClose();
    };

    const handleFetchLabelValues = useCallback((key: string) => {
        if (!key || labelValuesData[key] || loadingLabelKey) return;
        setLoadingLabelKey(key);
        const allDataSourceIds = Array.from(new Set(services.flatMap(s => s.dataSources))).sort();
        getLabelValues({
            variables: {
                input: {
                    tenantId: currentTenant?.id,
                    provider,
                    labelKey: key,
                    period: period.toUpperCase(),
                    dataSourceIds: allDataSourceIds,
                }
            }
        });
    }, [getLabelValues, provider, currentTenant, services, labelValuesData, loadingLabelKey, period]);

    const handleDeleteLabelChip = (serviceId: string, keyToDelete: string, valueToDelete: string) => {
        setServices(prevServices =>
          prevServices.map(service => {
            if (service.id === serviceId) {
              const updatedLabels = service.label
                .map(label => {
                  if (label.key === keyToDelete) {
                    const newValues = label.values.filter(v => v !== valueToDelete);
                    if (newValues.length > 0) {
                      return { ...label, values: newValues };
                    }
                    return null;
                  }
                  return label;
                })
                .filter((label): label is LabelFilterPair => label !== null);

              return { ...service, label: updatedLabels };
            }
            return service;
          })
        );
      };

      const handleAddService = useCallback(() => {
        const newService = { id: `service_${Date.now()}`, ...getInitialService() };
        setServices(prev => [...prev, newService]);
      }, [getInitialService, setServices]);

      const handleDeleteService = useCallback((idToDelete: string) => {
        setServices(p => p.filter(s => s.id !== idToDelete));
      }, [setServices]);

      const handleLabelMenuOpen = (event: React.MouseEvent<HTMLElement>, serviceId: string) => {
          setLabelMenuAnchor(event.currentTarget);
          setOpenPopper({ type: 'label', serviceId });
      };

      const handleLabelMenuClose = () => {
        setOpenPopper(null);
        setLabelMenuAnchor(null);
      };

      const handleLabelApply = (newFilters: LabelFilterPair[]) => {
          if (openPopper?.type === 'label') {
              handleUpdateService(openPopper.serviceId, 'label', newFilters);
          }
          handleLabelMenuClose();
      };

      const activeServiceForLabel = services.find(s => s.id === openPopper?.serviceId);

    const renderMultiSelectPopper = () => {
      if (!openPopper || !popperAnchor) return null;
      const checkboxStyle = { '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: '#0064FF' } };

      if (openPopper.type === 'dataSources' || openPopper.type === 'langfuseDataSources') {
        const options = dataSourceOptions;
        const isAllSelected = tempSelections.length === options.length && options.length > 0;
        const isSomeSelected = tempSelections.length > 0 && !isAllSelected;
        const handleSelectAll = () => setTempSelections(isAllSelected ? [] : options.map(o => o.id));
        return (
          <Popper open={true} anchorEl={popperAnchor} placement="bottom-start" transition sx={{zIndex: 1200}} modifiers={[{name: 'offset', options: {offset: [0, 8]}}]}>
            {({ TransitionProps }) => (
                <Grow {...TransitionProps} style={{ transformOrigin: 'top left' }}>
                    <Paper ref={popperPaperRef} sx={{ width: popperAnchor?.offsetWidth, borderRadius: '12px', backgroundColor: '#FFFFFF', boxShadow: '0 4px 20px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
                        <ClickAwayListener onClickAway={handlePopperClose}>
                            <Box>
                                <FormControlLabel
                                    label={<Typography sx={{ fontSize: '13px', color: isAllSelected ? '#333333' : '#666666' }}>All</Typography>}
                                    sx={{width: '100%', m:0, px: 2, py: 0.5}}
                                    control={ <Checkbox size="small" checked={isAllSelected} indeterminate={isSomeSelected} onChange={handleSelectAll} sx={checkboxStyle}/> }
                                />
                                <Box sx={{ maxHeight: '140px', overflow: 'auto' }}>
                                  {options.map(option => (
                                    <Tooltip
                                      key={option.id}
                                      title={option.isSyncing ? "This data source is currently syncing and cannot be selected." : ""}
                                      placement="right"
                                      arrow
                                    >
                                      <div>
                                        <FormControlLabel
                                            sx={{width: '100%', m:0, px:2, py:0.5}}
                                            disabled={option.isSyncing}
                                            control={
                                              <Checkbox
                                                size="small"
                                                checked={tempSelections.includes(option.id)}
                                                onChange={() => setTempSelections(p => p.includes(option.id) ? p.filter(i => i !== option.id) : [...p, option.id])}
                                                sx={checkboxStyle}
                                              />
                                            }
                                            label={
                                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography sx={{ fontSize: '13px', color: tempSelections.includes(option.id) ? '#333333' : '#666666' }}>
                                                  {option.name}
                                                </Typography>
                                                {option.isSyncing && (
                                                  <Box component="span" sx={{display: 'inline-flex', alignItems: 'center', height: '18px', px: 0.8, borderRadius: '10px', backgroundColor: 'rgba(255, 193, 7, 0.2)', color: 'rgba(180, 120, 0)', fontSize: '11px', fontWeight: 500 }}>
                                                    Syncing
                                                  </Box>
                                                )}
                                              </Box>
                                            }
                                        />
                                      </div>
                                    </Tooltip>
                                  ))}
                                </Box>
                                <Divider />
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', p: 1 }}>
                                    <Button onClick={() => setTempSelections([])} sx={{ textTransform: 'none', color: '#999999' }}>Clear</Button>
                                    <Button onClick={handleApply} sx={{ textTransform: 'none', color: '#0064FF' }}>Apply</Button>
                                </Box>
                            </Box>
                        </ClickAwayListener>
                    </Paper>
                </Grow>
            )}
        </Popper>
        );
      }

    if (openPopper.type === 'projectIds' || openPopper.type === 'skus' || openPopper.type === 'traces') {
          let options: string[] = [];
          if (openPopper.type === 'projectIds') options = projectOptions;
          if (openPopper.type === 'skus') options = skuOptions;
          if (openPopper.type === 'traces') options = activeServices;
        
          const isAllSelected = tempSelections.length === options.length && options.length > 0;
          const isSomeSelected = tempSelections.length > 0 && !isAllSelected;
          const handleSelectAll = () => setTempSelections(isAllSelected ? [] : options);
          return (
              <Popper open={true} anchorEl={popperAnchor} placement="bottom-start" transition sx={{zIndex: 1200}} modifiers={[{name: 'offset', options: {offset: [0, 8]}}]}>
                  {({ TransitionProps }) => (
                      <Grow {...TransitionProps} style={{ transformOrigin: 'top left' }}>
                          <Paper ref={popperPaperRef} sx={{ width: popperAnchor?.offsetWidth, borderRadius: '12px', backgroundColor: '#FFFFFF', boxShadow: '0 4px 20px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
                              <ClickAwayListener onClickAway={handlePopperClose}>
                                  <Box>
                                      <FormControlLabel
                                          label={<Typography sx={{ fontSize: '13px', color: isAllSelected ? '#333333' : '#666666' }}>All</Typography>}
                                          sx={{width: '100%', m:0, px: 2, py: 0.5}}
                                          control={ <Checkbox size="small" checked={isAllSelected} indeterminate={isSomeSelected} onChange={handleSelectAll} sx={checkboxStyle}/> }
                                      />
                                      <Box sx={{ maxHeight: '140px', overflow: 'auto' }}>
                                          {options.map(option => (
                                              <FormControlLabel
                                                  key={option}
                                                  sx={{width: '100%', m:0, px:2, py:0.5}}
                                                  control={ <Checkbox size="small" checked={tempSelections.includes(option)} onChange={() => setTempSelections(p => p.includes(option) ? p.filter(i => i !== option) : [...p, option])} sx={checkboxStyle}/> }
                                                  label={<Typography sx={{ fontSize: '13px', color: tempSelections.includes(option) ? '#333333' : '#666666' }}>{option}</Typography>}
                                              />
                                          ))}
                                      </Box>
                                      <Divider />
                                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', p: 1 }}>
                                          <Button onClick={() => setTempSelections([])} sx={{ textTransform: 'none', color: '#999999' }}>Clear</Button>
                                          <Button onClick={handleApply} sx={{ textTransform: 'none', color: '#0064FF' }}>Apply</Button>
                                      </Box>
                                  </Box>
                              </ClickAwayListener>
                          </Paper>
                      </Grow>
                  )}
              </Popper>
          );
      }

    if (openPopper.type === 'regions') {
        const filteredActive = activeRegions.filter(r => r.toLowerCase().includes(regionSearchTerm.toLowerCase()));
        const filteredAvailable = availableRegions.filter(r => r.toLowerCase().includes(regionSearchTerm.toLowerCase()));
        const handleRegionToggle = (value: string) => {
            const newSelection = tempSelections.includes(value)
                ? tempSelections.filter(v => v !== value)
                : [...tempSelections, value];
            setTempSelections(newSelection);
        };
        const handleSelectAll = (itemsToToggle: string[], shouldSelect: boolean) => {
            const otherSelections = tempSelections.filter(s => !itemsToToggle.includes(s));
            const newSelections = shouldSelect
                ? Array.from(new Set([...otherSelections, ...itemsToToggle]))
                : otherSelections;
            setTempSelections(newSelections);
        };
        const isAllActiveSelected = filteredActive.length > 0 && filteredActive.every(r => tempSelections.includes(r));
        const isSomeActiveSelected = filteredActive.some(r => tempSelections.includes(r)) && !isAllActiveSelected;
        const isAllAvailableSelected = filteredAvailable.length > 0 && filteredAvailable.every(r => tempSelections.includes(r));
        const isSomeAvailableSelected = filteredAvailable.some(r => tempSelections.includes(r)) && !isAllAvailableSelected;
        const renderRegionOption = (option: string) => (
            <FormControlLabel
                key={option}
                sx={{ width: '100%', m: 0, px: 2, py: 0.5 }}
                control={<Checkbox size="small" checked={tempSelections.includes(option)} onChange={() => handleRegionToggle(option)} sx={checkboxStyle}/>}
                label={<Typography sx={{ fontSize: '13px', color: tempSelections.includes(option) ? '#333333' : '#666666' }}>{option}</Typography>}
            />
        );
        return (
            <Popper open={true} anchorEl={popperAnchor} placement="bottom-start" transition sx={{zIndex: 1200}} modifiers={[{name: 'offset', options: {offset: [0, 8]}}]}>
                {({ TransitionProps }) => (
                    <Grow {...TransitionProps} style={{ transformOrigin: 'top left' }}>
                        <Paper ref={popperPaperRef} sx={{ width: popperAnchor?.offsetWidth, borderRadius: '12px', backgroundColor: '#FFFFFF', boxShadow: '0 4px 20px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
                            <ClickAwayListener onClickAway={handlePopperClose}>
                                <Box>
                                    <Box sx={{ p: 2, borderBottom: '1px solid #f0f0f0' }}>
                                        <TextField
                                            autoFocus placeholder="Search by region" value={regionSearchTerm}
                                            onChange={(e) => setRegionSearchTerm(e.target.value)}
                                            onKeyDown={(e) => e.stopPropagation()} fullWidth variant="outlined"
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start" sx={{ ml: -0.5 }}>
                                                    <img src="/icons/search_icon.svg" alt="Search" style={{ width: '16px', height: '16px' }} />
                                                    </InputAdornment>
                                                ),
                                                sx: { height: '32px', borderRadius: '10px', backgroundColor: 'rgba(0, 0, 0, 0.04)', '& .MuiOutlinedInput-notchedOutline': { border: 'none' }, '& .MuiInputBase-input::placeholder': { fontSize: '14px', fontWeight: 300 } },
                                            }}
                                        />
                                    </Box>
                                    <Box sx={{ maxHeight: '200px', overflowY: 'auto' }}>
                                        {filteredActive.length > 0 && (
                                            <>
                                                <ListSubheader sx={{ bgcolor: '#F9FAFB', lineHeight: '32px', fontSize: '13px', fontWeight: 600 }}>Active Regions</ListSubheader>
                                                <FormControlLabel
                                                    sx={{ width: '100%', m: 0, px: 2, py: 0.5 }}
                                                    label={<Typography sx={{ fontSize: '13px', fontWeight: 500 }}>All</Typography>}
                                                    control={<Checkbox size="small" checked={isAllActiveSelected} indeterminate={isSomeActiveSelected} onChange={(e) => handleSelectAll(filteredActive, e.target.checked)} sx={checkboxStyle}/>}
                                                />
                                                {filteredActive.map(renderRegionOption)}
                                            </>
                                        )}
                                        {filteredAvailable.length > 0 && (
                                            <>
                                                <ListSubheader sx={{ bgcolor: '#F9FAFB', lineHeight: '32px', fontSize: '13px', fontWeight: 600 }}>Available Regions</ListSubheader>
                                                <FormControlLabel
                                                    sx={{ width: '100%', m: 0, px: 2, py: 0.5 }}
                                                    label={<Typography sx={{ fontSize: '13px', fontWeight: 500 }}>All</Typography>}
                                                    control={<Checkbox size="small" checked={isAllAvailableSelected} indeterminate={isSomeAvailableSelected} onChange={(e) => handleSelectAll(filteredAvailable, e.target.checked)} sx={checkboxStyle}/>}
                                                />
                                                {filteredAvailable.map(renderRegionOption)}
                                            </>
                                        )}
                                    </Box>
                                    <Divider />
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', p: 1 }}>
                                        <Button onClick={() => setTempSelections([])} sx={{ textTransform: 'none', color: '#999999' }}>Clear</Button>
                                        <Button onClick={handleApply} sx={{ textTransform: 'none', color: '#0064FF' }}>Apply</Button>
                                    </Box>
                                </Box>
                            </ClickAwayListener>
                        </Paper>
                    </Grow>
                )}
            </Popper>
        );
    }

    // if (openPopper.type === 'gcpServices') {
    //     const filteredActive = activeServices.filter(opt => opt.toLowerCase().includes(serviceSearchTerm.toLowerCase()));
    //     const filteredAvailable = availableServices.filter(opt => opt.toLowerCase().includes(serviceSearchTerm.toLowerCase()));
        
    //     const isKubeSelected = tempSelections.includes('Kubernetes Engine');
    
    //     const isAnyActiveSelected = activeServices.some(opt => tempSelections.includes(opt) && opt !== 'Kubernetes Engine');
    //     const isAnyAvailableSelected = availableServices.some(opt => tempSelections.includes(opt) && opt !== 'Kubernetes Engine');
    
    //     const nonKubeFilteredActive = filteredActive.filter(s => s !== 'Kubernetes Engine');
    //     const nonKubeFilteredAvailable = filteredAvailable.filter(s => s !== 'Kubernetes Engine');
    
    //     const isAllActiveSelected = nonKubeFilteredActive.length > 0 && nonKubeFilteredActive.every(s => tempSelections.includes(s));
    //     const isSomeActiveSelected = nonKubeFilteredActive.some(s => tempSelections.includes(s)) && !isAllActiveSelected;
    
    //     const isAllAvailableSelected = nonKubeFilteredAvailable.length > 0 && nonKubeFilteredAvailable.every(s => tempSelections.includes(s));
    //     const isSomeAvailableSelected = nonKubeFilteredAvailable.some(s => tempSelections.includes(s)) && !isAllAvailableSelected;
    
    //     const handleToggle = (value: string) => {
    //       if (value === 'Kubernetes Engine') {
    //         setTempSelections(isKubeSelected ? [] : ['Kubernetes Engine']);
    //         return;
    //       }
    //       if (isKubeSelected) return;
      
    //       const isTogglingActive = activeServices.includes(value);
      
    //       if (isTogglingActive && isAnyAvailableSelected) {
    //         setTempSelections([value]);
    //         return;
    //       }
    //       if (!isTogglingActive && isAnyActiveSelected) {
    //         setTempSelections([value]);
    //         return;
    //       }
      
    //       const newSelected = tempSelections.includes(value)
    //         ? tempSelections.filter(v => v !== value)
    //         : [...tempSelections, value];
    //       setTempSelections(newSelected);
    //     };
      
    //     const handleSelectAll = (itemsToToggle: string[], isSelecting: boolean) => {
    //       if (isSelecting) {
    //         const nonKubeItems = itemsToToggle.filter(v => v !== 'Kubernetes Engine');
    //         setTempSelections(nonKubeItems);
    //       } else {
    //         setTempSelections([]);
    //       }
    //     };
        
    //     const renderOption = (option: string) => {
    //         const isThisOptionActive = activeServices.includes(option);
    //         const isThisOptionAvailable = availableServices.includes(option);
            
    //         const isDisabled = 
    //             (isKubeSelected && option !== 'Kubernetes Engine') || 
    //             ((isAnyActiveSelected || isAnyAvailableSelected) && option === 'Kubernetes Engine') ||
    //             (isThisOptionAvailable && isAnyActiveSelected) ||
    //             (isThisOptionActive && isAnyAvailableSelected);
    
    //         return (
    //           <FormControlLabel
    //               key={option}
    //               sx={{ width: '100%', m: 0, px: 2, py: 0.5 }}
    //               control={
    //                   <Checkbox
    //                       size="small"
    //                       checked={tempSelections.includes(option)}
    //                       onChange={() => handleToggle(option)}
    //                       disabled={isDisabled}
    //                       sx={checkboxStyle}
    //                   />
    //               }
    //               label={
    //                 <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
    //                   <Typography sx={{ fontSize: '13px', color: isDisabled ? '#B0B0B0' : tempSelections.includes(option) ? '#333333' : '#666666' }}>
    //                     {option}
    //                   </Typography>
    //                   {option === 'Kubernetes Engine' && isDisabled && (
    //                     <Tooltip
    //                       title="As you have selected other services, Kubernetes Engine is disabled. To select it, please clear other services."
    //                       arrow
    //                       componentsProps={{
    //                         tooltip: { sx: { backgroundColor: '#FFFFFF', color: '#000000', border: '1px solid #E0E0E0', fontSize: '12px', }, },
    //                         arrow: { sx: { color: '#FFFFFF', }, },
    //                       }}
    //                     >
    //                       <InfoOutlinedIcon sx={{ color: '#0064FF', ml: 1, fontSize: '16px', }} />
    //                     </Tooltip>
    //                   )}
    //                 </Box>
    //               }
    //           />
    //         );
    //     };
      
    //     return (
    //         <Popper open={true} anchorEl={popperAnchor} placement="bottom-start" transition sx={{zIndex: 1300, width: popperAnchor?.offsetWidth}}>
    //           {({ TransitionProps }) => (
    //             <ClickAwayListener onClickAway={handlePopperClose}>
    //               <Grow {...TransitionProps} style={{ transformOrigin: 'top left' }}>
    //                 <Paper sx={{ borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.1)', maxHeight: '300px', display: 'flex', flexDirection: 'column' }}>
    //                   <Box sx={{ p: 2, borderBottom: '1px solid #f0f0f0' }}>
    //                       <TextField
    //                           autoFocus placeholder="Search by service name.." value={serviceSearchTerm}
    //                           onChange={(e) => setServiceSearchTerm(e.target.value)}
    //                           onKeyDown={(e) => e.stopPropagation()} fullWidth variant="outlined"
    //                           InputProps={{
    //                               startAdornment: ( <InputAdornment position="start" sx={{ ml: -0.5 }}><img src="/icons/search_icon.svg" alt="Search" style={{ width: '16px', height: '16px' }} /></InputAdornment> ),
    //                               sx: { height: '32px', borderRadius: '10px', backgroundColor: 'rgba(0, 0, 0, 0.04)', '& .MuiOutlinedInput-notchedOutline': { border: 'none' }, '& .MuiInputBase-input::placeholder': { fontSize: '14px', fontWeight: 300 } },
    //                           }}
    //                       />
    //                   </Box>
    //                   <Box sx={{ overflowY: 'auto' }}>
    //                       {filteredActive.length === 0 && filteredAvailable.length === 0 ? (
    //                           <Typography sx={{ p: 2, color: '#999', fontSize: '13px', textAlign: 'center' }}>No results found</Typography>
    //                       ) : ( <>
    //                               {filteredActive.length > 0 && (<>
    //                                   <ListSubheader sx={{ bgcolor: '#F9FAFB', lineHeight: '32px', fontSize: '13px', fontWeight: 600 }}>Active Services</ListSubheader>
    //                                   <FormControlLabel sx={{ width: '100%', m: 0, px: 2, py: 0.5 }} label={<Typography sx={{ fontSize: '13px' }}>All</Typography>}
    //                                       control={<Checkbox size="small" checked={isAllActiveSelected} indeterminate={isSomeActiveSelected} onChange={(e) => handleSelectAll(filteredActive, e.target.checked)} disabled={isKubeSelected || isAnyAvailableSelected} sx={checkboxStyle} />}
    //                                   />
    //                                   {filteredActive.map(renderOption)}
    //                               </>)}
    //                               {filteredAvailable.length > 0 && (<>
    //                                   <ListSubheader sx={{ bgcolor: '#F9FAFB', lineHeight: '32px', fontSize: '13px', fontWeight: 600 }}>Available Services</ListSubheader>
    //                                   <FormControlLabel sx={{ width: '100%', m: 0, px: 2, py: 0.5 }} label={<Typography sx={{ fontSize: '13px' }}>All</Typography>}
    //                                       control={<Checkbox size="small" checked={isAllAvailableSelected} indeterminate={isSomeAvailableSelected} onChange={(e) => handleSelectAll(filteredAvailable, e.target.checked)} disabled={isKubeSelected || isAnyActiveSelected} sx={checkboxStyle} />}
    //                                   />
    //                                   {filteredAvailable.map(renderOption)}
    //                               </>)}
    //                           </>
    //                       )}
    //                   </Box>
    //                   <Divider />
    //                   <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1, flexShrink: 0 }}>
    //                     <Button onClick={() => setTempSelections([])} sx={{ textTransform: 'none', color: '#999999' }}>Clear</Button>
    //                     <Button onClick={handleApply} sx={{ textTransform: 'none', color: '#0064FF' }}>Apply</Button>
    //                   </Box>
    //                 </Paper>
    //               </Grow>
    //             </ClickAwayListener>
    //           )}
    //         </Popper>
    //     )
    //   }
    if (openPopper.type === 'gcpServices') {
    const filteredActive = activeServices.filter(opt => opt.toLowerCase().includes(serviceSearchTerm.toLowerCase()));
    const filteredAvailable = availableServices.filter(opt => opt.toLowerCase().includes(serviceSearchTerm.toLowerCase()));
    
    // --- START: Logic copied and adapted from SectionedMultiSelectDropdown ---
    const isKubeSelected = tempSelections.includes('Kubernetes Engine');

    const isAnyActiveSelected = activeServices.some(opt => tempSelections.includes(opt) && opt !== 'Kubernetes Engine');
    const isAnyAvailableSelected = availableServices.some(opt => tempSelections.includes(opt) && opt !== 'Kubernetes Engine');

    const nonKubeFilteredActive = filteredActive.filter(s => s !== 'Kubernetes Engine');
    const nonKubeFilteredAvailable = filteredAvailable.filter(s => s !== 'Kubernetes Engine');

    const isAllActiveSelected = nonKubeFilteredActive.length > 0 && nonKubeFilteredActive.every(s => tempSelections.includes(s));
    const isSomeActiveSelected = nonKubeFilteredActive.some(s => tempSelections.includes(s)) && !isAllActiveSelected;

    const isAllAvailableSelected = nonKubeFilteredAvailable.length > 0 && nonKubeFilteredAvailable.every(s => tempSelections.includes(s));
    const isSomeAvailableSelected = nonKubeFilteredAvailable.some(s => tempSelections.includes(s)) && !isAllAvailableSelected;

    const handleToggle = (value: string) => {
      // Rule 1: Kubernetes Engine is exclusive
      if (value === 'Kubernetes Engine') {
        setTempSelections(isKubeSelected ? [] : ['Kubernetes Engine']);
        return;
      }
      if (isKubeSelected) return;

      const isTogglingActive = activeServices.includes(value);

      // Rule 2: Reset on crossover
      if (isTogglingActive && isAnyAvailableSelected) {
        setTempSelections([value]);
        return;
      }
      if (!isTogglingActive && isAnyActiveSelected) {
        setTempSelections([value]);
        return;
      }

      // Rule 3: Standard multi-select within the same group
      const newSelected = tempSelections.includes(value)
        ? tempSelections.filter(v => v !== value)
        : [...tempSelections, value];
      setTempSelections(newSelected);
    };

    const handleSelectAll = (itemsToToggle: string[], isSelecting: boolean) => {
      if (isSelecting) {
        const nonKubeItems = itemsToToggle.filter(v => v !== 'Kubernetes Engine');
        setTempSelections(nonKubeItems);
      } else {
        setTempSelections([]);
      }
    };
    
    const renderOption = (option: string) => {
        const isThisOptionActive = activeServices.includes(option);
        const isThisOptionAvailable = availableServices.includes(option);
        
        const isDisabled = 
            (isKubeSelected && option !== 'Kubernetes Engine') || 
            ((isAnyActiveSelected || isAnyAvailableSelected) && option === 'Kubernetes Engine') ||
            (isThisOptionAvailable && isAnyActiveSelected) ||
            (isThisOptionActive && isAnyAvailableSelected);

        return (
          <FormControlLabel
              key={option}
              sx={{ width: '100%', m: 0, px: 2, py: 0.5 }}
              control={
                  <Checkbox
                      size="small"
                      checked={tempSelections.includes(option)}
                      onChange={() => handleToggle(option)}
                      disabled={isDisabled}
                      sx={checkboxStyle}
                  />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  <Typography sx={{ fontSize: '13px', color: isDisabled ? '#B0B0B0' : tempSelections.includes(option) ? '#333333' : '#666666' }}>
                    {option}
                  </Typography>
                  {option === 'Kubernetes Engine' && isDisabled && (
                    <Tooltip
                      title="As you have selected other services, Kubernetes Engine is disabled. To select it, please clear other services."
                      arrow
                      componentsProps={{
                        tooltip: { sx: { backgroundColor: '#FFFFFF', color: '#000000', border: '1px solid #E0E0E0', fontSize: '12px', }, },
                        arrow: { sx: { color: '#FFFFFF', }, },
                      }}
                    >
                      <InfoOutlinedIcon sx={{ color: '#0064FF', ml: 1, fontSize: '16px', }} />
                    </Tooltip>
                  )}
                </Box>
              }
          />
        );
    };
    // --- END: Adapted Logic ---
    
    return (
        <Popper open={true} anchorEl={popperAnchor} placement="bottom-start" transition sx={{zIndex: 1300, width: popperAnchor?.offsetWidth}}>
          {({ TransitionProps }) => (
            <ClickAwayListener onClickAway={handlePopperClose}>
              <Grow {...TransitionProps} style={{ transformOrigin: 'top left' }}>
                <Paper sx={{ borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.1)', maxHeight: '300px', display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ p: 2, borderBottom: '1px solid #f0f0f0' }}>
                      <TextField
                          autoFocus placeholder="Search by service name.." value={serviceSearchTerm}
                          onChange={(e) => setServiceSearchTerm(e.target.value)}
                          onKeyDown={(e) => e.stopPropagation()} fullWidth variant="outlined"
                          InputProps={{
                              startAdornment: ( <InputAdornment position="start" sx={{ ml: -0.5 }}><img src="/icons/search_icon.svg" alt="Search" style={{ width: '16px', height: '16px' }} /></InputAdornment> ),
                              sx: { height: '32px', borderRadius: '10px', backgroundColor: 'rgba(0, 0, 0, 0.04)', '& .MuiOutlinedInput-notchedOutline': { border: 'none' }, '& .MuiInputBase-input::placeholder': { fontSize: '14px', fontWeight: 300 } },
                          }}
                      />
                  </Box>
                  <Box sx={{ overflowY: 'auto' }}>
                      {filteredActive.length === 0 && filteredAvailable.length === 0 ? (
                          <Typography sx={{ p: 2, color: '#999', fontSize: '13px', textAlign: 'center' }}>No results found</Typography>
                      ) : ( <>
                          {filteredActive.length > 0 && (<>
                              <ListSubheader sx={{ bgcolor: '#F9FAFB', lineHeight: '32px', fontSize: '13px', fontWeight: 600 }}>Active Services</ListSubheader>
                              <FormControlLabel sx={{ width: '100%', m: 0, px: 2, py: 0.5 }} label={<Typography sx={{ fontSize: '13px' }}>All</Typography>}
                                  control={<Checkbox size="small" checked={isAllActiveSelected} indeterminate={isSomeActiveSelected} onChange={(e) => handleSelectAll(filteredActive, e.target.checked)} disabled={isKubeSelected || isAnyAvailableSelected} sx={checkboxStyle} />}
                              />
                              {filteredActive.map(renderOption)}
                          </>)}
                          {filteredAvailable.length > 0 && (<>
                              <ListSubheader sx={{ bgcolor: '#F9FAFB', lineHeight: '32px', fontSize: '13px', fontWeight: 600 }}>Available Services</ListSubheader>
                              <FormControlLabel sx={{ width: '100%', m: 0, px: 2, py: 0.5 }} label={<Typography sx={{ fontSize: '13px' }}>All</Typography>}
                                  control={<Checkbox size="small" checked={isAllAvailableSelected} indeterminate={isSomeAvailableSelected} onChange={(e) => handleSelectAll(filteredAvailable, e.target.checked)} disabled={isKubeSelected || isAnyActiveSelected} sx={checkboxStyle} />}
                              />
                              {filteredAvailable.map(renderOption)}
                          </>)}
                      </>
                      )}
                  </Box>
                  <Divider />
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1, flexShrink: 0 }}>
                    <Button onClick={() => setTempSelections([])} sx={{ textTransform: 'none', color: '#999999' }}>Clear</Button>
                    <Button onClick={handleApply} sx={{ textTransform: 'none', color: '#0064FF' }}>Apply</Button>
                  </Box>
                </Paper>
              </Grow>
            </ClickAwayListener>
          )}
        </Popper>
    )
  }

      return null;
    };

    return (
       
            <Box sx={{ p: { xs: 1, sm: 2 }, bgcolor: '#FFFFFF', borderRadius: '10px', border: '1px solid #F3F3F3', position: 'relative' }}>
                {/* {disabled && (
                  <Box sx={{
                      position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                      backgroundColor: 'rgba(255, 255, 255, 0.7)', zIndex: 1, cursor: 'not-allowed'
                  }} />
              )} */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <img src="/icons/Scope-Config.svg" alt="Scope Configuration" style={{ marginRight: '12px', width: '28px', height: '28px' }} />
              <Typography variant="h6" sx={{ fontWeight: 500, fontSize: '14px', color: '#333333', fontFamily: 'Inter' }}>Scope Configuration</Typography>
            </Box>
            <Divider sx={{ borderColor: '#F3F3F3', mx: -3 }} />

            <Box sx={{ display: 'flex', gap: 2, mt: 2, px: 1, flexWrap: 'wrap' }}>
            <Tooltip title={disabled ? "The provider cannot be changed while editing a budget." : ""}>
              <Box sx={providerBoxStyle(provider === 'GCP', disabled)} onClick={() => handleProviderChange('GCP')}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}><img src="/icons/google-cloud-icon-2048x1646-7admxejz.svg" alt="GCP" style={{ marginRight: 8, width: 20, height: 20 }} /><Typography variant="body2" sx={{ fontFamily: 'Inter', fontSize: '13px', fontWeight: 400 }}>Google Cloud Platform</Typography></Box>
                <Radio checked={provider === 'GCP'} disabled={disabled} size="small" sx={{ p: 0, transform: 'scale(0.85)', '&.Mui-checked': { color: '#0064FF' } }}/>
              </Box>
              </Tooltip>
              <Tooltip title={disabled ? "The provider cannot be changed while editing a budget." : ""}>
              <Box sx={providerBoxStyle(provider === 'LANGFUSE', disabled)} onClick={() => handleProviderChange('LANGFUSE')}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}><img src="/icons/langfuse-icon.png" alt="LANGFUSE" style={{ marginRight: 8, width: 20, height: 20 }}/><Typography variant="body2" sx={{ fontFamily: 'Inter', fontSize: '13px', fontWeight: 400 }}>Langfuse</Typography></Box>
                <Radio checked={provider === 'LANGFUSE'} disabled={disabled} size="small" sx={{ p: 0, transform: 'scale(0.85)', '&.Mui-checked': { color: '#0064FF' } }}/>
              </Box>
              </Tooltip>
              <Box sx={providerBoxStyle(false, true)}><Box sx={{ display: 'flex', alignItems: 'center' }}><img src="/icons/aws.svg" alt="AWS" style={{ marginRight: 8, width: 20, height: 20 }}/><Typography variant="body2" sx={{ fontFamily: 'Inter', fontSize: '13px', fontWeight: 400 }}>Amazon Web Services</Typography></Box><Radio checked={false} size="small" disabled={true} sx={{ p: 0, transform: 'scale(0.85)' }}/></Box>
              <Box sx={providerBoxStyle(false, true)}><Box sx={{ display: 'flex', alignItems: 'center' }}><img src="/icons/azure2.svg" alt="Azure" style={{ marginRight: 8, width: 20, height: 20 }}/><Typography variant="body2" sx={{ fontFamily: 'Inter', fontSize: '13px', fontWeight: 400 }}>Microsoft Azure</Typography></Box><Radio checked={false} size="small" disabled={true} sx={{ p: 0, transform: 'scale(0.85)' }}/></Box>
            </Box>
            {errors.provider && <Typography color="error" variant="caption" sx={{ display: 'block', mt: 1, px: 1 }}>{errors.provider}</Typography>}

            {provider === 'GCP' && (
              <Box sx={{ width: '100%', bgcolor: '#F9FAFB', borderRadius: '10px', mt: 2, p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                  <Button onClick={handleAddService} disabled={services.length > 0 && services[services.length-1].serviceTypes.length === 0} sx={{ textTransform: 'none', color: '#0064FF', fontFamily: 'Inter', fontWeight: 500, fontSize: '14px' }}>
                    + Add More
                  </Button>
                </Box>

                {services.map((service, index) => {
                  const isProjectDisabled = service.dataSources.length === 0 || optionsLoading;
                  const isRegionDisabled = service.projectIds.length === 0 || optionsLoading;
                  const isServiceDisabled = service.regions.length === 0 || optionsLoading;

                  const dataSourceButtonText = service.dataSources.length === dataSourceOptions.length && dataSourceOptions.length > 0 ? 'All' : service.dataSources.length > 0 ? `${service.dataSources.length} selected` : 'Select data sources';
                  const projectButtonText = service.projectIds.length === projectOptions.length && projectOptions.length > 0 ? 'All' : service.projectIds.length > 0 ? `${service.projectIds.length} selected` : 'Select projects';
                  const allRegionsCount = activeRegions.length + availableRegions.length;
                  const regionButtonText = service.regions.length === allRegionsCount && allRegionsCount > 0 ? 'All' : service.regions.length > 0 ? `${service.regions.length} selected` : 'Select regions';
                  
                  const allServicesCount = activeServices.length + availableServices.length;
                  const serviceButtonText = service.serviceTypes.length === allServicesCount && allServicesCount > 0 ? 'All' : service.serviceTypes.length > 0 ? `${service.serviceTypes.length} selected` : 'Select services';
                  
                  const labelButtonText = service.label.length === 1 ? '1 filter applied' : service.label.length > 0 ? `${service.label.length} filters applied` : 'Select Label';

                  const getButtonSx = (hasSelection: boolean) => ({
                      width: '100%', height: '36px', backgroundColor: '#FFFFFF', border: `1px solid #E0E0E0`, borderRadius: '8px',
                      fontFamily: 'Inter', fontWeight: hasSelection ? 500 : 400, fontSize: '13px',
                      color: hasSelection ? '#333333' : '#999999', lineHeight: '18px', letterSpacing: '0%',
                      textTransform: 'none', justifyContent: 'space-between', '&:hover': { borderColor: '#0064FF' },
                      '&.Mui-disabled': { backgroundColor: '#F3F3F3', border: '1px solid #D3D3D3' }
                  });

                  const showLabelButton = service.serviceTypes.length > 0;
                  const isKubeSelected = service.serviceTypes.includes('Kubernetes Engine');
                  const showFullKubeFields = isKubeSelected && activeServices.includes('Kubernetes Engine');
                  return (
                    <Box key={service.id}>
                      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: 2, alignItems: 'flex-end' }}>
                        <Box>
                          <Typography sx={{ fontFamily: 'Inter', fontWeight: 400, fontSize: '13px', color: optionsLoading ? '#A0A0A0' : '#333333', mb: 0.5 }}>Data Source<span style={{ color: 'red' }}>*</span></Typography>
                          <Button ref={el => buttonRefs.current[`${service.id}-dataSources`] = el} onClick={(e) => handlePopperOpen(e, 'dataSources', service.id, service.dataSources)} endIcon={<ExpandMoreIcon />} sx={getButtonSx(service.dataSources.length > 0)} disabled={optionsLoading}>{dataSourceButtonText}</Button>
                        </Box>

                        <Box>
                          <Typography sx={{ fontFamily: 'Inter', fontWeight: 400, fontSize: '13px', color: isProjectDisabled ? '#A0A0A0' : '#333333', mb: 0.5 }}>Project<span style={{ color: 'red' }}>*</span></Typography>
                          <Button
                            ref={el => buttonRefs.current[`${service.id}-projectIds`] = el}
                            disabled={isProjectDisabled}
                            onClick={(e) => handlePopperOpen(e, 'projectIds', service.id, service.projectIds)}
                            endIcon={<ExpandMoreIcon />}
                            sx={getButtonSx(service.projectIds.length > 0)}
                          >
                            {projectButtonText}
                          </Button>
                        </Box>

                        <Box>
                          <Typography sx={{ fontFamily: 'Inter', fontWeight: 400, fontSize: '13px', color: isRegionDisabled ? '#A0A0A0' : '#333333', mb: 0.5 }}>Region<span style={{ color: 'red' }}>*</span></Typography>
                          <Button ref={el => buttonRefs.current[`${service.id}-regions`] = el} disabled={isRegionDisabled} onClick={(e) => handlePopperOpen(e, 'regions', service.id, service.regions)} endIcon={<ExpandMoreIcon />} sx={getButtonSx(service.regions.length > 0)}>{regionButtonText}</Button>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
                           <Box sx={{flexGrow: 1}}>
                                <Typography sx={{ fontFamily: 'Inter', fontWeight: 400, fontSize: '13px', color: isServiceDisabled ? '#A0A0A0' : '#333333', mb: 0.5 }}>Service<span style={{ color: 'red' }}>*</span></Typography>
                                <Button
                                    ref={el => buttonRefs.current[`${service.id}-service`] = el}
                                    disabled={isServiceDisabled}
                                    onClick={(e) => handlePopperOpen(e, 'gcpServices', service.id, service.serviceTypes)}
                                    endIcon={<ExpandMoreIcon />}
                                    sx={getButtonSx(service.serviceTypes.length > 0)}
                                >
                                    {serviceButtonText}
                                </Button>
                           </Box>
                          <IconButton onClick={() => handleDeleteService(service.id)} disabled={services.length <= 1} sx={{ mb: '4px' }}><img src={(services.length <= 1) ? "/icons/delete_icon_disabled.svg" : "/icons/delete_icon.svg"} alt="Delete Service" style={{ width: '17px', height: '17px' }} /></IconButton>
                        </Box>

                        {showLabelButton && (
                          <Box>
                            <Typography sx={{ fontFamily: 'Inter', fontWeight: 400, fontSize: '13px', color: isServiceDisabled ? '#A0A0A0' : '#333333', mb: 0.5 }}>Label</Typography>
                            <Button onClick={(e) => handleLabelMenuOpen(e, service.id)} disabled={isServiceDisabled} endIcon={<ExpandMoreIcon />} variant="outlined" sx={getButtonSx(service.label.length > 0)}>{labelButtonText}</Button>
                          </Box>
                        )}

                        {showFullKubeFields && (
                          <>
                            <Box>
                              <MultiSelectDropdown label="Cluster" options={k8sClusters} selectedValues={service.cluster} onChange={(newValues: string[]) => handleUpdateService(service.id, 'cluster', newValues)} disabled={isServiceDisabled} width="100%" />
                            </Box>
                            <Box>
                              <MultiSelectDropdown label="Namespace" options={k8sNamespaces} selectedValues={service.namespace} onChange={(newValues: string[]) => handleUpdateService(service.id, 'namespace', newValues)} disabled={isServiceDisabled} width="100%" />
                            </Box>
                          </>
                        )}
                      </Box>
                      {service.label.length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                          {service.label.flatMap(labelItem =>
                            labelItem.values.map(value => (
                              <Chip
                                key={`${labelItem.key}-${value}`}
                                label={`${labelItem.key}: ${value}`}
                                size="small"
                                onDelete={() => handleDeleteLabelChip(service.id, labelItem.key, value)}
                                sx={{
                                  backgroundColor: ' #F3F8FE',
                                  color: '#333333',
                                  fontFamily: 'Inter',
                                  fontSize: '12px',
                                  fontWeight: 500,
                                  borderRadius: '8px',
                                  '.MuiChip-deleteIcon': {
                                      color: '#333333',
                                      fontSize: '16px',
                                      '&:hover': {
                                        color: 'red',
                                      },
                                  },
                                }}
                              />
                            ))
                          )}
                        </Box>
                      )}
                      {index < services.length - 1 && <Divider sx={{ my: 2, mx: -2 }} />}
                    </Box>
                  )
                })}
              </Box>
            )}

            {provider === 'LANGFUSE' && (
                <Box sx={{ width: '100%', bgcolor: '#F9FAFB', borderRadius: '10px', mt: 2, p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                        <Button onClick={handleAddService} disabled={services.length > 0 && services[services.length-1].serviceTypes.length === 0} sx={{ textTransform: 'none', color: '#0064FF', fontFamily: 'Inter', fontWeight: 500, fontSize: '14px' }}>
                            + Add More
                        </Button>
                    </Box>
                    {services.map((service, index) => {
                        const isModelDisabled = service.dataSources.length === 0 || optionsLoading;
                        const isTraceDisabled = service.skus.length === 0 || optionsLoading;
        
                        const getButtonSx = (hasSelection: boolean) => ({
                            width: '100%', height: '36px', backgroundColor: '#FFFFFF', border: `1px solid #E0E0E0`, borderRadius: '8px',
                            fontFamily: 'Inter', fontWeight: hasSelection ? 500 : 400, fontSize: '13px',
                            color: hasSelection ? '#333333' : '#999999', lineHeight: '18px', letterSpacing: '0%',
                            textTransform: 'none', justifyContent: 'space-between', '&:hover': { borderColor: '#0064FF' },
                            '&.Mui-disabled': { backgroundColor: '#F3F3F3', border: '1px solid #D3D3D3' }
                        });
            
                        const dataSourceButtonText = service.dataSources.length === dataSourceOptions.length && dataSourceOptions.length > 0 ? 'All' : service.dataSources.length > 0 ? `${service.dataSources.length} selected` : 'Select data sources';
                        const modelButtonText = service.skus.length === skuOptions.length && skuOptions.length > 0 ? 'All' : service.skus.length > 0 ? `${service.skus.length} selected` : 'Select models';
                        const traceButtonText = service.serviceTypes.length === activeServices.length && activeServices.length > 0 ? 'All' : service.serviceTypes.length > 0 ? `${service.serviceTypes.length} selected` : 'Select traces';
        
                        return (
                        <Box key={service.id}>
                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: 2, alignItems: 'flex-end' }}>
                                <Box>
                                    <Typography sx={{ fontFamily: 'Inter', fontWeight: 400, fontSize: '13px', color: optionsLoading ? '#A0A0A0' : '#333333', mb: 0.5 }}>Data Source<span style={{ color: 'red' }}>*</span></Typography>
                                    <Button ref={el => buttonRefs.current[`${service.id}-langfuseDataSources`] = el} onClick={(e) => handlePopperOpen(e, 'langfuseDataSources', service.id, service.dataSources)} endIcon={<ExpandMoreIcon />} sx={getButtonSx(service.dataSources.length > 0)} disabled={optionsLoading}>{dataSourceButtonText}</Button>
                                </Box>
                                <Box>
                                    <Typography sx={{ fontFamily: 'Inter', fontWeight: 400, fontSize: '13px', color: isModelDisabled ? '#A0A0A0' : '#333333', mb: 0.5 }}>Model<span style={{ color: 'red' }}>*</span></Typography>
                                    <Button ref={el => buttonRefs.current[`${service.id}-skus`] = el} onClick={(e) => handlePopperOpen(e, 'skus', service.id, service.skus)} endIcon={<ExpandMoreIcon />} sx={getButtonSx(service.skus.length > 0)} disabled={isModelDisabled}>{modelButtonText}</Button>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
                                    <Box sx={{ flexGrow: 1 }}>
                                       <Typography sx={{ fontFamily: 'Inter', fontWeight: 400, fontSize: '13px', color: isTraceDisabled ? '#A0A0A0' : '#333333', mb: 0.5 }}>Trace<span style={{ color: 'red' }}>*</span></Typography>
                                       <Button ref={el => buttonRefs.current[`${service.id}-traces`] = el} onClick={(e) => handlePopperOpen(e, 'traces', service.id, service.serviceTypes)} endIcon={<ExpandMoreIcon />} sx={getButtonSx(service.serviceTypes.length > 0)} disabled={isTraceDisabled}>{traceButtonText}</Button>
                                    </Box>
                                    <IconButton onClick={() => handleDeleteService(service.id)} disabled={services.length <= 1} sx={{ mb: '4px' }}>
                                        <img src={(services.length <= 1) ? "/icons/delete_icon_disabled.svg" : "/icons/delete_icon.svg"} alt="Delete Service" style={{ width: '17px', height: '17px' }} />
                                    </IconButton>
                                </Box>
                            </Box>
                            {index < services.length - 1 && <Divider sx={{ my: 2, mx: -2 }} />}
                        </Box>
                        )
                    })}
                </Box>
            )}

            {renderMultiSelectPopper()}

            <LabelFilterMenu
                open={openPopper?.type === 'label'}
                anchorEl={labelMenuAnchor}
                onClose={handleLabelMenuClose}
                onApply={handleLabelApply}
                appliedFilters={activeServiceForLabel?.label || []}
                labelKeys={labelKeys}
                labelValues={labelValuesData}
                onFetchLabelValues={handleFetchLabelValues}
                loadingLabelKey={loadingLabelKey}
            />
            </Box>
        
    );
};

export default ScopeConfig;



// import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
// import { useLazyQuery, gql } from '@apollo/client';
// import { useOutletContext } from 'react-router-dom';
// import {
//   Box, Typography, Radio, Divider, Button, Popper, Grow, Paper, Checkbox, FormControlLabel,
//   ClickAwayListener, IconButton, ListSubheader, TextField, InputAdornment, Tooltip, Chip
// } from '@mui/material';
// import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
// import ExpandLessIcon from '@mui/icons-material/ExpandLess';
// import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
// import LabelFilterMenu, { LabelFilterPair } from './LabelFilterMenu';

// // --- MODIFIED GraphQL Query Definition ---
// const GET_BUDGET_FILTER_OPTIONS = gql`
//   query GetBudgetFilterOptions($input: BudgetFilterOptionsInput!) {
//     getBudgetFilterOptions(input: $input) {
//       dataSources { id, name, providerType, isSyncing }
//       activeRegions, availableRegions, activeServices, availableServices, projectIds
//       labelKeys, k8sClusters, k8sNamespaces, skus
//     }
//   }
// `;

// const GET_LABEL_VALUES = gql`
//   query GetBudgetLabelValues($input: BudgetLabelValuesInput!) {
//     getBudgetLabelValues(input: $input) {
//       values
//     }
//   }
// `;

// // --- MODIFIED Interface ---
// export interface ServiceState {
//   id: string;
//   dataSources: string[];
//   projectIds: string[];
//   regions: string[];
//   serviceTypes: string[];
//   label: LabelFilterPair[];
//   cluster: string[];
//   namespace: string[];
//   deployment: string[];
//   skus: string[]; // Added for LANGFUSE models
// }

// interface ScopeConfigProps {
//     provider: string;
//     setProvider: (provider: string) => void;
//     services: ServiceState[];
//     setServices: React.Dispatch<React.SetStateAction<ServiceState[]>>;
//     getInitialService: () => Omit<ServiceState, 'id' | 'serviceTypes'> & { serviceTypes: string[] };
//     errors: { [key: string]: string };
//     disabled?: boolean;
//     period: 'Monthly' | 'Quarterly' | 'Yearly';
// }

// interface AppOutletContext {
//   currentTenant?: { id: string; name: string; };
// }

// interface DataSourceOption {
//   id: string;
//   name: string;
//   providerType: string;
//   isSyncing: boolean;
// }

// // SectionedMultiSelectDropdown remains unchanged
// const SectionedMultiSelectDropdown = React.forwardRef<
//   HTMLButtonElement,
//   {
//     label: string;
//     options: { active: string[]; available: string[] };
//     selectedValues: string[];
//     onChange: (selected: string[]) => void;
//     disabled?: boolean;
//   }
// >(({ label, options, selectedValues, onChange, disabled = false }, ref) => {
//   const [isOpen, setIsOpen] = useState(false);
//   const [searchTerm, setSearchTerm] = useState('');
//   const anchorRef = useRef<HTMLButtonElement>(null);

//   const handleRef = useCallback((node: HTMLButtonElement) => {
//     (anchorRef as React.MutableRefObject<HTMLButtonElement | null>).current = node;
//     if (typeof ref === 'function') {
//       ref(node);
//     } else if (ref) {
//       (ref as React.MutableRefObject<HTMLButtonElement | null>).current = node;
//     }
//   }, [ref]);

//   const handleOpen = () => setIsOpen(true);
//   const handleClose = () => {
//     setIsOpen(false);
//     setSearchTerm('');
//   };

//   const filteredActive = useMemo(() => options.active.filter(opt => opt.toLowerCase().includes(searchTerm.toLowerCase())), [options.active, searchTerm]);
//   const filteredAvailable = useMemo(() => options.available.filter(opt => opt.toLowerCase().includes(searchTerm.toLowerCase())), [options.available, searchTerm]);
  
//   const isKubeSelected = useMemo(() => selectedValues.includes('Kubernetes Engine'), [selectedValues]);

//   const isAnyActiveSelected = useMemo(() => options.active.some(opt => selectedValues.includes(opt) && opt !== 'Kubernetes Engine'), [options.active, selectedValues]);
//   const isAnyAvailableSelected = useMemo(() => options.available.some(opt => selectedValues.includes(opt) && opt !== 'Kubernetes Engine'), [options.available, selectedValues]);

//   const nonKubeFilteredActive = useMemo(() => filteredActive.filter(s => s !== 'Kubernetes Engine'), [filteredActive]);
//   const nonKubeFilteredAvailable = useMemo(() => filteredAvailable.filter(s => s !== 'Kubernetes Engine'), [filteredAvailable]);

//   const isAllActiveSelected = nonKubeFilteredActive.length > 0 && nonKubeFilteredActive.every(s => selectedValues.includes(s));
//   const isSomeActiveSelected = nonKubeFilteredActive.some(s => selectedValues.includes(s)) && !isAllActiveSelected;

//   const isAllAvailableSelected = nonKubeFilteredAvailable.length > 0 && nonKubeFilteredAvailable.every(s => selectedValues.includes(s));
//   const isSomeAvailableSelected = nonKubeFilteredAvailable.some(s => selectedValues.includes(s)) && !isAllAvailableSelected;

//   const handleToggle = (value: string) => {
//     if (value === 'Kubernetes Engine') {
//       onChange(isKubeSelected ? [] : ['Kubernetes Engine']);
//       return;
//     }
//     if (isKubeSelected) return;

//     const isTogglingActive = options.active.includes(value);

//     if (isTogglingActive && isAnyAvailableSelected) {
//       onChange([value]);
//       return;
//     }
//     if (!isTogglingActive && isAnyActiveSelected) {
//       onChange([value]);
//       return;
//     }

//     const newSelected = selectedValues.includes(value)
//       ? selectedValues.filter(v => v !== value)
//       : [...selectedValues, value];
//     onChange(newSelected);
//   };

//   const handleSelectAll = (itemsToToggle: string[], isSelecting: boolean) => {
//     if (isSelecting) {
//       const nonKubeItems = itemsToToggle.filter(v => v !== 'Kubernetes Engine');
//       onChange(nonKubeItems);
//     } else {
//       onChange([]);
//     }
//   };

//   const allOptions = [...options.active, ...options.available];
  
//   const buttonText =
//     selectedValues.length === 0 ? `Select ${label.toLowerCase()}`
//     : selectedValues.length === allOptions.length && allOptions.length > 0 ? 'All selected'
//     : `${selectedValues.length} selected`;

//     const renderOption = (option: string) => {
//         const isThisOptionActive = options.active.includes(option);
//         const isThisOptionAvailable = options.available.includes(option);
        
//         const isDisabled = 
//             (isKubeSelected && option !== 'Kubernetes Engine') || 
//             ((isAnyActiveSelected || isAnyAvailableSelected) && option === 'Kubernetes Engine') ||
//             (isThisOptionAvailable && isAnyActiveSelected) ||
//             (isThisOptionActive && isAnyAvailableSelected);

//         return (
//           <FormControlLabel
//               key={option}
//               sx={{ width: '100%', m: 0, px: 2, py: 0.5 }}
//               control={
//                   <Checkbox
//                       size="small"
//                       checked={selectedValues.includes(option)}
//                       onChange={() => handleToggle(option)}
//                       disabled={isDisabled}
//                       sx={{ '&.Mui-checked': { color: '#0064FF' } }}
//                   />
//               }
//               label={
//                 <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
//                   <Typography sx={{ fontSize: '13px', color: isDisabled ? '#B0B0B0' : selectedValues.includes(option) ? '#333333' : '#666666' }}>
//                     {option}
//                   </Typography>
//                   {option === 'Kubernetes Engine' && isDisabled && (
//                     <Tooltip
//                       title="As you have selected other services, Kubernetes Engine is disabled. To select it, please clear other services."
//                       arrow
//                       componentsProps={{
//                         tooltip: {
//                           sx: { backgroundColor: '#FFFFFF', color: '#000000', border: '1px solid #E0E0E0', fontSize: '12px', },
//                         },
//                         arrow: { sx: { color: '#FFFFFF', }, },
//                       }}
//                     >
//                       <InfoOutlinedIcon sx={{ color: '#0064FF', ml: 1, fontSize: '16px', }} />
//                     </Tooltip>
//                   )}
//                 </Box>
//               }
//           />
//         );
//       };

//   return (
//     <Box sx={{ width: '100%' }}>
//       <Typography sx={{ fontFamily: 'Inter', fontWeight: 400, fontSize: '13px', color: disabled ? '#A0A0A0' : '#333333', mb: 0.5 }}>
//         {label}<span style={{ color: 'red' }}>*</span>
//       </Typography>
//       <Button
//         ref={handleRef} variant="outlined" disabled={disabled} onClick={handleOpen}
//         endIcon={isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
//         sx={{
//           width: '100%', height: '36px', backgroundColor: disabled ? '#F3F3F3' : '#FFFFFF',
//           border: `1px solid ${disabled ? '#D3D3D3' : '#E0E0E0'}`, borderRadius: '8px',
//           fontFamily: 'Inter', fontWeight: selectedValues.length > 0 ? 500 : 400, fontSize: '13px',
//           color: selectedValues.length > 0 ? '#333333' : '#999999', lineHeight: '18px',
//           letterSpacing: '0%', textTransform: 'none', justifyContent: 'space-between',
//           '&:hover': { borderColor: disabled ? '#D3D3D3' : '#0064FF' },
//         }}
//       >
//         {buttonText}
//       </Button>
//       <Popper
//         open={isOpen} anchorEl={anchorRef.current} placement="bottom-start"
//         transition sx={{ zIndex: 1300, width: anchorRef.current?.offsetWidth }}
//       >
//         {({ TransitionProps }) => (
//           <ClickAwayListener onClickAway={handleClose}>
//             <Grow {...TransitionProps} style={{ transformOrigin: 'top left' }}>
//               <Paper sx={{ borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.1)', maxHeight: '300px', display: 'flex', flexDirection: 'column' }}>
//                 <Box sx={{ p: 2, borderBottom: '1px solid #f0f0f0' }}>
//                     <TextField
//                         autoFocus placeholder="Search by service name.." value={searchTerm}
//                         onChange={(e) => setSearchTerm(e.target.value)}
//                         onKeyDown={(e) => e.stopPropagation()} fullWidth variant="outlined"
//                         InputProps={{
//                             startAdornment: (
//                                 <InputAdornment position="start" sx={{ ml: -0.5 }}>
//                                 <img src="/icons/search_icon.svg" alt="Search" style={{ width: '16px', height: '16px' }} />
//                                 </InputAdornment>
//                             ),
//                             sx: { height: '32px', borderRadius: '10px', backgroundColor: 'rgba(0, 0, 0, 0.04)', '& .MuiOutlinedInput-notchedOutline': { border: 'none' }, '& .MuiInputBase-input::placeholder': { fontSize: '14px', fontWeight: 300 } },
//                         }}
//                     />
//                 </Box>
//                 <Box sx={{ overflowY: 'auto' }}>
//                     {filteredActive.length === 0 && filteredAvailable.length === 0 ? (
//                         <Typography sx={{ p: 2, color: '#999', fontSize: '13px', textAlign: 'center' }}>No results found</Typography>
//                     ) : (
//                         <>
//                             {filteredActive.length > 0 && (
//                                 <>
//                                     <ListSubheader sx={{ bgcolor: '#F9FAFB', lineHeight: '32px', fontSize: '13px', fontWeight: 600 }}>Active Services</ListSubheader>
//                                     <FormControlLabel
//                                         sx={{ width: '100%', m: 0, px: 2, py: 0.5 }}
//                                         label={<Typography sx={{ fontSize: '13px' }}>All</Typography>}
//                                         control={<Checkbox size="small" checked={isAllActiveSelected} indeterminate={isSomeActiveSelected} onChange={(e) => handleSelectAll(filteredActive, e.target.checked)} disabled={isKubeSelected || isAnyAvailableSelected} sx={{'&.Mui-checked, &.MuiCheckbox-indeterminate': { color: '#0064FF' }}} />}
//                                     />
//                                     {filteredActive.map(renderOption)}
//                                 </>
//                             )}
//                             {filteredAvailable.length > 0 && (
//                                 <>
//                                     <ListSubheader sx={{ bgcolor: '#F9FAFB', lineHeight: '32px', fontSize: '13px', fontWeight: 600 }}>Available Services</ListSubheader>
//                                     <FormControlLabel
//                                         sx={{ width: '100%', m: 0, px: 2, py: 0.5 }}
//                                         label={<Typography sx={{ fontSize: '13px' }}>All</Typography>}
//                                         control={<Checkbox size="small" checked={isAllAvailableSelected} indeterminate={isSomeAvailableSelected} onChange={(e) => handleSelectAll(filteredAvailable, e.target.checked)} disabled={isKubeSelected || isAnyActiveSelected} sx={{'&.Mui-checked, &.MuiCheckbox-indeterminate': { color: '#0064FF' }}} />}
//                                     />
//                                     {filteredAvailable.map(renderOption)}
//                                 </>
//                             )}
//                         </>
//                     )}
//                 </Box>
//                 <Divider />
//                 <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1, flexShrink: 0 }}>
//                   <Button onClick={() => onChange([])} sx={{ textTransform: 'none', color: '#999999' }}>Clear</Button>
//                   <Button onClick={handleClose} sx={{ textTransform: 'none', color: '#0064FF' }}>Apply</Button>
//                 </Box>
//               </Paper>
//             </Grow>
//           </ClickAwayListener>
//         )}
//       </Popper>
//     </Box>
//   );
// });

// // MultiSelectDropdown remains unchanged
// const MultiSelectDropdown: React.FC<{
//   label: string;
//   options: string[];
//   selectedValues: string[];
//   onChange: (selected: string[]) => void;
//   disabled?: boolean;
//   width?: string | number;
// }> = ({ label, options, selectedValues, onChange, disabled = false, width = '100%' }) => {
//   const [isOpen, setIsOpen] = useState(false);
//   const anchorRef = useRef<HTMLButtonElement>(null);

//   const handleOpen = () => setIsOpen(true);
//   const handleClose = () => setIsOpen(false);

//   const handleToggle = (value: string) => {
//     const newSelected = selectedValues.includes(value)
//       ? selectedValues.filter((v) => v !== value)
//       : [...selectedValues, value];
//     onChange(newSelected);
//   };

//   const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
//     onChange(event.target.checked ? options : []);
//   };

//   const isAllSelected = options.length > 0 && selectedValues.length === options.length;
//   const buttonText =
//     selectedValues.length === 0 ? `Select ${label.toLowerCase()}`
//     : selectedValues.length === options.length && options.length > 0 ? 'All selected'
//     : `${selectedValues.length} selected`;

//   return (
//     <Box sx={{ width }}>
//       <Typography sx={{ fontFamily: 'Inter', fontWeight: 400, fontSize: '13px', color: disabled ? '#A0A0A0' : '#333333', mb: 0.5 }}>
//         {label}<span style={{ color: 'red' }}>*</span>
//       </Typography>
//       <Button
//         ref={anchorRef}
//         variant="outlined"
//         disabled={disabled}
//         onClick={handleOpen}
//         endIcon={isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
//         sx={{
//           width: '100%', height: '36px', backgroundColor: disabled ? '#F3F3F3' : '#FFFFFF',
//           border: `1px solid ${disabled ? '#D3D3D3' : '#E0E0E0'}`, borderRadius: '8px',
//           fontFamily: 'Inter',
//           fontWeight: selectedValues.length > 0 ? 500 : 400,
//           fontSize: '13px',
//           color: selectedValues.length > 0 ? '#333333' : '#999999',
//           lineHeight: '18px',
//           letterSpacing: '0%',
//           textTransform: 'none',
//           justifyContent: 'space-between',
//           '&:hover': { borderColor: disabled ? '#D3D3D3' : '#0064FF' },
//         }}
//       >
//         {buttonText}
//       </Button>
//       <Popper
//         open={isOpen}
//         anchorEl={anchorRef.current}
//         placement="bottom-start"
//         transition
//         sx={{ zIndex: 1300, width: anchorRef.current?.offsetWidth }}
//       >
//         {({ TransitionProps }) => (
//           <ClickAwayListener onClickAway={handleClose}>
//             <Grow {...TransitionProps} style={{ transformOrigin: 'top left' }}>
//               <Paper sx={{ borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
//                 <FormControlLabel
//                   label={<Typography sx={{ fontSize: '13px', color: isAllSelected ? '#333333' : '#666666' }}>All</Typography>}
//                   sx={{ width: '100%', m: 0, px: 2, py: 0.5 }}
//                   control={
//                     <Checkbox size="small" checked={isAllSelected}
//                       indeterminate={selectedValues.length > 0 && !isAllSelected}
//                       onChange={handleSelectAll}
//                       sx={{ '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: '#0064FF' } }}
//                     />
//                   }
//                 />
//                 <Box sx={{ maxHeight: '200px', overflowY: 'auto' }}>
//                   {options.map((option) => (
//                     <FormControlLabel
//                       key={option} sx={{ width: '100%', m: 0, px: 2, py: 0.5 }}
//                       control={
//                         <Checkbox size="small" checked={selectedValues.includes(option)}
//                           onChange={() => handleToggle(option)}
//                           sx={{ '&.Mui-checked': { color: '#0064FF' } }}
//                         />
//                       }
//                       label={<Typography sx={{ fontSize: '13px', color: selectedValues.includes(option) ? '#333333' : '#666666' }}>{option}</Typography>}
//                     />
//                   ))}
//                 </Box>
//                 <Divider />
//                 <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
//                   <Button onClick={() => onChange([])} sx={{ textTransform: 'none', color: '#999999' }}>Clear</Button>
//                   <Button onClick={handleClose} sx={{ textTransform: 'none', color: '#0064FF' }}>Apply</Button>
//                 </Box>
//               </Paper>
//             </Grow>
//           </ClickAwayListener>
//         )}
//       </Popper>
//     </Box>
//   );
// };


// // --- Main ScopeConfig Component ---
// const ScopeConfig: React.FC<ScopeConfigProps> = ({
//   provider, setProvider, services, setServices, getInitialService,
//   errors, disabled = false, period,
// }) => {
//     const { currentTenant } = useOutletContext<AppOutletContext>();
//     const [openPopper, setOpenPopper] = useState<{ type: 'dataSources' | 'projectIds' | 'regions' | 'label', serviceId: string } | null>(null);
//     const [tempSelections, setTempSelections] = useState<string[]>([]);
//     const [regionSearchTerm, setRegionSearchTerm] = useState('');

//     const buttonRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({});
//     const [popperAnchor, setPopperAnchor] = useState<HTMLButtonElement | null>(null);
//     const [labelMenuAnchor, setLabelMenuAnchor] = useState<HTMLElement | null>(null);
//     const popperPaperRef = useRef(null);
//     //const hasOpenedAutomatically = useRef(false);

//     const [dataSourceOptions, setDataSourceOptions] = useState<DataSourceOption[]>([]);
//     const [projectOptions, setProjectOptions] = useState<string[]>([]);
//     const [activeRegions, setActiveRegions] = useState<string[]>([]);
//     const [availableRegions, setAvailableRegions] = useState<string[]>([]);
//     const [activeServices, setActiveServices] = useState<string[]>([]);
//     const [availableServices, setAvailableServices] = useState<string[]>([]);
//     const [labelKeys, setLabelKeys] = useState<string[]>([]);
//     const [k8sClusters, setK8sClusters] = useState<string[]>([]);
//     const [k8sNamespaces, setK8sNamespaces] = useState<string[]>([]);
//     const [skuOptions, setSkuOptions] = useState<string[]>([]); // For LANGFUSE Models
//     const [labelValuesData, setLabelValuesData] = useState<{ [key: string]: string[] }>({});
//     const [loadingLabelKey, setLoadingLabelKey] = useState<string | null>(null);

//     const dataSourceDependency = useMemo(() => {
//         const sortedIds = Array.from(new Set(services.flatMap(s => s.dataSources))).sort();
//         return JSON.stringify(sortedIds);
//     }, [services]);

//     const [getOptions, { loading: optionsLoading }] = useLazyQuery(GET_BUDGET_FILTER_OPTIONS, {
//         onCompleted: (data) => {
//             if (data?.getBudgetFilterOptions) {
//                 const {
//                     dataSources, projectIds, activeRegions, availableRegions,
//                     activeServices, availableServices, labelKeys, k8sClusters,
//                     k8sNamespaces, skus
//                 } = data.getBudgetFilterOptions;

//                 setDataSourceOptions(dataSources || []);
//                 setProjectOptions(projectIds || []);
//                 setActiveRegions(activeRegions || []);
//                 setAvailableRegions(availableRegions || []);
//                 setActiveServices(activeServices || []);
//                 setAvailableServices(availableServices || []);
//                 setLabelKeys(labelKeys || []);
//                 setK8sClusters(k8sClusters || []);
//                 setK8sNamespaces(k8sNamespaces || []);
//                 setSkuOptions(skus || []);
//             }
//         }
//     });

//     const [getLabelValues] = useLazyQuery(GET_LABEL_VALUES, {
//         onCompleted: (data) => {
//             if (loadingLabelKey && data?.getBudgetLabelValues?.values) {
//                 setLabelValuesData(prev => ({ ...prev, [loadingLabelKey]: data.getBudgetLabelValues.values }));
//             }
//             setLoadingLabelKey(null);
//         }
//     });

//     useEffect(() => {
//         if (!provider || !currentTenant?.id || !period) return;
//         const allSelectedDataSources = JSON.parse(dataSourceDependency);
//         getOptions({
//             variables: {
//                 input: {
//                     tenantId: currentTenant.id,
//                     provider,
//                     period: period.toUpperCase(),
//                     dataSourceIds: allSelectedDataSources,
//                 }
//             }
//         });
//     }, [provider, currentTenant, period, dataSourceDependency, getOptions]);

//     // useEffect(() => {
//     //     if (provider === 'GCP' && services.length === 1 && services[0].dataSources.length === 0 &&
//     //         !disabled && !optionsLoading && !hasOpenedAutomatically.current && dataSourceOptions.length > 0) {
//     //         const firstServiceId = services[0].id;
//     //         const dataSourceButton = buttonRefs.current[`${firstServiceId}-dataSources`];
//     //         if (dataSourceButton) {
//     //             setPopperAnchor(dataSourceButton);
//     //             setTempSelections(services[0].dataSources);
//     //             setOpenPopper({ type: 'dataSources', serviceId: firstServiceId });
//     //             hasOpenedAutomatically.current = true;
//     //         }
//     //     }
//     //     if (provider !== 'GCP' || services.length === 0) {
//     //         hasOpenedAutomatically.current = false;
//     //     }
//     // }, [provider, services, disabled, optionsLoading, dataSourceOptions]);

//     const providerBoxStyle = (isSelected: boolean, isDisabled: boolean) => ({
//       p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
//       backgroundColor: isSelected ? '#F2F7FF' : (isDisabled ? '#F3F3F3' : '#FFFFFF'),
//       border: `1px solid ${isSelected ? '#0064FF' : '#E0E0E0'}`,
//       borderRadius: '8px', cursor: isDisabled ? 'not-allowed' : 'pointer', flex: 1, height: '36px',
//       opacity: isDisabled ? 0.6 : 1,
//       '&:hover': { borderColor: isDisabled ? '#E0E0E0' : '#0064FF' }
//     });
    
//     const handleProviderChange = (newProvider: string) => {
//         if (disabled || provider === newProvider) return;
//         setProvider(newProvider);
//         // Reset services when provider changes
//         setServices([{ id: `service_${Date.now()}`, ...getInitialService() }]);
//     };

//     const handlePopperOpen = (event: React.MouseEvent<HTMLButtonElement>, type: 'dataSources' | 'projectIds' | 'regions', serviceId: string, currentValues: string[]) => {
//       setPopperAnchor(event.currentTarget);
//       setTempSelections(currentValues);
//       setOpenPopper({ type, serviceId });
//     };

//     const handlePopperClose = () => {
//       setOpenPopper(null);
//       setRegionSearchTerm('');
//       setPopperAnchor(null);
//     };

//     const handleUpdateService = useCallback((serviceId: string, field: keyof Omit<ServiceState, 'id'>, value: any) => {
//         setServices(prev => prev.map(s => {
//             if (s.id === serviceId) {
//                 const updatedService = { ...s, [field]: value };
//                 // GCP specific reset logic
//                 if (provider === 'GCP') {
//                   if (field === 'dataSources') {
//                       updatedService.projectIds = []; updatedService.regions = []; updatedService.serviceTypes = [];
//                       updatedService.label = []; updatedService.cluster = []; updatedService.namespace = [];
//                   }
//                   if (field === 'projectIds') {
//                       updatedService.regions = []; updatedService.serviceTypes = []; updatedService.label = [];
//                       updatedService.cluster = []; updatedService.namespace = [];
//                   }
//                   if (field === 'regions') {
//                       updatedService.serviceTypes = []; updatedService.label = [];
//                       updatedService.cluster = []; updatedService.namespace = [];
//                   }
//                   if (field === 'serviceTypes' && !value.includes('Kubernetes Engine')) {
//                      updatedService.cluster = []; updatedService.namespace = [];
//                   }
//                 }
//                 // LANGFUSE specific reset logic
//                 if (provider === 'LANGFUSE') {
//                     if (field === 'dataSources') {
//                         updatedService.skus = [];
//                         updatedService.serviceTypes = []; // Traces
//                     }
//                 }
//                 return updatedService;
//             }
//             return s;
//         }));
//     }, [setServices, provider]);

//     const handleApply = () => {
//         if (!openPopper) return;
//         const { serviceId, type } = openPopper;
//         handleUpdateService(serviceId, type as any, tempSelections);
//         handlePopperClose();
//     };

//     const handleFetchLabelValues = useCallback((key: string) => {
//         if (!key || labelValuesData[key] || loadingLabelKey) return;
//         setLoadingLabelKey(key);
//         const allDataSourceIds = Array.from(new Set(services.flatMap(s => s.dataSources))).sort();
//         getLabelValues({
//             variables: {
//                 input: {
//                     tenantId: currentTenant?.id,
//                     provider,
//                     labelKey: key,
//                     period: period.toUpperCase(),
//                     dataSourceIds: allDataSourceIds,
//                 }
//             }
//         });
//     }, [getLabelValues, provider, currentTenant, services, labelValuesData, loadingLabelKey, period]);

//     const handleDeleteLabelChip = (serviceId: string, keyToDelete: string, valueToDelete: string) => {
//         setServices(prevServices =>
//           prevServices.map(service => {
//             if (service.id === serviceId) {
//               const updatedLabels = service.label
//                 .map(label => {
//                   if (label.key === keyToDelete) {
//                     const newValues = label.values.filter(v => v !== valueToDelete);
//                     if (newValues.length > 0) {
//                       return { ...label, values: newValues };
//                     }
//                     return null;
//                   }
//                   return label;
//                 })
//                 .filter((label): label is LabelFilterPair => label !== null);

//               return { ...service, label: updatedLabels };
//             }
//             return service;
//           })
//         );
//       };

//       const handleAddService = useCallback(() => {
//         const newService = { id: `service_${Date.now()}`, ...getInitialService() };
//         setServices(prev => [...prev, newService]);
//       }, [getInitialService, setServices]);

//       const handleDeleteService = useCallback((idToDelete: string) => {
//         setServices(p => p.filter(s => s.id !== idToDelete));
//       }, [setServices]);

//       const handleLabelMenuOpen = (event: React.MouseEvent<HTMLElement>, serviceId: string) => {
//           setLabelMenuAnchor(event.currentTarget);
//           setOpenPopper({ type: 'label', serviceId });
//       };

//       const handleLabelMenuClose = () => {
//         setOpenPopper(null);
//         setLabelMenuAnchor(null);
//       };

//       const handleLabelApply = (newFilters: LabelFilterPair[]) => {
//           if (openPopper?.type === 'label') {
//               handleUpdateService(openPopper.serviceId, 'label', newFilters);
//           }
//           handleLabelMenuClose();
//       };

//       const activeServiceForLabel = services.find(s => s.id === openPopper?.serviceId);

//     const renderMultiSelectPopper = () => {
//       if (!openPopper || !popperAnchor) return null;
//       const checkboxStyle = { '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: '#0064FF' } };

//       if (openPopper.type === 'dataSources') {
//         const options = dataSourceOptions.map(s => s.id);
//         const optionLabels = dataSourceOptions;
//         const isAllSelected = tempSelections.length === options.length && options.length > 0;
//         const isSomeSelected = tempSelections.length > 0 && !isAllSelected;
//         const handleSelectAll = () => setTempSelections(isAllSelected ? [] : options);
//         return (
//           <Popper open={true} anchorEl={popperAnchor} placement="bottom-start" transition sx={{zIndex: 1200}} modifiers={[{name: 'offset', options: {offset: [0, 8]}}]}>
//             {({ TransitionProps }) => (
//                 <Grow {...TransitionProps} style={{ transformOrigin: 'top left' }}>
//                     <Paper ref={popperPaperRef} sx={{ width: popperAnchor?.offsetWidth, borderRadius: '12px', backgroundColor: '#FFFFFF', boxShadow: '0 4px 20px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
//                         <ClickAwayListener onClickAway={handlePopperClose}>
//                             <Box>
//                                 <FormControlLabel
//                                     label={<Typography sx={{ fontSize: '13px', color: isAllSelected ? '#333333' : '#666666' }}>All</Typography>}
//                                     sx={{width: '100%', m:0, px: 2, py: 0.5}}
//                                     control={ <Checkbox size="small" checked={isAllSelected} indeterminate={isSomeSelected} onChange={handleSelectAll} sx={checkboxStyle}/> }
//                                 />
//                                 <Box sx={{ maxHeight: '140px', overflow: 'auto' }}>
//                                   {optionLabels.map(option => (
//                                     <Tooltip
//                                       key={option.id}
//                                       title={option.isSyncing ? "This data source is currently syncing and cannot be selected." : ""}
//                                       placement="right"
//                                       arrow
//                                     >
//                                       <div>
//                                         <FormControlLabel
//                                             sx={{width: '100%', m:0, px:2, py:0.5}}
//                                             disabled={option.isSyncing}
//                                             control={
//                                               <Checkbox
//                                                 size="small"
//                                                 checked={tempSelections.includes(option.id)}
//                                                 onChange={() => setTempSelections(p => p.includes(option.id) ? p.filter(i => i !== option.id) : [...p, option.id])}
//                                                 sx={checkboxStyle}
//                                               />
//                                             }
//                                             label={
//                                               <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
//                                                 <Typography sx={{ fontSize: '13px', color: tempSelections.includes(option.id) ? '#333333' : '#666666' }}>
//                                                   {option.name}
//                                                 </Typography>
//                                                 {option.isSyncing && (
//                                                   <Box component="span" sx={{display: 'inline-flex', alignItems: 'center', height: '18px', px: 0.8, borderRadius: '10px', backgroundColor: 'rgba(255, 193, 7, 0.2)', color: 'rgba(180, 120, 0)', fontSize: '11px', fontWeight: 500 }}>
//                                                     Syncing
//                                                   </Box>
//                                                 )}
//                                               </Box>
//                                             }
//                                         />
//                                       </div>
//                                     </Tooltip>
//                                   ))}
//                                 </Box>
//                                 <Divider />
//                                 <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', p: 1 }}>
//                                     <Button onClick={() => setTempSelections([])} sx={{ textTransform: 'none', color: '#999999' }}>Clear</Button>
//                                     <Button onClick={handleApply} sx={{ textTransform: 'none', color: '#0064FF' }}>Apply</Button>
//                                 </Box>
//                             </Box>
//                         </ClickAwayListener>
//                     </Paper>
//                 </Grow>
//             )}
//         </Popper>
//         );
//       }

//     if (openPopper.type === 'projectIds') {
//           const options = projectOptions;
//           const isAllSelected = tempSelections.length === options.length && options.length > 0;
//           const isSomeSelected = tempSelections.length > 0 && !isAllSelected;
//           const handleSelectAll = () => setTempSelections(isAllSelected ? [] : options);
//           return (
//               <Popper open={true} anchorEl={popperAnchor} placement="bottom-start" transition sx={{zIndex: 1200}} modifiers={[{name: 'offset', options: {offset: [0, 8]}}]}>
//                   {({ TransitionProps }) => (
//                       <Grow {...TransitionProps} style={{ transformOrigin: 'top left' }}>
//                           <Paper ref={popperPaperRef} sx={{ width: popperAnchor?.offsetWidth, borderRadius: '12px', backgroundColor: '#FFFFFF', boxShadow: '0 4px 20px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
//                               <ClickAwayListener onClickAway={handlePopperClose}>
//                                   <Box>
//                                       <FormControlLabel
//                                           label={<Typography sx={{ fontSize: '13px', color: isAllSelected ? '#333333' : '#666666' }}>All</Typography>}
//                                           sx={{width: '100%', m:0, px: 2, py: 0.5}}
//                                           control={ <Checkbox size="small" checked={isAllSelected} indeterminate={isSomeSelected} onChange={handleSelectAll} sx={checkboxStyle}/> }
//                                       />
//                                       <Box sx={{ maxHeight: '140px', overflow: 'auto' }}>
//                                           {options.map(option => (
//                                               <FormControlLabel
//                                                   key={option}
//                                                   sx={{width: '100%', m:0, px:2, py:0.5}}
//                                                   control={ <Checkbox size="small" checked={tempSelections.includes(option)} onChange={() => setTempSelections(p => p.includes(option) ? p.filter(i => i !== option) : [...p, option])} sx={checkboxStyle}/> }
//                                                   label={<Typography sx={{ fontSize: '13px', color: tempSelections.includes(option) ? '#333333' : '#666666' }}>{option}</Typography>}
//                                               />
//                                           ))}
//                                       </Box>
//                                       <Divider />
//                                       <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', p: 1 }}>
//                                           <Button onClick={() => setTempSelections([])} sx={{ textTransform: 'none', color: '#999999' }}>Clear</Button>
//                                           <Button onClick={handleApply} sx={{ textTransform: 'none', color: '#0064FF' }}>Apply</Button>
//                                       </Box>
//                                   </Box>
//                               </ClickAwayListener>
//                           </Paper>
//                       </Grow>
//                   )}
//               </Popper>
//           );
//       }

//     if (openPopper.type === 'regions') {
//         const filteredActive = activeRegions.filter(r => r.toLowerCase().includes(regionSearchTerm.toLowerCase()));
//         const filteredAvailable = availableRegions.filter(r => r.toLowerCase().includes(regionSearchTerm.toLowerCase()));
//         const handleRegionToggle = (value: string) => {
//             const newSelection = tempSelections.includes(value)
//                 ? tempSelections.filter(v => v !== value)
//                 : [...tempSelections, value];
//             setTempSelections(newSelection);
//         };
//         const handleSelectAll = (itemsToToggle: string[], shouldSelect: boolean) => {
//             const otherSelections = tempSelections.filter(s => !itemsToToggle.includes(s));
//             const newSelections = shouldSelect
//                 ? Array.from(new Set([...otherSelections, ...itemsToToggle]))
//                 : otherSelections;
//             setTempSelections(newSelections);
//         };
//         const isAllActiveSelected = filteredActive.length > 0 && filteredActive.every(r => tempSelections.includes(r));
//         const isSomeActiveSelected = filteredActive.some(r => tempSelections.includes(r)) && !isAllActiveSelected;
//         const isAllAvailableSelected = filteredAvailable.length > 0 && filteredAvailable.every(r => tempSelections.includes(r));
//         const isSomeAvailableSelected = filteredAvailable.some(r => tempSelections.includes(r)) && !isAllAvailableSelected;
//         const renderRegionOption = (option: string) => (
//             <FormControlLabel
//                 key={option}
//                 sx={{ width: '100%', m: 0, px: 2, py: 0.5 }}
//                 control={<Checkbox size="small" checked={tempSelections.includes(option)} onChange={() => handleRegionToggle(option)} sx={checkboxStyle}/>}
//                 label={<Typography sx={{ fontSize: '13px', color: tempSelections.includes(option) ? '#333333' : '#666666' }}>{option}</Typography>}
//             />
//         );
//         return (
//             <Popper open={true} anchorEl={popperAnchor} placement="bottom-start" transition sx={{zIndex: 1200}} modifiers={[{name: 'offset', options: {offset: [0, 8]}}]}>
//                 {({ TransitionProps }) => (
//                     <Grow {...TransitionProps} style={{ transformOrigin: 'top left' }}>
//                         <Paper ref={popperPaperRef} sx={{ width: popperAnchor?.offsetWidth, borderRadius: '12px', backgroundColor: '#FFFFFF', boxShadow: '0 4px 20px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
//                             <ClickAwayListener onClickAway={handlePopperClose}>
//                                 <Box>
//                                     <Box sx={{ p: 2, borderBottom: '1px solid #f0f0f0' }}>
//                                         <TextField
//                                             autoFocus placeholder="Search by region" value={regionSearchTerm}
//                                             onChange={(e) => setRegionSearchTerm(e.target.value)}
//                                             onKeyDown={(e) => e.stopPropagation()} fullWidth variant="outlined"
//                                             InputProps={{
//                                                 startAdornment: (
//                                                     <InputAdornment position="start" sx={{ ml: -0.5 }}>
//                                                     <img src="/icons/search_icon.svg" alt="Search" style={{ width: '16px', height: '16px' }} />
//                                                     </InputAdornment>
//                                                 ),
//                                                 sx: { height: '32px', borderRadius: '10px', backgroundColor: 'rgba(0, 0, 0, 0.04)', '& .MuiOutlinedInput-notchedOutline': { border: 'none' }, '& .MuiInputBase-input::placeholder': { fontSize: '14px', fontWeight: 300 } },
//                                             }}
//                                         />
//                                     </Box>
//                                     <Box sx={{ maxHeight: '200px', overflowY: 'auto' }}>
//                                         {filteredActive.length > 0 && (
//                                             <>
//                                                 <ListSubheader sx={{ bgcolor: '#F9FAFB', lineHeight: '32px', fontSize: '13px', fontWeight: 600 }}>Active Regions</ListSubheader>
//                                                 <FormControlLabel
//                                                     sx={{ width: '100%', m: 0, px: 2, py: 0.5 }}
//                                                     label={<Typography sx={{ fontSize: '13px', fontWeight: 500 }}>All</Typography>}
//                                                     control={<Checkbox size="small" checked={isAllActiveSelected} indeterminate={isSomeActiveSelected} onChange={(e) => handleSelectAll(filteredActive, e.target.checked)} sx={checkboxStyle}/>}
//                                                 />
//                                                 {filteredActive.map(renderRegionOption)}
//                                             </>
//                                         )}
//                                         {filteredAvailable.length > 0 && (
//                                             <>
//                                                 <ListSubheader sx={{ bgcolor: '#F9FAFB', lineHeight: '32px', fontSize: '13px', fontWeight: 600 }}>Available Regions</ListSubheader>
//                                                 <FormControlLabel
//                                                     sx={{ width: '100%', m: 0, px: 2, py: 0.5 }}
//                                                     label={<Typography sx={{ fontSize: '13px', fontWeight: 500 }}>All</Typography>}
//                                                     control={<Checkbox size="small" checked={isAllAvailableSelected} indeterminate={isSomeAvailableSelected} onChange={(e) => handleSelectAll(filteredAvailable, e.target.checked)} sx={checkboxStyle}/>}
//                                                 />
//                                                 {filteredAvailable.map(renderRegionOption)}
//                                             </>
//                                         )}
//                                     </Box>
//                                     <Divider />
//                                     <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', p: 1 }}>
//                                         <Button onClick={() => setTempSelections([])} sx={{ textTransform: 'none', color: '#999999' }}>Clear</Button>
//                                         <Button onClick={handleApply} sx={{ textTransform: 'none', color: '#0064FF' }}>Apply</Button>
//                                     </Box>
//                                 </Box>
//                             </ClickAwayListener>
//                         </Paper>
//                     </Grow>
//                 )}
//             </Popper>
//         );
//     }

//       return null;
//     };

//     return (
//         <Tooltip title={disabled ? "Please complete Budget Details before configuring scope." : ""}>
//             <Box sx={{ p: { xs: 1, sm: 2 }, bgcolor: '#FFFFFF', borderRadius: '10px', border: '1px solid #F3F3F3', position: 'relative' }}>
//                 {disabled && (
//                   <Box sx={{
//                       position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
//                       backgroundColor: 'rgba(255, 255, 255, 0.7)', zIndex: 1, cursor: 'not-allowed'
//                   }} />
//               )}
//             <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
//               <img src="/icons/Scope-Config.svg" alt="Scope Configuration" style={{ marginRight: '12px', width: '28px', height: '28px' }} />
//               <Typography variant="h6" sx={{ fontWeight: 500, fontSize: '14px', color: '#333333', fontFamily: 'Inter' }}>Scope Configuration</Typography>
//             </Box>
//             <Divider sx={{ borderColor: '#F3F3F3', mx: -3 }} />

//             <Box sx={{ display: 'flex', gap: 2, mt: 2, px: 1, flexWrap: 'wrap' }}>
//               <Box sx={providerBoxStyle(provider === 'GCP', false)} onClick={() => handleProviderChange('GCP')}>
//                 <Box sx={{ display: 'flex', alignItems: 'center' }}><img src="/icons/google-cloud-icon-2048x1646-7admxejz.svg" alt="GCP" style={{ marginRight: 8, width: 20, height: 20 }} /><Typography variant="body2" sx={{ fontFamily: 'Inter', fontSize: '13px', fontWeight: 400 }}>Google Cloud Platform</Typography></Box>
//                 <Radio checked={provider === 'GCP'} size="small" sx={{ p: 0, transform: 'scale(0.85)', '&.Mui-checked': { color: '#0064FF' } }}/>
//               </Box>
//               <Box sx={providerBoxStyle(provider === 'LANGFUSE', false)} onClick={() => handleProviderChange('LANGFUSE')}>
//                 <Box sx={{ display: 'flex', alignItems: 'center' }}><img src="/icons/langfuse-icon.png" alt="LANGFUSE" style={{ marginRight: 8, width: 20, height: 20 }}/><Typography variant="body2" sx={{ fontFamily: 'Inter', fontSize: '13px', fontWeight: 400 }}>Langfuse</Typography></Box>
//                 <Radio checked={provider === 'LANGFUSE'} size="small" sx={{ p: 0, transform: 'scale(0.85)', '&.Mui-checked': { color: '#0064FF' } }}/>
//               </Box>
//               <Box sx={providerBoxStyle(false, true)}><Box sx={{ display: 'flex', alignItems: 'center' }}><img src="/icons/aws.svg" alt="AWS" style={{ marginRight: 8, width: 20, height: 20 }}/><Typography variant="body2" sx={{ fontFamily: 'Inter', fontSize: '13px', fontWeight: 400 }}>Amazon Web Services</Typography></Box><Radio checked={false} size="small" disabled={true} sx={{ p: 0, transform: 'scale(0.85)' }}/></Box>
//               <Box sx={providerBoxStyle(false, true)}><Box sx={{ display: 'flex', alignItems: 'center' }}><img src="/icons/azure2.svg" alt="Azure" style={{ marginRight: 8, width: 20, height: 20 }}/><Typography variant="body2" sx={{ fontFamily: 'Inter', fontSize: '13px', fontWeight: 400 }}>Microsoft Azure</Typography></Box><Radio checked={false} size="small" disabled={true} sx={{ p: 0, transform: 'scale(0.85)' }}/></Box>
//             </Box>
//             {errors.provider && <Typography color="error" variant="caption" sx={{ display: 'block', mt: 1, px: 1 }}>{errors.provider}</Typography>}

//             {provider === 'GCP' && (
//               <Box sx={{ width: '100%', bgcolor: '#F9FAFB', borderRadius: '10px', mt: 2, p: 2 }}>
//                 <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
//                   <Button onClick={handleAddService} disabled={services.length > 0 && services[services.length-1].serviceTypes.length === 0} sx={{ textTransform: 'none', color: '#0064FF', fontFamily: 'Inter', fontWeight: 500, fontSize: '14px' }}>
//                     + Add More
//                   </Button>
//                 </Box>

//                 {services.map((service, index) => {
//                   const isProjectDisabled = service.dataSources.length === 0 || optionsLoading;
//                   const isRegionDisabled = service.projectIds.length === 0 || optionsLoading;
//                   const isServiceDisabled = service.regions.length === 0 || optionsLoading;

//                   const dataSourceButtonText = service.dataSources.length === dataSourceOptions.length && dataSourceOptions.length > 0 ? 'All' : service.dataSources.length > 0 ? `${service.dataSources.length} selected` : 'Select data sources';
//                   const projectButtonText = service.projectIds.length === projectOptions.length && projectOptions.length > 0 ? 'All' : service.projectIds.length > 0 ? `${service.projectIds.length} selected` : 'Select projects';
//                   const allRegionsCount = activeRegions.length + availableRegions.length;
//                   const regionButtonText = service.regions.length === allRegionsCount && allRegionsCount > 0 ? 'All' : service.regions.length > 0 ? `${service.regions.length} selected` : 'Select regions';
//                   const labelButtonText = service.label.length === 1 ? '1 filter applied' : service.label.length > 0 ? `${service.label.length} filters applied` : 'Select Label';

//                   const getButtonSx = (hasSelection: boolean) => ({
//                       width: '100%', height: '36px', backgroundColor: '#FFFFFF', border: `1px solid #E0E0E0`, borderRadius: '8px',
//                       fontFamily: 'Inter', fontWeight: hasSelection ? 500 : 400, fontSize: '13px',
//                       color: hasSelection ? '#333333' : '#999999', lineHeight: '18px', letterSpacing: '0%',
//                       textTransform: 'none', justifyContent: 'space-between', '&:hover': { borderColor: '#0064FF' },
//                       '&.Mui-disabled': { backgroundColor: '#F3F3F3', border: '1px solid #D3D3D3' }
//                   });

//                   const showLabelButton = service.serviceTypes.length > 0;
//                   const isKubeSelected = service.serviceTypes.includes('Kubernetes Engine');
//                   const showFullKubeFields = isKubeSelected && activeServices.includes('Kubernetes Engine');
//                   return (
//                     <Box key={service.id}>
//                       <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: 2, alignItems: 'flex-end' }}>
//                         <Box>
//                           <Typography sx={{ fontFamily: 'Inter', fontWeight: 400, fontSize: '13px', color: optionsLoading ? '#A0A0A0' : '#333333', mb: 0.5 }}>Data Source<span style={{ color: 'red' }}>*</span></Typography>
//                           <Button ref={el => buttonRefs.current[`${service.id}-dataSources`] = el} onClick={(e) => handlePopperOpen(e, 'dataSources', service.id, service.dataSources)} endIcon={<ExpandMoreIcon />} sx={getButtonSx(service.dataSources.length > 0)} disabled={optionsLoading}>{dataSourceButtonText}</Button>
//                         </Box>

//                         <Box>
//                           <Typography sx={{ fontFamily: 'Inter', fontWeight: 400, fontSize: '13px', color: isProjectDisabled ? '#A0A0A0' : '#333333', mb: 0.5 }}>Project<span style={{ color: 'red' }}>*</span></Typography>
//                           <Button
//                             ref={el => buttonRefs.current[`${service.id}-projectIds`] = el}
//                             disabled={isProjectDisabled}
//                             onClick={(e) => handlePopperOpen(e, 'projectIds', service.id, service.projectIds)}
//                             endIcon={<ExpandMoreIcon />}
//                             sx={getButtonSx(service.projectIds.length > 0)}
//                           >
//                             {projectButtonText}
//                           </Button>
//                         </Box>

//                         <Box>
//                           <Typography sx={{ fontFamily: 'Inter', fontWeight: 400, fontSize: '13px', color: isRegionDisabled ? '#A0A0A0' : '#333333', mb: 0.5 }}>Region<span style={{ color: 'red' }}>*</span></Typography>
//                           <Button ref={el => buttonRefs.current[`${service.id}-regions`] = el} disabled={isRegionDisabled} onClick={(e) => handlePopperOpen(e, 'regions', service.id, service.regions)} endIcon={<ExpandMoreIcon />} sx={getButtonSx(service.regions.length > 0)}>{regionButtonText}</Button>
//                         </Box>

//                         <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
//                            <Box sx={{flexGrow: 1}}>
//                                <SectionedMultiSelectDropdown
//                                    ref={el => buttonRefs.current[`${service.id}-service`] = el}
//                                    label="Service"
//                                    options={{ active: activeServices, available: availableServices }}
//                                    selectedValues={service.serviceTypes}
//                                    onChange={(newValues) => handleUpdateService(service.id, 'serviceTypes', newValues)}
//                                    disabled={isServiceDisabled}
//                                />
//                            </Box>
//                           <IconButton onClick={() => handleDeleteService(service.id)} disabled={services.length <= 1} sx={{ mb: '4px' }}><img src={(services.length <= 1) ? "/icons/delete_icon_disabled.svg" : "/icons/delete_icon.svg"} alt="Delete Service" style={{ width: '17px', height: '17px' }} /></IconButton>
//                         </Box>

//                         {showLabelButton && (
//                           <Box>
//                             <Typography sx={{ fontFamily: 'Inter', fontWeight: 400, fontSize: '13px', color: isServiceDisabled ? '#A0A0A0' : '#333333', mb: 0.5 }}>Label</Typography>
//                             <Button onClick={(e) => handleLabelMenuOpen(e, service.id)} disabled={isServiceDisabled} endIcon={<ExpandMoreIcon />} variant="outlined" sx={getButtonSx(service.label.length > 0)}>{labelButtonText}</Button>
//                           </Box>
//                         )}

//                         {showFullKubeFields && (
//                           <>
//                             <Box>
//                               <MultiSelectDropdown label="Cluster" options={k8sClusters} selectedValues={service.cluster} onChange={(newValues: string[]) => handleUpdateService(service.id, 'cluster', newValues)} disabled={isServiceDisabled} width="100%" />
//                             </Box>
//                             <Box>
//                               <MultiSelectDropdown label="Namespace" options={k8sNamespaces} selectedValues={service.namespace} onChange={(newValues: string[]) => handleUpdateService(service.id, 'namespace', newValues)} disabled={isServiceDisabled} width="100%" />
//                             </Box>
//                           </>
//                         )}
//                       </Box>
//                       {service.label.length > 0 && (
//                         <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
//                           {service.label.flatMap(labelItem =>
//                             labelItem.values.map(value => (
//                               <Chip
//                                 key={`${labelItem.key}-${value}`}
//                                 label={`${labelItem.key}: ${value}`}
//                                 size="small"
//                                 onDelete={() => handleDeleteLabelChip(service.id, labelItem.key, value)}
//                                 sx={{
//                                   backgroundColor: ' #F3F8FE',
//                                   color: '#333333',
//                                   fontFamily: 'Inter',
//                                   fontSize: '12px',
//                                   fontWeight: 500,
//                                   borderRadius: '8px',
//                                   '.MuiChip-deleteIcon': {
//                                       color: '#333333',
//                                       fontSize: '16px',
//                                       '&:hover': {
//                                         color: 'red',
//                                       },
//                                   },
//                                 }}
//                               />
//                             ))
//                           )}
//                         </Box>
//                       )}
//                       {index < services.length - 1 && <Divider sx={{ my: 2, mx: -2 }} />}
//                     </Box>
//                   )
//                 })}
//               </Box>
//             )}

//             {provider === 'LANGFUSE' && (
//                 <Box sx={{ width: '100%', bgcolor: '#F9FAFB', borderRadius: '10px', mt: 2, p: 2 }}>
//                     <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
//                         <Button onClick={handleAddService} disabled={services.length > 0 && services[services.length-1].serviceTypes.length === 0} sx={{ textTransform: 'none', color: '#0064FF', fontFamily: 'Inter', fontWeight: 500, fontSize: '14px' }}>
//                             + Add More
//                         </Button>
//                     </Box>
//                     {services.map((service, index) => {
//                         const isModelDisabled = service.dataSources.length === 0 || optionsLoading;
//                         const isTraceDisabled = service.skus.length === 0 || optionsLoading;
        
//                         // Map selected data source IDs back to names for display in the dropdown
//                         const selectedDataSourceNames = service.dataSources
//                         .map(id => dataSourceOptions.find(d => d.id === id)?.name)
//                         .filter((name): name is string => !!name);
        
//                         return (
//                         <Box key={service.id}>
//                             <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: 2, alignItems: 'flex-end' }}>
//                                 <Box>
//                                     <MultiSelectDropdown 
//                                         label="Data Source" 
//                                         options={dataSourceOptions.map(d => d.name)} 
//                                         selectedValues={selectedDataSourceNames}
//                                         onChange={(newNames) => {
//                                             // Map selected names back to IDs to store in state
//                                             const newIds = newNames
//                                                 .map(name => dataSourceOptions.find(d => d.name === name)?.id)
//                                                 .filter((id): id is string => !!id);
//                                             handleUpdateService(service.id, 'dataSources', newIds);
//                                         }} 
//                                         disabled={optionsLoading} 
//                                     />
//                                 </Box>
//                                 <Box>
//                                     <MultiSelectDropdown label="Model" options={skuOptions} selectedValues={service.skus} onChange={(newValues) => handleUpdateService(service.id, 'skus', newValues)} disabled={isModelDisabled} />
//                                 </Box>
//                                 <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
//                                     <Box sx={{ flexGrow: 1 }}>
//                                         <MultiSelectDropdown label="Trace" options={activeServices} selectedValues={service.serviceTypes} onChange={(newValues) => handleUpdateService(service.id, 'serviceTypes', newValues)} disabled={isTraceDisabled} />
//                                     </Box>
//                                     <IconButton onClick={() => handleDeleteService(service.id)} disabled={services.length <= 1} sx={{ mb: '4px' }}>
//                                         <img src={(services.length <= 1) ? "/icons/delete_icon_disabled.svg" : "/icons/delete_icon.svg"} alt="Delete Service" style={{ width: '17px', height: '17px' }} />
//                                     </IconButton>
//                                 </Box>
//                             </Box>
//                             {index < services.length - 1 && <Divider sx={{ my: 2, mx: -2 }} />}
//                         </Box>
//                         )
//                     })}
//                 </Box>
//             )}

//             {renderMultiSelectPopper()}

//             <LabelFilterMenu
//                 open={openPopper?.type === 'label'}
//                 anchorEl={labelMenuAnchor}
//                 onClose={handleLabelMenuClose}
//                 onApply={handleLabelApply}
//                 appliedFilters={activeServiceForLabel?.label || []}
//                 labelKeys={labelKeys}
//                 labelValues={labelValuesData}
//                 onFetchLabelValues={handleFetchLabelValues}
//                 loadingLabelKey={loadingLabelKey}
//             />
//             </Box>
//         </Tooltip>
//     );
// };

// export default ScopeConfig;




// import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
// import { useLazyQuery, gql } from '@apollo/client';
// import { useOutletContext } from 'react-router-dom';
// import {
//   Box, Typography, Radio, Divider, Button, Popper, Grow, Paper, Checkbox, FormControlLabel,
//   ClickAwayListener, IconButton, ListSubheader, TextField, InputAdornment, Tooltip, Chip
// } from '@mui/material';
// import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
// import ExpandLessIcon from '@mui/icons-material/ExpandLess';
// import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
// import LabelFilterMenu, { LabelFilterPair } from './LabelFilterMenu';

// // --- MODIFIED GraphQL Query Definition ---
// const GET_BUDGET_FILTER_OPTIONS = gql`
//   query GetBudgetFilterOptions($input: BudgetFilterOptionsInput!) {
//     getBudgetFilterOptions(input: $input) {
//       dataSources { id, name, providerType, isSyncing }
//       activeRegions, availableRegions, activeServices, availableServices, projectIds
//       labelKeys, k8sClusters, k8sNamespaces, skus
//     }
//   }
// `;

// const GET_LABEL_VALUES = gql`
//   query GetBudgetLabelValues($input: BudgetLabelValuesInput!) {
//     getBudgetLabelValues(input: $input) {
//       values
//     }
//   }
// `;

// // --- MODIFIED Interface ---
// export interface ServiceState {
//   id: string;
//   dataSources: string[];
//   projectIds: string[];
//   regions: string[];
//   serviceTypes: string[];
//   label: LabelFilterPair[];
//   cluster: string[];
//   namespace: string[];
//   deployment: string[];
//   skus: string[]; // Added for LANGFUSE models
// }

// interface ScopeConfigProps {
//     provider: string;
//     setProvider: (provider: string) => void;
//     services: ServiceState[];
//     setServices: React.Dispatch<React.SetStateAction<ServiceState[]>>;
//     getInitialService: () => Omit<ServiceState, 'id' | 'serviceTypes'> & { serviceTypes: string[] };
//     errors: { [key: string]: string };
//     disabled?: boolean;
//     period: 'Monthly' | 'Quarterly' | 'Yearly';
// }

// interface AppOutletContext {
//   currentTenant?: { id: string; name: string; };
// }

// interface DataSourceOption {
//   id: string;
//   name: string;
//   providerType: string;
//   isSyncing: boolean;
// }

// // SectionedMultiSelectDropdown remains unchanged
// const SectionedMultiSelectDropdown = React.forwardRef<
//   HTMLButtonElement,
//   {
//     label: string;
//     options: { active: string[]; available: string[] };
//     selectedValues: string[];
//     onChange: (selected: string[]) => void;
//     disabled?: boolean;
//   }
// >(({ label, options, selectedValues, onChange, disabled = false }, ref) => {
//   const [isOpen, setIsOpen] = useState(false);
//   const [searchTerm, setSearchTerm] = useState('');
//   const anchorRef = useRef<HTMLButtonElement>(null);

//   const handleRef = useCallback((node: HTMLButtonElement) => {
//     (anchorRef as React.MutableRefObject<HTMLButtonElement | null>).current = node;
//     if (typeof ref === 'function') {
//       ref(node);
//     } else if (ref) {
//       (ref as React.MutableRefObject<HTMLButtonElement | null>).current = node;
//     }
//   }, [ref]);

//   const handleOpen = () => setIsOpen(true);
//   const handleClose = () => {
//     setIsOpen(false);
//     setSearchTerm('');
//   };

//   const filteredActive = useMemo(() => options.active.filter(opt => opt.toLowerCase().includes(searchTerm.toLowerCase())), [options.active, searchTerm]);
//   const filteredAvailable = useMemo(() => options.available.filter(opt => opt.toLowerCase().includes(searchTerm.toLowerCase())), [options.available, searchTerm]);
  
//   const isKubeSelected = useMemo(() => selectedValues.includes('Kubernetes Engine'), [selectedValues]);

//   const isAnyActiveSelected = useMemo(() => options.active.some(opt => selectedValues.includes(opt) && opt !== 'Kubernetes Engine'), [options.active, selectedValues]);
//   const isAnyAvailableSelected = useMemo(() => options.available.some(opt => selectedValues.includes(opt) && opt !== 'Kubernetes Engine'), [options.available, selectedValues]);

//   const nonKubeFilteredActive = useMemo(() => filteredActive.filter(s => s !== 'Kubernetes Engine'), [filteredActive]);
//   const nonKubeFilteredAvailable = useMemo(() => filteredAvailable.filter(s => s !== 'Kubernetes Engine'), [filteredAvailable]);

//   const isAllActiveSelected = nonKubeFilteredActive.length > 0 && nonKubeFilteredActive.every(s => selectedValues.includes(s));
//   const isSomeActiveSelected = nonKubeFilteredActive.some(s => selectedValues.includes(s)) && !isAllActiveSelected;

//   const isAllAvailableSelected = nonKubeFilteredAvailable.length > 0 && nonKubeFilteredAvailable.every(s => selectedValues.includes(s));
//   const isSomeAvailableSelected = nonKubeFilteredAvailable.some(s => selectedValues.includes(s)) && !isAllAvailableSelected;

//   const handleToggle = (value: string) => {
//     if (value === 'Kubernetes Engine') {
//       onChange(isKubeSelected ? [] : ['Kubernetes Engine']);
//       return;
//     }
//     if (isKubeSelected) return;

//     const isTogglingActive = options.active.includes(value);

//     if (isTogglingActive && isAnyAvailableSelected) {
//       onChange([value]);
//       return;
//     }
//     if (!isTogglingActive && isAnyActiveSelected) {
//       onChange([value]);
//       return;
//     }

//     const newSelected = selectedValues.includes(value)
//       ? selectedValues.filter(v => v !== value)
//       : [...selectedValues, value];
//     onChange(newSelected);
//   };

//   const handleSelectAll = (itemsToToggle: string[], isSelecting: boolean) => {
//     if (isSelecting) {
//       const nonKubeItems = itemsToToggle.filter(v => v !== 'Kubernetes Engine');
//       onChange(nonKubeItems);
//     } else {
//       onChange([]);
//     }
//   };

//   const allOptions = [...options.active, ...options.available];
  
//   const buttonText =
//     selectedValues.length === 0 ? `Select ${label.toLowerCase()}`
//     : selectedValues.length === allOptions.length && allOptions.length > 0 ? 'All selected'
//     : `${selectedValues.length} selected`;

//     const renderOption = (option: string) => {
//         const isThisOptionActive = options.active.includes(option);
//         const isThisOptionAvailable = options.available.includes(option);
        
//         const isDisabled = 
//             (isKubeSelected && option !== 'Kubernetes Engine') || 
//             ((isAnyActiveSelected || isAnyAvailableSelected) && option === 'Kubernetes Engine') ||
//             (isThisOptionAvailable && isAnyActiveSelected) ||
//             (isThisOptionActive && isAnyAvailableSelected);

//         return (
//           <FormControlLabel
//               key={option}
//               sx={{ width: '100%', m: 0, px: 2, py: 0.5 }}
//               control={
//                   <Checkbox
//                       size="small"
//                       checked={selectedValues.includes(option)}
//                       onChange={() => handleToggle(option)}
//                       disabled={isDisabled}
//                       sx={{ '&.Mui-checked': { color: '#0064FF' } }}
//                   />
//               }
//               label={
//                 <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
//                   <Typography sx={{ fontSize: '13px', color: isDisabled ? '#B0B0B0' : selectedValues.includes(option) ? '#333333' : '#666666' }}>
//                     {option}
//                   </Typography>
//                   {option === 'Kubernetes Engine' && isDisabled && (
//                     <Tooltip
//                       title="As you have selected other services, Kubernetes Engine is disabled. To select it, please clear other services."
//                       arrow
//                       componentsProps={{
//                         tooltip: {
//                           sx: { backgroundColor: '#FFFFFF', color: '#000000', border: '1px solid #E0E0E0', fontSize: '12px', },
//                         },
//                         arrow: { sx: { color: '#FFFFFF', }, },
//                       }}
//                     >
//                       <InfoOutlinedIcon sx={{ color: '#0064FF', ml: 1, fontSize: '16px', }} />
//                     </Tooltip>
//                   )}
//                 </Box>
//               }
//           />
//         );
//       };

//   return (
//     <Box sx={{ width: '100%' }}>
//       <Typography sx={{ fontFamily: 'Inter', fontWeight: 400, fontSize: '13px', color: disabled ? '#A0A0A0' : '#333333', mb: 0.5 }}>
//         {label}<span style={{ color: 'red' }}>*</span>
//       </Typography>
//       <Button
//         ref={handleRef} variant="outlined" disabled={disabled} onClick={handleOpen}
//         endIcon={isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
//         sx={{
//           width: '100%', height: '36px', backgroundColor: disabled ? '#F3F3F3' : '#FFFFFF',
//           border: `1px solid ${disabled ? '#D3D3D3' : '#E0E0E0'}`, borderRadius: '8px',
//           fontFamily: 'Inter', fontWeight: selectedValues.length > 0 ? 500 : 400, fontSize: '13px',
//           color: selectedValues.length > 0 ? '#333333' : '#999999', lineHeight: '18px',
//           letterSpacing: '0%', textTransform: 'none', justifyContent: 'space-between',
//           '&:hover': { borderColor: disabled ? '#D3D3D3' : '#0064FF' },
//         }}
//       >
//         {buttonText}
//       </Button>
//       <Popper
//         open={isOpen} anchorEl={anchorRef.current} placement="bottom-start"
//         transition sx={{ zIndex: 1300, width: anchorRef.current?.offsetWidth }}
//       >
//         {({ TransitionProps }) => (
//           <ClickAwayListener onClickAway={handleClose}>
//             <Grow {...TransitionProps} style={{ transformOrigin: 'top left' }}>
//               <Paper sx={{ borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.1)', maxHeight: '300px', display: 'flex', flexDirection: 'column' }}>
//                 <Box sx={{ p: 2, borderBottom: '1px solid #f0f0f0' }}>
//                     <TextField
//                         autoFocus placeholder="Search by service name.." value={searchTerm}
//                         onChange={(e) => setSearchTerm(e.target.value)}
//                         onKeyDown={(e) => e.stopPropagation()} fullWidth variant="outlined"
//                         InputProps={{
//                             startAdornment: (
//                                 <InputAdornment position="start" sx={{ ml: -0.5 }}>
//                                 <img src="/icons/search_icon.svg" alt="Search" style={{ width: '16px', height: '16px' }} />
//                                 </InputAdornment>
//                             ),
//                             sx: { height: '32px', borderRadius: '10px', backgroundColor: 'rgba(0, 0, 0, 0.04)', '& .MuiOutlinedInput-notchedOutline': { border: 'none' }, '& .MuiInputBase-input::placeholder': { fontSize: '14px', fontWeight: 300 } },
//                         }}
//                     />
//                 </Box>
//                 <Box sx={{ overflowY: 'auto' }}>
//                     {filteredActive.length === 0 && filteredAvailable.length === 0 ? (
//                         <Typography sx={{ p: 2, color: '#999', fontSize: '13px', textAlign: 'center' }}>No results found</Typography>
//                     ) : (
//                         <>
//                             {filteredActive.length > 0 && (
//                                 <>
//                                     <ListSubheader sx={{ bgcolor: '#F9FAFB', lineHeight: '32px', fontSize: '13px', fontWeight: 600 }}>Active Services</ListSubheader>
//                                     <FormControlLabel
//                                         sx={{ width: '100%', m: 0, px: 2, py: 0.5 }}
//                                         label={<Typography sx={{ fontSize: '13px' }}>All</Typography>}
//                                         control={<Checkbox size="small" checked={isAllActiveSelected} indeterminate={isSomeActiveSelected} onChange={(e) => handleSelectAll(filteredActive, e.target.checked)} disabled={isKubeSelected || isAnyAvailableSelected} sx={{'&.Mui-checked, &.MuiCheckbox-indeterminate': { color: '#0064FF' }}} />}
//                                     />
//                                     {filteredActive.map(renderOption)}
//                                 </>
//                             )}
//                             {filteredAvailable.length > 0 && (
//                                 <>
//                                     <ListSubheader sx={{ bgcolor: '#F9FAFB', lineHeight: '32px', fontSize: '13px', fontWeight: 600 }}>Available Services</ListSubheader>
//                                     <FormControlLabel
//                                         sx={{ width: '100%', m: 0, px: 2, py: 0.5 }}
//                                         label={<Typography sx={{ fontSize: '13px' }}>All</Typography>}
//                                         control={<Checkbox size="small" checked={isAllAvailableSelected} indeterminate={isSomeAvailableSelected} onChange={(e) => handleSelectAll(filteredAvailable, e.target.checked)} disabled={isKubeSelected || isAnyActiveSelected} sx={{'&.Mui-checked, &.MuiCheckbox-indeterminate': { color: '#0064FF' }}} />}
//                                     />
//                                     {filteredAvailable.map(renderOption)}
//                                 </>
//                             )}
//                         </>
//                     )}
//                 </Box>
//                 <Divider />
//                 <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1, flexShrink: 0 }}>
//                   <Button onClick={() => onChange([])} sx={{ textTransform: 'none', color: '#999999' }}>Clear</Button>
//                   <Button onClick={handleClose} sx={{ textTransform: 'none', color: '#0064FF' }}>Apply</Button>
//                 </Box>
//               </Paper>
//             </Grow>
//           </ClickAwayListener>
//         )}
//       </Popper>
//     </Box>
//   );
// });

// // MultiSelectDropdown remains unchanged
// const MultiSelectDropdown: React.FC<{
//   label: string;
//   options: string[];
//   selectedValues: string[];
//   onChange: (selected: string[]) => void;
//   disabled?: boolean;
//   width?: string | number;
// }> = ({ label, options, selectedValues, onChange, disabled = false, width = '100%' }) => {
//   const [isOpen, setIsOpen] = useState(false);
//   const anchorRef = useRef<HTMLButtonElement>(null);

//   const handleOpen = () => setIsOpen(true);
//   const handleClose = () => setIsOpen(false);

//   const handleToggle = (value: string) => {
//     const newSelected = selectedValues.includes(value)
//       ? selectedValues.filter((v) => v !== value)
//       : [...selectedValues, value];
//     onChange(newSelected);
//   };

//   const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
//     onChange(event.target.checked ? options : []);
//   };

//   const isAllSelected = options.length > 0 && selectedValues.length === options.length;
//   const buttonText =
//     selectedValues.length === 0 ? `Select ${label.toLowerCase()}`
//     : selectedValues.length === options.length && options.length > 0 ? 'All selected'
//     : `${selectedValues.length} selected`;

//   return (
//     <Box sx={{ width }}>
//       <Typography sx={{ fontFamily: 'Inter', fontWeight: 400, fontSize: '13px', color: disabled ? '#A0A0A0' : '#333333', mb: 0.5 }}>
//         {label}<span style={{ color: 'red' }}>*</span>
//       </Typography>
//       <Button
//         ref={anchorRef}
//         variant="outlined"
//         disabled={disabled}
//         onClick={handleOpen}
//         endIcon={isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
//         sx={{
//           width: '100%', height: '36px', backgroundColor: disabled ? '#F3F3F3' : '#FFFFFF',
//           border: `1px solid ${disabled ? '#D3D3D3' : '#E0E0E0'}`, borderRadius: '8px',
//           fontFamily: 'Inter',
//           fontWeight: selectedValues.length > 0 ? 500 : 400,
//           fontSize: '13px',
//           color: selectedValues.length > 0 ? '#333333' : '#999999',
//           lineHeight: '18px',
//           letterSpacing: '0%',
//           textTransform: 'none',
//           justifyContent: 'space-between',
//           '&:hover': { borderColor: disabled ? '#D3D3D3' : '#0064FF' },
//         }}
//       >
//         {buttonText}
//       </Button>
//       <Popper
//         open={isOpen}
//         anchorEl={anchorRef.current}
//         placement="bottom-start"
//         transition
//         sx={{ zIndex: 1300, width: anchorRef.current?.offsetWidth }}
//       >
//         {({ TransitionProps }) => (
//           <ClickAwayListener onClickAway={handleClose}>
//             <Grow {...TransitionProps} style={{ transformOrigin: 'top left' }}>
//               <Paper sx={{ borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
//                 <FormControlLabel
//                   label={<Typography sx={{ fontSize: '13px', color: isAllSelected ? '#333333' : '#666666' }}>All</Typography>}
//                   sx={{ width: '100%', m: 0, px: 2, py: 0.5 }}
//                   control={
//                     <Checkbox size="small" checked={isAllSelected}
//                       indeterminate={selectedValues.length > 0 && !isAllSelected}
//                       onChange={handleSelectAll}
//                       sx={{ '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: '#0064FF' } }}
//                     />
//                   }
//                 />
//                 <Box sx={{ maxHeight: '200px', overflowY: 'auto' }}>
//                   {options.map((option) => (
//                     <FormControlLabel
//                       key={option} sx={{ width: '100%', m: 0, px: 2, py: 0.5 }}
//                       control={
//                         <Checkbox size="small" checked={selectedValues.includes(option)}
//                           onChange={() => handleToggle(option)}
//                           sx={{ '&.Mui-checked': { color: '#0064FF' } }}
//                         />
//                       }
//                       label={<Typography sx={{ fontSize: '13px', color: selectedValues.includes(option) ? '#333333' : '#666666' }}>{option}</Typography>}
//                     />
//                   ))}
//                 </Box>
//                 <Divider />
//                 <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
//                   <Button onClick={() => onChange([])} sx={{ textTransform: 'none', color: '#999999' }}>Clear</Button>
//                   <Button onClick={handleClose} sx={{ textTransform: 'none', color: '#0064FF' }}>Apply</Button>
//                 </Box>
//               </Paper>
//             </Grow>
//           </ClickAwayListener>
//         )}
//       </Popper>
//     </Box>
//   );
// };


// // --- Main ScopeConfig Component ---
// const ScopeConfig: React.FC<ScopeConfigProps> = ({
//   provider, setProvider, services, setServices, getInitialService,
//   errors, disabled = false, period,
// }) => {
//     const { currentTenant } = useOutletContext<AppOutletContext>();
//     const [openPopper, setOpenPopper] = useState<{ type: 'dataSources' | 'projectIds' | 'regions' | 'label', serviceId: string } | null>(null);
//     const [tempSelections, setTempSelections] = useState<string[]>([]);
//     const [regionSearchTerm, setRegionSearchTerm] = useState('');

//     const buttonRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({});
//     const [popperAnchor, setPopperAnchor] = useState<HTMLButtonElement | null>(null);
//     const [labelMenuAnchor, setLabelMenuAnchor] = useState<HTMLElement | null>(null);
//     const popperPaperRef = useRef(null);
//     const hasOpenedAutomatically = useRef(false);

//     const [dataSourceOptions, setDataSourceOptions] = useState<DataSourceOption[]>([]);
//     const [projectOptions, setProjectOptions] = useState<string[]>([]);
//     const [activeRegions, setActiveRegions] = useState<string[]>([]);
//     const [availableRegions, setAvailableRegions] = useState<string[]>([]);
//     const [activeServices, setActiveServices] = useState<string[]>([]);
//     const [availableServices, setAvailableServices] = useState<string[]>([]);
//     const [labelKeys, setLabelKeys] = useState<string[]>([]);
//     const [k8sClusters, setK8sClusters] = useState<string[]>([]);
//     const [k8sNamespaces, setK8sNamespaces] = useState<string[]>([]);
//     const [skuOptions, setSkuOptions] = useState<string[]>([]); // For LANGFUSE Models
//     const [labelValuesData, setLabelValuesData] = useState<{ [key: string]: string[] }>({});
//     const [loadingLabelKey, setLoadingLabelKey] = useState<string | null>(null);

//     const dataSourceDependency = useMemo(() => {
//         const sortedIds = Array.from(new Set(services.flatMap(s => s.dataSources))).sort();
//         return JSON.stringify(sortedIds);
//     }, [services]);

//     const [getOptions, { loading: optionsLoading }] = useLazyQuery(GET_BUDGET_FILTER_OPTIONS, {
//         onCompleted: (data) => {
//             if (data?.getBudgetFilterOptions) {
//                 const {
//                     dataSources, projectIds, activeRegions, availableRegions,
//                     activeServices, availableServices, labelKeys, k8sClusters,
//                     k8sNamespaces, skus
//                 } = data.getBudgetFilterOptions;

//                 setDataSourceOptions(dataSources || []);
//                 setProjectOptions(projectIds || []);
//                 setActiveRegions(activeRegions || []);
//                 setAvailableRegions(availableRegions || []);
//                 setActiveServices(activeServices || []);
//                 setAvailableServices(availableServices || []);
//                 setLabelKeys(labelKeys || []);
//                 setK8sClusters(k8sClusters || []);
//                 setK8sNamespaces(k8sNamespaces || []);
//                 setSkuOptions(skus || []);
//             }
//         }
//     });

//     const [getLabelValues] = useLazyQuery(GET_LABEL_VALUES, {
//         onCompleted: (data) => {
//             if (loadingLabelKey && data?.getBudgetLabelValues?.values) {
//                 setLabelValuesData(prev => ({ ...prev, [loadingLabelKey]: data.getBudgetLabelValues.values }));
//             }
//             setLoadingLabelKey(null);
//         }
//     });

//     useEffect(() => {
//         if (!provider || !currentTenant?.id || !period) return;
//         const allSelectedDataSources = JSON.parse(dataSourceDependency);
//         getOptions({
//             variables: {
//                 input: {
//                     tenantId: currentTenant.id,
//                     provider,
//                     period: period.toUpperCase(),
//                     dataSourceIds: allSelectedDataSources,
//                 }
//             }
//         });
//     }, [provider, currentTenant, period, dataSourceDependency, getOptions]);

//     useEffect(() => {
//         if (provider === 'GCP' && services.length === 1 && services[0].dataSources.length === 0 &&
//             !disabled && !optionsLoading && !hasOpenedAutomatically.current && dataSourceOptions.length > 0) {
//             const firstServiceId = services[0].id;
//             const dataSourceButton = buttonRefs.current[`${firstServiceId}-dataSources`];
//             if (dataSourceButton) {
//                 setPopperAnchor(dataSourceButton);
//                 setTempSelections(services[0].dataSources);
//                 setOpenPopper({ type: 'dataSources', serviceId: firstServiceId });
//                 hasOpenedAutomatically.current = true;
//             }
//         }
//         if (provider !== 'GCP' || services.length === 0) {
//             hasOpenedAutomatically.current = false;
//         }
//     }, [provider, services, disabled, optionsLoading, dataSourceOptions]);

//     const providerBoxStyle = (isSelected: boolean, isDisabled: boolean) => ({
//       p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
//       backgroundColor: isSelected ? '#F2F7FF' : (isDisabled ? '#F3F3F3' : '#FFFFFF'),
//       border: `1px solid ${isSelected ? '#0064FF' : '#E0E0E0'}`,
//       borderRadius: '8px', cursor: isDisabled ? 'not-allowed' : 'pointer', flex: 1, height: '36px',
//       opacity: isDisabled ? 0.6 : 1,
//       '&:hover': { borderColor: isDisabled ? '#E0E0E0' : '#0064FF' }
//     });
    
//     const handleProviderChange = (newProvider: string) => {
//         if (disabled || provider === newProvider) return;
//         setProvider(newProvider);
//         // Reset services when provider changes
//         setServices([{ id: `service_${Date.now()}`, ...getInitialService() }]);
//     };

//     const handlePopperOpen = (event: React.MouseEvent<HTMLButtonElement>, type: 'dataSources' | 'projectIds' | 'regions', serviceId: string, currentValues: string[]) => {
//       setPopperAnchor(event.currentTarget);
//       setTempSelections(currentValues);
//       setOpenPopper({ type, serviceId });
//     };

//     const handlePopperClose = () => {
//       setOpenPopper(null);
//       setRegionSearchTerm('');
//       setPopperAnchor(null);
//     };

//     const handleUpdateService = useCallback((serviceId: string, field: keyof Omit<ServiceState, 'id'>, value: any) => {
//         setServices(prev => prev.map(s => {
//             if (s.id === serviceId) {
//                 const updatedService = { ...s, [field]: value };
//                 // GCP specific reset logic
//                 if (provider === 'GCP') {
//                   if (field === 'dataSources') {
//                       updatedService.projectIds = []; updatedService.regions = []; updatedService.serviceTypes = [];
//                       updatedService.label = []; updatedService.cluster = []; updatedService.namespace = [];
//                   }
//                   if (field === 'projectIds') {
//                       updatedService.regions = []; updatedService.serviceTypes = []; updatedService.label = [];
//                       updatedService.cluster = []; updatedService.namespace = [];
//                   }
//                   if (field === 'regions') {
//                       updatedService.serviceTypes = []; updatedService.label = [];
//                       updatedService.cluster = []; updatedService.namespace = [];
//                   }
//                   if (field === 'serviceTypes' && !value.includes('Kubernetes Engine')) {
//                      updatedService.cluster = []; updatedService.namespace = [];
//                   }
//                 }
//                 // LANGFUSE specific reset logic
//                 if (provider === 'LANGFUSE') {
//                     if (field === 'dataSources') {
//                         updatedService.skus = [];
//                         updatedService.serviceTypes = []; // Traces
//                     }
//                 }
//                 return updatedService;
//             }
//             return s;
//         }));
//     }, [setServices, provider]);

//     const handleApply = () => {
//         if (!openPopper) return;
//         const { serviceId, type } = openPopper;
//         handleUpdateService(serviceId, type as any, tempSelections);
//         handlePopperClose();
//     };

//     const handleFetchLabelValues = useCallback((key: string) => {
//         if (!key || labelValuesData[key] || loadingLabelKey) return;
//         setLoadingLabelKey(key);
//         const allDataSourceIds = Array.from(new Set(services.flatMap(s => s.dataSources)));
//         getLabelValues({
//             variables: {
//                 input: {
//                     tenantId: currentTenant?.id,
//                     provider,
//                     labelKey: key,
//                     period: period.toUpperCase(),
//                     dataSourceIds: allDataSourceIds,
//                 }
//             }
//         });
//     }, [getLabelValues, provider, currentTenant, services, labelValuesData, loadingLabelKey, period]);

//     const handleDeleteLabelChip = (serviceId: string, keyToDelete: string, valueToDelete: string) => {
//         setServices(prevServices =>
//           prevServices.map(service => {
//             if (service.id === serviceId) {
//               const updatedLabels = service.label
//                 .map(label => {
//                   if (label.key === keyToDelete) {
//                     const newValues = label.values.filter(v => v !== valueToDelete);
//                     if (newValues.length > 0) {
//                       return { ...label, values: newValues };
//                     }
//                     return null;
//                   }
//                   return label;
//                 })
//                 .filter((label): label is LabelFilterPair => label !== null);

//               return { ...service, label: updatedLabels };
//             }
//             return service;
//           })
//         );
//       };

//       const handleAddService = useCallback(() => {
//         const newService = { id: `service_${Date.now()}`, ...getInitialService() };
//         setServices(prev => [...prev, newService]);
//       }, [getInitialService, setServices]);

//       const handleDeleteService = useCallback((idToDelete: string) => {
//         setServices(p => p.filter(s => s.id !== idToDelete));
//       }, [setServices]);

//       const handleLabelMenuOpen = (event: React.MouseEvent<HTMLElement>, serviceId: string) => {
//           setLabelMenuAnchor(event.currentTarget);
//           setOpenPopper({ type: 'label', serviceId });
//       };

//       const handleLabelMenuClose = () => {
//         setOpenPopper(null);
//         setLabelMenuAnchor(null);
//       };

//       const handleLabelApply = (newFilters: LabelFilterPair[]) => {
//           if (openPopper?.type === 'label') {
//               handleUpdateService(openPopper.serviceId, 'label', newFilters);
//           }
//           handleLabelMenuClose();
//       };

//       const activeServiceForLabel = services.find(s => s.id === openPopper?.serviceId);

//     const renderMultiSelectPopper = () => {
//       if (!openPopper || !popperAnchor) return null;
//       const checkboxStyle = { '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: '#0064FF' } };

//       if (openPopper.type === 'dataSources') {
//         const options = dataSourceOptions.map(s => s.id);
//         const optionLabels = dataSourceOptions;
//         const isAllSelected = tempSelections.length === options.length && options.length > 0;
//         const isSomeSelected = tempSelections.length > 0 && !isAllSelected;
//         const handleSelectAll = () => setTempSelections(isAllSelected ? [] : options);
//         return (
//           <Popper open={true} anchorEl={popperAnchor} placement="bottom-start" transition sx={{zIndex: 1200}} modifiers={[{name: 'offset', options: {offset: [0, 8]}}]}>
//             {({ TransitionProps }) => (
//                 <Grow {...TransitionProps} style={{ transformOrigin: 'top left' }}>
//                     <Paper ref={popperPaperRef} sx={{ width: popperAnchor?.offsetWidth, borderRadius: '12px', backgroundColor: '#FFFFFF', boxShadow: '0 4px 20px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
//                         <ClickAwayListener onClickAway={handlePopperClose}>
//                             <Box>
//                                 <FormControlLabel
//                                     label={<Typography sx={{ fontSize: '13px', color: isAllSelected ? '#333333' : '#666666' }}>All</Typography>}
//                                     sx={{width: '100%', m:0, px: 2, py: 0.5}}
//                                     control={ <Checkbox size="small" checked={isAllSelected} indeterminate={isSomeSelected} onChange={handleSelectAll} sx={checkboxStyle}/> }
//                                 />
//                                 <Box sx={{ maxHeight: '140px', overflow: 'auto' }}>
//                                   {optionLabels.map(option => (
//                                     <Tooltip
//                                       key={option.id}
//                                       title={option.isSyncing ? "This data source is currently syncing and cannot be selected." : ""}
//                                       placement="right"
//                                       arrow
//                                     >
//                                       <div>
//                                         <FormControlLabel
//                                             sx={{width: '100%', m:0, px:2, py:0.5}}
//                                             disabled={option.isSyncing}
//                                             control={
//                                               <Checkbox
//                                                 size="small"
//                                                 checked={tempSelections.includes(option.id)}
//                                                 onChange={() => setTempSelections(p => p.includes(option.id) ? p.filter(i => i !== option.id) : [...p, option.id])}
//                                                 sx={checkboxStyle}
//                                               />
//                                             }
//                                             label={
//                                               <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
//                                                 <Typography sx={{ fontSize: '13px', color: tempSelections.includes(option.id) ? '#333333' : '#666666' }}>
//                                                   {option.name}
//                                                 </Typography>
//                                                 {option.isSyncing && (
//                                                   <Box component="span" sx={{display: 'inline-flex', alignItems: 'center', height: '18px', px: 0.8, borderRadius: '10px', backgroundColor: 'rgba(255, 193, 7, 0.2)', color: 'rgba(180, 120, 0)', fontSize: '11px', fontWeight: 500 }}>
//                                                     Syncing
//                                                   </Box>
//                                                 )}
//                                               </Box>
//                                             }
//                                         />
//                                       </div>
//                                     </Tooltip>
//                                   ))}
//                                 </Box>
//                                 <Divider />
//                                 <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', p: 1 }}>
//                                     <Button onClick={() => setTempSelections([])} sx={{ textTransform: 'none', color: '#999999' }}>Clear</Button>
//                                     <Button onClick={handleApply} sx={{ textTransform: 'none', color: '#0064FF' }}>Apply</Button>
//                                 </Box>
//                             </Box>
//                         </ClickAwayListener>
//                     </Paper>
//                 </Grow>
//             )}
//         </Popper>
//         );
//       }

//     if (openPopper.type === 'projectIds') {
//           const options = projectOptions;
//           const isAllSelected = tempSelections.length === options.length && options.length > 0;
//           const isSomeSelected = tempSelections.length > 0 && !isAllSelected;
//           const handleSelectAll = () => setTempSelections(isAllSelected ? [] : options);
//           return (
//               <Popper open={true} anchorEl={popperAnchor} placement="bottom-start" transition sx={{zIndex: 1200}} modifiers={[{name: 'offset', options: {offset: [0, 8]}}]}>
//                   {({ TransitionProps }) => (
//                       <Grow {...TransitionProps} style={{ transformOrigin: 'top left' }}>
//                           <Paper ref={popperPaperRef} sx={{ width: popperAnchor?.offsetWidth, borderRadius: '12px', backgroundColor: '#FFFFFF', boxShadow: '0 4px 20px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
//                               <ClickAwayListener onClickAway={handlePopperClose}>
//                                   <Box>
//                                       <FormControlLabel
//                                           label={<Typography sx={{ fontSize: '13px', color: isAllSelected ? '#333333' : '#666666' }}>All</Typography>}
//                                           sx={{width: '100%', m:0, px: 2, py: 0.5}}
//                                           control={ <Checkbox size="small" checked={isAllSelected} indeterminate={isSomeSelected} onChange={handleSelectAll} sx={checkboxStyle}/> }
//                                       />
//                                       <Box sx={{ maxHeight: '140px', overflow: 'auto' }}>
//                                           {options.map(option => (
//                                               <FormControlLabel
//                                                   key={option}
//                                                   sx={{width: '100%', m:0, px:2, py:0.5}}
//                                                   control={ <Checkbox size="small" checked={tempSelections.includes(option)} onChange={() => setTempSelections(p => p.includes(option) ? p.filter(i => i !== option) : [...p, option])} sx={checkboxStyle}/> }
//                                                   label={<Typography sx={{ fontSize: '13px', color: tempSelections.includes(option) ? '#333333' : '#666666' }}>{option}</Typography>}
//                                               />
//                                           ))}
//                                       </Box>
//                                       <Divider />
//                                       <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', p: 1 }}>
//                                           <Button onClick={() => setTempSelections([])} sx={{ textTransform: 'none', color: '#999999' }}>Clear</Button>
//                                           <Button onClick={handleApply} sx={{ textTransform: 'none', color: '#0064FF' }}>Apply</Button>
//                                       </Box>
//                                   </Box>
//                               </ClickAwayListener>
//                           </Paper>
//                       </Grow>
//                   )}
//               </Popper>
//           );
//       }

//     if (openPopper.type === 'regions') {
//         const filteredActive = activeRegions.filter(r => r.toLowerCase().includes(regionSearchTerm.toLowerCase()));
//         const filteredAvailable = availableRegions.filter(r => r.toLowerCase().includes(regionSearchTerm.toLowerCase()));
//         const handleRegionToggle = (value: string) => {
//             const newSelection = tempSelections.includes(value)
//                 ? tempSelections.filter(v => v !== value)
//                 : [...tempSelections, value];
//             setTempSelections(newSelection);
//         };
//         const handleSelectAll = (itemsToToggle: string[], shouldSelect: boolean) => {
//             const otherSelections = tempSelections.filter(s => !itemsToToggle.includes(s));
//             const newSelections = shouldSelect
//                 ? Array.from(new Set([...otherSelections, ...itemsToToggle]))
//                 : otherSelections;
//             setTempSelections(newSelections);
//         };
//         const isAllActiveSelected = filteredActive.length > 0 && filteredActive.every(r => tempSelections.includes(r));
//         const isSomeActiveSelected = filteredActive.some(r => tempSelections.includes(r)) && !isAllActiveSelected;
//         const isAllAvailableSelected = filteredAvailable.length > 0 && filteredAvailable.every(r => tempSelections.includes(r));
//         const isSomeAvailableSelected = filteredAvailable.some(r => tempSelections.includes(r)) && !isAllAvailableSelected;
//         const renderRegionOption = (option: string) => (
//             <FormControlLabel
//                 key={option}
//                 sx={{ width: '100%', m: 0, px: 2, py: 0.5 }}
//                 control={<Checkbox size="small" checked={tempSelections.includes(option)} onChange={() => handleRegionToggle(option)} sx={checkboxStyle}/>}
//                 label={<Typography sx={{ fontSize: '13px', color: tempSelections.includes(option) ? '#333333' : '#666666' }}>{option}</Typography>}
//             />
//         );
//         return (
//             <Popper open={true} anchorEl={popperAnchor} placement="bottom-start" transition sx={{zIndex: 1200}} modifiers={[{name: 'offset', options: {offset: [0, 8]}}]}>
//                 {({ TransitionProps }) => (
//                     <Grow {...TransitionProps} style={{ transformOrigin: 'top left' }}>
//                         <Paper ref={popperPaperRef} sx={{ width: popperAnchor?.offsetWidth, borderRadius: '12px', backgroundColor: '#FFFFFF', boxShadow: '0 4px 20px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
//                             <ClickAwayListener onClickAway={handlePopperClose}>
//                                 <Box>
//                                     <Box sx={{ p: 2, borderBottom: '1px solid #f0f0f0' }}>
//                                         <TextField
//                                             autoFocus placeholder="Search by region" value={regionSearchTerm}
//                                             onChange={(e) => setRegionSearchTerm(e.target.value)}
//                                             onKeyDown={(e) => e.stopPropagation()} fullWidth variant="outlined"
//                                             InputProps={{
//                                                 startAdornment: (
//                                                     <InputAdornment position="start" sx={{ ml: -0.5 }}>
//                                                     <img src="/icons/search_icon.svg" alt="Search" style={{ width: '16px', height: '16px' }} />
//                                                     </InputAdornment>
//                                                 ),
//                                                 sx: { height: '32px', borderRadius: '10px', backgroundColor: 'rgba(0, 0, 0, 0.04)', '& .MuiOutlinedInput-notchedOutline': { border: 'none' }, '& .MuiInputBase-input::placeholder': { fontSize: '14px', fontWeight: 300 } },
//                                             }}
//                                         />
//                                     </Box>
//                                     <Box sx={{ maxHeight: '200px', overflowY: 'auto' }}>
//                                         {filteredActive.length > 0 && (
//                                             <>
//                                                 <ListSubheader sx={{ bgcolor: '#F9FAFB', lineHeight: '32px', fontSize: '13px', fontWeight: 600 }}>Active Regions</ListSubheader>
//                                                 <FormControlLabel
//                                                     sx={{ width: '100%', m: 0, px: 2, py: 0.5 }}
//                                                     label={<Typography sx={{ fontSize: '13px', fontWeight: 500 }}>All</Typography>}
//                                                     control={<Checkbox size="small" checked={isAllActiveSelected} indeterminate={isSomeActiveSelected} onChange={(e) => handleSelectAll(filteredActive, e.target.checked)} sx={checkboxStyle}/>}
//                                                 />
//                                                 {filteredActive.map(renderRegionOption)}
//                                             </>
//                                         )}
//                                         {filteredAvailable.length > 0 && (
//                                             <>
//                                                 <ListSubheader sx={{ bgcolor: '#F9FAFB', lineHeight: '32px', fontSize: '13px', fontWeight: 600 }}>Available Regions</ListSubheader>
//                                                 <FormControlLabel
//                                                     sx={{ width: '100%', m: 0, px: 2, py: 0.5 }}
//                                                     label={<Typography sx={{ fontSize: '13px', fontWeight: 500 }}>All</Typography>}
//                                                     control={<Checkbox size="small" checked={isAllAvailableSelected} indeterminate={isSomeAvailableSelected} onChange={(e) => handleSelectAll(filteredAvailable, e.target.checked)} sx={checkboxStyle}/>}
//                                                 />
//                                                 {filteredAvailable.map(renderRegionOption)}
//                                             </>
//                                         )}
//                                     </Box>
//                                     <Divider />
//                                     <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', p: 1 }}>
//                                         <Button onClick={() => setTempSelections([])} sx={{ textTransform: 'none', color: '#999999' }}>Clear</Button>
//                                         <Button onClick={handleApply} sx={{ textTransform: 'none', color: '#0064FF' }}>Apply</Button>
//                                     </Box>
//                                 </Box>
//                             </ClickAwayListener>
//                         </Paper>
//                     </Grow>
//                 )}
//             </Popper>
//         );
//     }

//       return null;
//     };

//     return (
//         <Tooltip title={disabled ? "Please complete Budget Details before configuring scope." : ""}>
//             <Box sx={{ p: { xs: 1, sm: 2 }, bgcolor: '#FFFFFF', borderRadius: '10px', border: '1px solid #F3F3F3', position: 'relative' }}>
//                 {disabled && (
//                   <Box sx={{
//                       position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
//                       backgroundColor: 'rgba(255, 255, 255, 0.7)', zIndex: 1, cursor: 'not-allowed'
//                   }} />
//               )}
//             <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
//               <img src="/icons/Scope-Config.svg" alt="Scope Configuration" style={{ marginRight: '12px', width: '28px', height: '28px' }} />
//               <Typography variant="h6" sx={{ fontWeight: 500, fontSize: '14px', color: '#333333', fontFamily: 'Inter' }}>Scope Configuration</Typography>
//             </Box>
//             <Divider sx={{ borderColor: '#F3F3F3', mx: -3 }} />

//             <Box sx={{ display: 'flex', gap: 2, mt: 2, px: 1, flexWrap: 'wrap' }}>
//               <Box sx={providerBoxStyle(provider === 'GCP', false)} onClick={() => handleProviderChange('GCP')}>
//                 <Box sx={{ display: 'flex', alignItems: 'center' }}><img src="/icons/google-cloud-icon-2048x1646-7admxejz.svg" alt="GCP" style={{ marginRight: 8, width: 20, height: 20 }} /><Typography variant="body2" sx={{ fontFamily: 'Inter', fontSize: '13px', fontWeight: 400 }}>Google Cloud Platform</Typography></Box>
//                 <Radio checked={provider === 'GCP'} size="small" sx={{ p: 0, transform: 'scale(0.85)', '&.Mui-checked': { color: '#0064FF' } }}/>
//               </Box>
//               <Box sx={providerBoxStyle(provider === 'LANGFUSE', false)} onClick={() => handleProviderChange('LANGFUSE')}>
//                 <Box sx={{ display: 'flex', alignItems: 'center' }}><img src="/icons/langfuse-icon.png" alt="LANGFUSE" style={{ marginRight: 8, width: 20, height: 20 }}/><Typography variant="body2" sx={{ fontFamily: 'Inter', fontSize: '13px', fontWeight: 400 }}>Langfuse</Typography></Box>
//                 <Radio checked={provider === 'LANGFUSE'} size="small" sx={{ p: 0, transform: 'scale(0.85)', '&.Mui-checked': { color: '#0064FF' } }}/>
//               </Box>
//               <Box sx={providerBoxStyle(false, true)}><Box sx={{ display: 'flex', alignItems: 'center' }}><img src="/icons/aws.svg" alt="AWS" style={{ marginRight: 8, width: 20, height: 20 }}/><Typography variant="body2" sx={{ fontFamily: 'Inter', fontSize: '13px', fontWeight: 400 }}>Amazon Web Services</Typography></Box><Radio checked={false} size="small" disabled={true} sx={{ p: 0, transform: 'scale(0.85)' }}/></Box>
//               <Box sx={providerBoxStyle(false, true)}><Box sx={{ display: 'flex', alignItems: 'center' }}><img src="/icons/azure2.svg" alt="Azure" style={{ marginRight: 8, width: 20, height: 20 }}/><Typography variant="body2" sx={{ fontFamily: 'Inter', fontSize: '13px', fontWeight: 400 }}>Microsoft Azure</Typography></Box><Radio checked={false} size="small" disabled={true} sx={{ p: 0, transform: 'scale(0.85)' }}/></Box>
//             </Box>
//             {errors.provider && <Typography color="error" variant="caption" sx={{ display: 'block', mt: 1, px: 1 }}>{errors.provider}</Typography>}

//             {provider === 'GCP' && (
//               <Box sx={{ width: '100%', bgcolor: '#F9FAFB', borderRadius: '10px', mt: 2, p: 2 }}>
//                 <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
//                   <Button onClick={handleAddService} disabled={services.length > 0 && services[services.length-1].serviceTypes.length === 0} sx={{ textTransform: 'none', color: '#0064FF', fontFamily: 'Inter', fontWeight: 500, fontSize: '14px' }}>
//                     + Add More
//                   </Button>
//                 </Box>

//                 {services.map((service, index) => {
//                   const isProjectDisabled = service.dataSources.length === 0 || optionsLoading;
//                   const isRegionDisabled = service.projectIds.length === 0 || optionsLoading;
//                   const isServiceDisabled = service.regions.length === 0 || optionsLoading;

//                   const dataSourceButtonText = service.dataSources.length === dataSourceOptions.length && dataSourceOptions.length > 0 ? 'All' : service.dataSources.length > 0 ? `${service.dataSources.length} selected` : 'Select data sources';
//                   const projectButtonText = service.projectIds.length === projectOptions.length && projectOptions.length > 0 ? 'All' : service.projectIds.length > 0 ? `${service.projectIds.length} selected` : 'Select projects';
//                   const allRegionsCount = activeRegions.length + availableRegions.length;
//                   const regionButtonText = service.regions.length === allRegionsCount && allRegionsCount > 0 ? 'All' : service.regions.length > 0 ? `${service.regions.length} selected` : 'Select regions';
//                   const labelButtonText = service.label.length === 1 ? '1 filter applied' : service.label.length > 0 ? `${service.label.length} filters applied` : 'Select Label';

//                   const getButtonSx = (hasSelection: boolean) => ({
//                       width: '100%', height: '36px', backgroundColor: '#FFFFFF', border: `1px solid #E0E0E0`, borderRadius: '8px',
//                       fontFamily: 'Inter', fontWeight: hasSelection ? 500 : 400, fontSize: '13px',
//                       color: hasSelection ? '#333333' : '#999999', lineHeight: '18px', letterSpacing: '0%',
//                       textTransform: 'none', justifyContent: 'space-between', '&:hover': { borderColor: '#0064FF' },
//                       '&.Mui-disabled': { backgroundColor: '#F3F3F3', border: '1px solid #D3D3D3' }
//                   });

//                   const showLabelButton = service.serviceTypes.length > 0;
//                   const isKubeSelected = service.serviceTypes.includes('Kubernetes Engine');
//                   const showFullKubeFields = isKubeSelected && activeServices.includes('Kubernetes Engine');
//                   return (
//                     <Box key={service.id}>
//                       <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: 2, alignItems: 'flex-end' }}>
//                         <Box>
//                           <Typography sx={{ fontFamily: 'Inter', fontWeight: 400, fontSize: '13px', color: optionsLoading ? '#A0A0A0' : '#333333', mb: 0.5 }}>Data Source<span style={{ color: 'red' }}>*</span></Typography>
//                           <Button ref={el => buttonRefs.current[`${service.id}-dataSources`] = el} onClick={(e) => handlePopperOpen(e, 'dataSources', service.id, service.dataSources)} endIcon={<ExpandMoreIcon />} sx={getButtonSx(service.dataSources.length > 0)} disabled={optionsLoading}>{dataSourceButtonText}</Button>
//                         </Box>

//                         <Box>
//                           <Typography sx={{ fontFamily: 'Inter', fontWeight: 400, fontSize: '13px', color: isProjectDisabled ? '#A0A0A0' : '#333333', mb: 0.5 }}>Project<span style={{ color: 'red' }}>*</span></Typography>
//                           <Button
//                             ref={el => buttonRefs.current[`${service.id}-projectIds`] = el}
//                             disabled={isProjectDisabled}
//                             onClick={(e) => handlePopperOpen(e, 'projectIds', service.id, service.projectIds)}
//                             endIcon={<ExpandMoreIcon />}
//                             sx={getButtonSx(service.projectIds.length > 0)}
//                           >
//                             {projectButtonText}
//                           </Button>
//                         </Box>

//                         <Box>
//                           <Typography sx={{ fontFamily: 'Inter', fontWeight: 400, fontSize: '13px', color: isRegionDisabled ? '#A0A0A0' : '#333333', mb: 0.5 }}>Region<span style={{ color: 'red' }}>*</span></Typography>
//                           <Button ref={el => buttonRefs.current[`${service.id}-regions`] = el} disabled={isRegionDisabled} onClick={(e) => handlePopperOpen(e, 'regions', service.id, service.regions)} endIcon={<ExpandMoreIcon />} sx={getButtonSx(service.regions.length > 0)}>{regionButtonText}</Button>
//                         </Box>

//                         <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
//                            <Box sx={{flexGrow: 1}}>
//                                <SectionedMultiSelectDropdown
//                                    ref={el => buttonRefs.current[`${service.id}-service`] = el}
//                                    label="Service"
//                                    options={{ active: activeServices, available: availableServices }}
//                                    selectedValues={service.serviceTypes}
//                                    onChange={(newValues) => handleUpdateService(service.id, 'serviceTypes', newValues)}
//                                    disabled={isServiceDisabled}
//                                />
//                            </Box>
//                           <IconButton onClick={() => handleDeleteService(service.id)} disabled={services.length <= 1} sx={{ mb: '4px' }}><img src={(services.length <= 1) ? "/icons/delete_icon_disabled.svg" : "/icons/delete_icon.svg"} alt="Delete Service" style={{ width: '17px', height: '17px' }} /></IconButton>
//                         </Box>

//                         {showLabelButton && (
//                           <Box>
//                             <Typography sx={{ fontFamily: 'Inter', fontWeight: 400, fontSize: '13px', color: isServiceDisabled ? '#A0A0A0' : '#333333', mb: 0.5 }}>Label</Typography>
//                             <Button onClick={(e) => handleLabelMenuOpen(e, service.id)} disabled={isServiceDisabled} endIcon={<ExpandMoreIcon />} variant="outlined" sx={getButtonSx(service.label.length > 0)}>{labelButtonText}</Button>
//                           </Box>
//                         )}

//                         {showFullKubeFields && (
//                           <>
//                             <Box>
//                               <MultiSelectDropdown label="Cluster" options={k8sClusters} selectedValues={service.cluster} onChange={(newValues: string[]) => handleUpdateService(service.id, 'cluster', newValues)} disabled={isServiceDisabled} width="100%" />
//                             </Box>
//                             <Box>
//                               <MultiSelectDropdown label="Namespace" options={k8sNamespaces} selectedValues={service.namespace} onChange={(newValues: string[]) => handleUpdateService(service.id, 'namespace', newValues)} disabled={isServiceDisabled} width="100%" />
//                             </Box>
//                           </>
//                         )}
//                       </Box>
//                       {service.label.length > 0 && (
//                         <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
//                           {service.label.flatMap(labelItem =>
//                             labelItem.values.map(value => (
//                               <Chip
//                                 key={`${labelItem.key}-${value}`}
//                                 label={`${labelItem.key}: ${value}`}
//                                 size="small"
//                                 onDelete={() => handleDeleteLabelChip(service.id, labelItem.key, value)}
//                                 sx={{
//                                   backgroundColor: ' #F3F8FE',
//                                   color: '#333333',
//                                   fontFamily: 'Inter',
//                                   fontSize: '12px',
//                                   fontWeight: 500,
//                                   borderRadius: '8px',
//                                   '.MuiChip-deleteIcon': {
//                                       color: '#333333',
//                                       fontSize: '16px',
//                                       '&:hover': {
//                                         color: 'red',
//                                       },
//                                   },
//                                 }}
//                               />
//                             ))
//                           )}
//                         </Box>
//                       )}
//                       {index < services.length - 1 && <Divider sx={{ my: 2, mx: -2 }} />}
//                     </Box>
//                   )
//                 })}
//               </Box>
//             )}

//             {provider === 'LANGFUSE' && services.map((service, index) => {
//                  const isModelDisabled = service.dataSources.length === 0 || optionsLoading;
//                  const isTraceDisabled = service.skus.length === 0 || optionsLoading;

//                  // Map selected data source IDs back to names for display in the dropdown
//                  const selectedDataSourceNames = service.dataSources
//                     .map(id => dataSourceOptions.find(d => d.id === id)?.name)
//                     .filter((name): name is string => !!name);

//                  return (
//                     <Box key={service.id} sx={{ width: '100%', bgcolor: '#F9FAFB', borderRadius: '10px', mt: 2, p: 2 }}>
//                         <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: 2, alignItems: 'flex-end' }}>
//                             <Box>
//                                 <MultiSelectDropdown 
//                                     label="Data Source" 
//                                     options={dataSourceOptions.map(d => d.name)} 
//                                     selectedValues={selectedDataSourceNames}
//                                     onChange={(newNames) => {
//                                         // Map selected names back to IDs to store in state
//                                         const newIds = newNames
//                                             .map(name => dataSourceOptions.find(d => d.name === name)?.id)
//                                             .filter((id): id is string => !!id);
//                                         handleUpdateService(service.id, 'dataSources', newIds);
//                                     }} 
//                                     disabled={optionsLoading} 
//                                 />
//                             </Box>
//                              <Box>
//                                 <MultiSelectDropdown label="Model" options={skuOptions} selectedValues={service.skus} onChange={(newValues) => handleUpdateService(service.id, 'skus', newValues)} disabled={isModelDisabled} />
//                             </Box>
//                              <Box>
//                                 <MultiSelectDropdown label="Trace" options={activeServices} selectedValues={service.serviceTypes} onChange={(newValues) => handleUpdateService(service.id, 'serviceTypes', newValues)} disabled={isTraceDisabled} />
//                             </Box>
//                         </Box>
//                         {index < services.length - 1 && <Divider sx={{ my: 2, mx: -2 }} />}
//                     </Box>
//                  )
//             })}

//             {renderMultiSelectPopper()}

//             <LabelFilterMenu
//                 open={openPopper?.type === 'label'}
//                 anchorEl={labelMenuAnchor}
//                 onClose={handleLabelMenuClose}
//                 onApply={handleLabelApply}
//                 appliedFilters={activeServiceForLabel?.label || []}
//                 labelKeys={labelKeys}
//                 labelValues={labelValuesData}
//                 onFetchLabelValues={handleFetchLabelValues}
//                 loadingLabelKey={loadingLabelKey}
//             />
//             </Box>
//         </Tooltip>
//     );
// };

// export default ScopeConfig;



// // import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
// // import { useLazyQuery, gql } from '@apollo/client';
// // import { useOutletContext } from 'react-router-dom';
// // import {
// //   Box, Typography, Radio, Divider, Button, Popper, Grow, Paper, Checkbox, FormControlLabel,
// //   ClickAwayListener, IconButton, ListSubheader, TextField, InputAdornment, Tooltip, Chip
// // } from '@mui/material';
// // import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
// // import ExpandLessIcon from '@mui/icons-material/ExpandLess';
// // import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
// // import LabelFilterMenu, { LabelFilterPair } from './LabelFilterMenu';

// // // --- GraphQL Query Definitions ---
// // const GET_BUDGET_FILTER_OPTIONS = gql`
// //   query GetBudgetFilterOptions($input: BudgetFilterOptionsInput!) {
// //     getBudgetFilterOptions(input: $input) {
// //       dataSources { id, name, providerType, isSyncing }
// //       activeRegions, availableRegions, activeServices, availableServices, projectIds
// //       labelKeys, k8sClusters, k8sNamespaces
// //     }
// //   }
// // `;

// // const GET_LABEL_VALUES = gql`
// //   query GetBudgetLabelValues($input: BudgetLabelValuesInput!) {
// //     getBudgetLabelValues(input: $input) {
// //       values
// //     }
// //   }
// // `;

// // // --- Interfaces and Helper Components ---
// // export interface ServiceState {
// //   id: string;
// //   dataSources: string[];
// //   projectIds: string[];
// //   regions: string[];
// //   serviceTypes: string[];
// //   label: LabelFilterPair[];
// //   cluster: string[];
// //   namespace: string[];
// //   deployment: string[];
// // }

// // interface ScopeConfigProps {
// //     provider: string;
// //     setProvider: (provider: string) => void;
// //     services: ServiceState[];
// //     setServices: React.Dispatch<React.SetStateAction<ServiceState[]>>;
// //     getInitialService: () => Omit<ServiceState, 'id' | 'serviceTypes'> & { serviceTypes: string[] };
// //     errors: { [key: string]: string };
// //     disabled?: boolean;
// //     period: 'Monthly' | 'Quarterly' | 'Yearly';
// // }

// // interface AppOutletContext {
// //   currentTenant?: { id: string; name: string; };
// // }

// // interface DataSourceOption {
// //   id: string;
// //   name: string;
// //   providerType: string;
// //   isSyncing: boolean;
// // }

// // const SectionedMultiSelectDropdown = React.forwardRef<
// //   HTMLButtonElement,
// //   {
// //     label: string;
// //     options: { active: string[]; available: string[] };
// //     selectedValues: string[];
// //     onChange: (selected: string[]) => void;
// //     disabled?: boolean;
// //   }
// // >(({ label, options, selectedValues, onChange, disabled = false }, ref) => {
// //   const [isOpen, setIsOpen] = useState(false);
// //   const [searchTerm, setSearchTerm] = useState('');
// //   const anchorRef = useRef<HTMLButtonElement>(null);

// //   const handleRef = useCallback((node: HTMLButtonElement) => {
// //     (anchorRef as React.MutableRefObject<HTMLButtonElement | null>).current = node;
// //     if (typeof ref === 'function') {
// //       ref(node);
// //     } else if (ref) {
// //       (ref as React.MutableRefObject<HTMLButtonElement | null>).current = node;
// //     }
// //   }, [ref]);

// //   const handleOpen = () => setIsOpen(true);
// //   const handleClose = () => {
// //     setIsOpen(false);
// //     setSearchTerm('');
// //   };

// //   const filteredActive = useMemo(() => options.active.filter(opt => opt.toLowerCase().includes(searchTerm.toLowerCase())), [options.active, searchTerm]);
// //   const filteredAvailable = useMemo(() => options.available.filter(opt => opt.toLowerCase().includes(searchTerm.toLowerCase())), [options.available, searchTerm]);
  
// //   const isKubeSelected = useMemo(() => selectedValues.includes('Kubernetes Engine'), [selectedValues]);

// //   const isAnyActiveSelected = useMemo(() => options.active.some(opt => selectedValues.includes(opt) && opt !== 'Kubernetes Engine'), [options.active, selectedValues]);
// //   const isAnyAvailableSelected = useMemo(() => options.available.some(opt => selectedValues.includes(opt) && opt !== 'Kubernetes Engine'), [options.available, selectedValues]);

// //   const nonKubeFilteredActive = useMemo(() => filteredActive.filter(s => s !== 'Kubernetes Engine'), [filteredActive]);
// //   const nonKubeFilteredAvailable = useMemo(() => filteredAvailable.filter(s => s !== 'Kubernetes Engine'), [filteredAvailable]);

// //   const isAllActiveSelected = nonKubeFilteredActive.length > 0 && nonKubeFilteredActive.every(s => selectedValues.includes(s));
// //   const isSomeActiveSelected = nonKubeFilteredActive.some(s => selectedValues.includes(s)) && !isAllActiveSelected;

// //   const isAllAvailableSelected = nonKubeFilteredAvailable.length > 0 && nonKubeFilteredAvailable.every(s => selectedValues.includes(s));
// //   const isSomeAvailableSelected = nonKubeFilteredAvailable.some(s => selectedValues.includes(s)) && !isAllAvailableSelected;

// //   const handleToggle = (value: string) => {
// //     if (isKubeSelected && value !== 'Kubernetes Engine') return;
// //     if ((isAnyActiveSelected || isAnyAvailableSelected) && value === 'Kubernetes Engine') return;

// //     if (value === 'Kubernetes Engine') {
// //       onChange(isKubeSelected ? [] : ['Kubernetes Engine']);
// //       return;
// //     }
    
// //     const isTogglingActive = options.active.includes(value);
// //     const isTogglingAvailable = options.available.includes(value);
    
// //     let baseSelection = selectedValues.filter(v => v !== 'Kubernetes Engine');
// //     if (isTogglingActive) {
// //         baseSelection = baseSelection.filter(v => !options.available.includes(v));
// //     } else if (isTogglingAvailable) {
// //         baseSelection = baseSelection.filter(v => !options.active.includes(v));
// //     }

// //     const newSelected = baseSelection.includes(value)
// //         ? baseSelection.filter(v => v !== value)
// //         : [...baseSelection, value];
        
// //     onChange(newSelected);
// //   };

// //   const handleSelectAll = (itemsToToggle: string[], shouldSelect: boolean) => {
// //     if (shouldSelect) {
// //       const nonKubeItems = itemsToToggle.filter(v => v !== 'Kubernetes Engine');
// //       onChange(nonKubeItems);
// //     } else {
// //       onChange([]);
// //     }
// //   };

// //   const allOptions = [...options.active, ...options.available];
  
// //   // --- FIX: Changed button text logic as per your request ---
// //   const buttonText =
// //     selectedValues.length === 0 ? `Select ${label.toLowerCase()}`
// //     : selectedValues.length === allOptions.length && allOptions.length > 0 ? 'All selected'
// //     : `${selectedValues.length} selected`;

// //     const renderOption = (option: string) => {
// //         const isThisOptionActive = options.active.includes(option);
// //         const isThisOptionAvailable = options.available.includes(option);
        
// //         const isDisabled = 
// //             (isKubeSelected && option !== 'Kubernetes Engine') || 
// //             ((isAnyActiveSelected || isAnyAvailableSelected) && option === 'Kubernetes Engine') ||
// //             (isThisOptionAvailable && isAnyActiveSelected) ||
// //             (isThisOptionActive && isAnyActiveSelected);

// //         return (
// //           <FormControlLabel
// //               key={option}
// //               sx={{ width: '100%', m: 0, px: 2, py: 0.5 }}
// //               control={
// //                   <Checkbox
// //                       size="small"
// //                       checked={selectedValues.includes(option)}
// //                       onChange={() => handleToggle(option)}
// //                       disabled={isDisabled}
// //                       sx={{ '&.Mui-checked': { color: '#0064FF' } }}
// //                   />
// //               }
// //               label={
// //                 <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
// //                   <Typography sx={{ fontSize: '13px', color: isDisabled ? '#B0B0B0' : selectedValues.includes(option) ? '#333333' : '#666666' }}>
// //                     {option}
// //                   </Typography>
// //                   {option === 'Kubernetes Engine' && isDisabled && (
// //                     <Tooltip
// //                       title="As you have selected other services, Kubernetes Engine is disabled. To select it, please clear other services."
// //                       arrow
// //                       componentsProps={{
// //                         tooltip: {
// //                           sx: { backgroundColor: '#FFFFFF', color: '#000000', border: '1px solid #E0E0E0', fontSize: '12px', },
// //                         },
// //                         arrow: { sx: { color: '#FFFFFF', }, },
// //                       }}
// //                     >
// //                       <InfoOutlinedIcon sx={{ color: '#0064FF', ml: 1, fontSize: '16px', }} />
// //                     </Tooltip>
// //                   )}
// //                 </Box>
// //               }
// //           />
// //         );
// //       };

// //   return (
// //     <Box sx={{ width: '100%' }}>
// //       <Typography sx={{ fontFamily: 'Inter', fontWeight: 400, fontSize: '13px', color: disabled ? '#A0A0A0' : '#333333', mb: 0.5 }}>
// //         {label}<span style={{ color: 'red' }}>*</span>
// //       </Typography>
// //       <Button
// //         ref={handleRef} variant="outlined" disabled={disabled} onClick={handleOpen}
// //         endIcon={isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
// //         sx={{
// //           width: '100%', height: '36px', backgroundColor: disabled ? '#F3F3F3' : '#FFFFFF',
// //           border: `1px solid ${disabled ? '#D3D3D3' : '#E0E0E0'}`, borderRadius: '8px',
// //           fontFamily: 'Inter', fontWeight: selectedValues.length > 0 ? 500 : 400, fontSize: '13px',
// //           color: selectedValues.length > 0 ? '#333333' : '#999999', lineHeight: '18px',
// //           letterSpacing: '0%', textTransform: 'none', justifyContent: 'space-between',
// //           '&:hover': { borderColor: disabled ? '#D3D3D3' : '#0064FF' },
// //         }}
// //       >
// //         {buttonText}
// //       </Button>
// //       <Popper
// //         open={isOpen} anchorEl={anchorRef.current} placement="bottom-start"
// //         transition sx={{ zIndex: 1300, width: anchorRef.current?.offsetWidth }}
// //       >
// //         {({ TransitionProps }) => (
// //           <ClickAwayListener onClickAway={handleClose}>
// //             <Grow {...TransitionProps} style={{ transformOrigin: 'top left' }}>
// //               <Paper sx={{ borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.1)', maxHeight: '300px', display: 'flex', flexDirection: 'column' }}>
// //                 <Box sx={{ p: 2, borderBottom: '1px solid #f0f0f0' }}>
// //                     <TextField
// //                         autoFocus placeholder="Search by service name.." value={searchTerm}
// //                         onChange={(e) => setSearchTerm(e.target.value)}
// //                         onKeyDown={(e) => e.stopPropagation()} fullWidth variant="outlined"
// //                         InputProps={{
// //                             startAdornment: (
// //                                 <InputAdornment position="start" sx={{ ml: -0.5 }}>
// //                                 <img src="/icons/search_icon.svg" alt="Search" style={{ width: '16px', height: '16px' }} />
// //                                 </InputAdornment>
// //                             ),
// //                             sx: { height: '32px', borderRadius: '10px', backgroundColor: 'rgba(0, 0, 0, 0.04)', '& .MuiOutlinedInput-notchedOutline': { border: 'none' }, '& .MuiInputBase-input::placeholder': { fontSize: '14px', fontWeight: 300 } },
// //                         }}
// //                     />
// //                 </Box>
// //                 <Box sx={{ overflowY: 'auto' }}>
// //                     {filteredActive.length === 0 && filteredAvailable.length === 0 ? (
// //                         <Typography sx={{ p: 2, color: '#999', fontSize: '13px', textAlign: 'center' }}>No results found</Typography>
// //                     ) : (
// //                         <>
// //                             {filteredActive.length > 0 && (
// //                                 <>
// //                                     <ListSubheader sx={{ bgcolor: '#F9FAFB', lineHeight: '32px', fontSize: '13px', fontWeight: 600 }}>Active Services</ListSubheader>
// //                                     <FormControlLabel
// //                                         sx={{ width: '100%', m: 0, px: 2, py: 0.5 }}
// //                                         label={<Typography sx={{ fontSize: '13px' }}>All</Typography>}
// //                                         control={<Checkbox size="small" checked={isAllActiveSelected} indeterminate={isSomeActiveSelected} onChange={(e) => handleSelectAll(filteredActive, e.target.checked)} disabled={isKubeSelected || isAnyAvailableSelected} sx={{'&.Mui-checked, &.MuiCheckbox-indeterminate': { color: '#0064FF' }}} />}
// //                                     />
// //                                     {filteredActive.map(renderOption)}
// //                                 </>
// //                             )}
// //                             {filteredAvailable.length > 0 && (
// //                                 <>
// //                                     <ListSubheader sx={{ bgcolor: '#F9FAFB', lineHeight: '32px', fontSize: '13px', fontWeight: 600 }}>Available Services</ListSubheader>
// //                                     <FormControlLabel
// //                                         sx={{ width: '100%', m: 0, px: 2, py: 0.5 }}
// //                                         label={<Typography sx={{ fontSize: '13px' }}>All</Typography>}
// //                                         control={<Checkbox size="small" checked={isAllAvailableSelected} indeterminate={isSomeAvailableSelected} onChange={(e) => handleSelectAll(filteredAvailable, e.target.checked)} disabled={isKubeSelected || isAnyActiveSelected} sx={{'&.Mui-checked, &.MuiCheckbox-indeterminate': { color: '#0064FF' }}} />}
// //                                     />
// //                                     {filteredAvailable.map(renderOption)}
// //                                 </>
// //                             )}
// //                         </>
// //                     )}
// //                 </Box>
// //                 <Divider />
// //                 <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1, flexShrink: 0 }}>
// //                   <Button onClick={() => onChange([])} sx={{ textTransform: 'none', color: '#999999' }}>Clear</Button>
// //                   <Button onClick={handleClose} sx={{ textTransform: 'none', color: '#0064FF' }}>Apply</Button>
// //                 </Box>
// //               </Paper>
// //             </Grow>
// //           </ClickAwayListener>
// //         )}
// //       </Popper>
// //     </Box>
// //   );
// // });

// // const MultiSelectDropdown: React.FC<{
// //   label: string;
// //   options: string[];
// //   selectedValues: string[];
// //   onChange: (selected: string[]) => void;
// //   disabled?: boolean;
// //   width?: string | number;
// // }> = ({ label, options, selectedValues, onChange, disabled = false, width = '100%' }) => {
// //   const [isOpen, setIsOpen] = useState(false);
// //   const anchorRef = useRef<HTMLButtonElement>(null);

// //   const handleOpen = () => setIsOpen(true);
// //   const handleClose = () => setIsOpen(false);

// //   const handleToggle = (value: string) => {
// //     const newSelected = selectedValues.includes(value)
// //       ? selectedValues.filter((v) => v !== value)
// //       : [...selectedValues, value];
// //     onChange(newSelected);
// //   };

// //   const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
// //     onChange(event.target.checked ? options : []);
// //   };

// //   const isAllSelected = options.length > 0 && selectedValues.length === options.length;
// //   const buttonText =
// //     selectedValues.length === 0 ? `Select ${label.toLowerCase()}`
// //     : selectedValues.length === options.length ? 'All selected'
// //     : `${selectedValues.length} selected`;

// //   return (
// //     <Box sx={{ width }}>
// //       <Typography sx={{ fontFamily: 'Inter', fontWeight: 400, fontSize: '13px', color: disabled ? '#A0A0A0' : '#333333', mb: 0.5 }}>
// //         {label}<span style={{ color: 'red' }}>*</span>
// //       </Typography>
// //       <Button
// //         ref={anchorRef}
// //         variant="outlined"
// //         disabled={disabled}
// //         onClick={handleOpen}
// //         endIcon={isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
// //         sx={{
// //           width: '100%', height: '36px', backgroundColor: disabled ? '#F3F3F3' : '#FFFFFF',
// //           border: `1px solid ${disabled ? '#D3D3D3' : '#E0E0E0'}`, borderRadius: '8px',
// //           fontFamily: 'Inter',
// //           fontWeight: selectedValues.length > 0 ? 500 : 400,
// //           fontSize: '13px',
// //           color: selectedValues.length > 0 ? '#333333' : '#999999',
// //           lineHeight: '18px',
// //           letterSpacing: '0%',
// //           textTransform: 'none',
// //           justifyContent: 'space-between',
// //           '&:hover': { borderColor: disabled ? '#D3D3D3' : '#0064FF' },
// //         }}
// //       >
// //         {buttonText}
// //       </Button>
// //       <Popper
// //         open={isOpen}
// //         anchorEl={anchorRef.current}
// //         placement="bottom-start"
// //         transition
// //         sx={{ zIndex: 1300, width: anchorRef.current?.offsetWidth }}
// //       >
// //         {({ TransitionProps }) => (
// //           <ClickAwayListener onClickAway={handleClose}>
// //             <Grow {...TransitionProps} style={{ transformOrigin: 'top left' }}>
// //               <Paper sx={{ borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
// //                 <FormControlLabel
// //                   label={<Typography sx={{ fontSize: '13px', color: isAllSelected ? '#333333' : '#666666' }}>All</Typography>}
// //                   sx={{ width: '100%', m: 0, px: 2, py: 0.5 }}
// //                   control={
// //                     <Checkbox size="small" checked={isAllSelected}
// //                       indeterminate={selectedValues.length > 0 && !isAllSelected}
// //                       onChange={handleSelectAll}
// //                       sx={{ '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: '#0064FF' } }}
// //                     />
// //                   }
// //                 />
// //                 <Box sx={{ maxHeight: '200px', overflowY: 'auto' }}>
// //                   {options.map((option) => (
// //                     <FormControlLabel
// //                       key={option} sx={{ width: '100%', m: 0, px: 2, py: 0.5 }}
// //                       control={
// //                         <Checkbox size="small" checked={selectedValues.includes(option)}
// //                           onChange={() => handleToggle(option)}
// //                           sx={{ '&.Mui-checked': { color: '#0064FF' } }}
// //                         />
// //                       }
// //                       label={<Typography sx={{ fontSize: '13px', color: selectedValues.includes(option) ? '#333333' : '#666666' }}>{option}</Typography>}
// //                     />
// //                   ))}
// //                 </Box>
// //                 <Divider />
// //                 <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
// //                   <Button onClick={() => onChange([])} sx={{ textTransform: 'none', color: '#999999' }}>Clear</Button>
// //                   <Button onClick={handleClose} sx={{ textTransform: 'none', color: '#0064FF' }}>Apply</Button>
// //                 </Box>
// //               </Paper>
// //             </Grow>
// //           </ClickAwayListener>
// //         )}
// //       </Popper>
// //     </Box>
// //   );
// // };


// // // --- Main ScopeConfig Component ---
// // const ScopeConfig: React.FC<ScopeConfigProps> = ({
// //   provider, setProvider, services, setServices, getInitialService,
// //   errors, disabled = false, period,
// // }) => {
// //     const { currentTenant } = useOutletContext<AppOutletContext>();
// //     const [openPopper, setOpenPopper] = useState<{ type: 'dataSources' | 'projectIds' | 'regions' | 'label', serviceId: string } | null>(null);
// //     const [tempSelections, setTempSelections] = useState<string[]>([]);
// //     const [regionSearchTerm, setRegionSearchTerm] = useState('');

// //     const buttonRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({});
// //     const [popperAnchor, setPopperAnchor] = useState<HTMLButtonElement | null>(null);
// //     const [labelMenuAnchor, setLabelMenuAnchor] = useState<HTMLElement | null>(null);
// //     const popperPaperRef = useRef(null);
// //     const hasOpenedAutomatically = useRef(false);

// //     const [dataSourceOptions, setDataSourceOptions] = useState<DataSourceOption[]>([]);
// //     const [projectOptions, setProjectOptions] = useState<string[]>([]);
// //     const [activeRegions, setActiveRegions] = useState<string[]>([]);
// //     const [availableRegions, setAvailableRegions] = useState<string[]>([]);
// //     const [activeServices, setActiveServices] = useState<string[]>([]);
// //     const [availableServices, setAvailableServices] = useState<string[]>([]);
// //     const [labelKeys, setLabelKeys] = useState<string[]>([]);
// //     const [k8sClusters, setK8sClusters] = useState<string[]>([]);
// //     const [k8sNamespaces, setK8sNamespaces] = useState<string[]>([]);
// //     const [labelValuesData, setLabelValuesData] = useState<{ [key: string]: string[] }>({});
// //     const [loadingLabelKey, setLoadingLabelKey] = useState<string | null>(null);

// //     const dataSourceDependency = useMemo(() => {
// //         const sortedIds = Array.from(new Set(services.flatMap(s => s.dataSources))).sort();
// //         return JSON.stringify(sortedIds);
// //     }, [services]);

// //     const [getOptions, { loading: optionsLoading }] = useLazyQuery(GET_BUDGET_FILTER_OPTIONS, {
// //         onCompleted: (data) => {
// //             if (data?.getBudgetFilterOptions) {
// //                 const {
// //                     dataSources,
// //                     projectIds,
// //                     activeRegions,
// //                     availableRegions,
// //                     activeServices,
// //                     availableServices,
// //                     labelKeys,
// //                     k8sClusters,
// //                     k8sNamespaces
// //                 } = data.getBudgetFilterOptions;

// //                 setDataSourceOptions(dataSources || []);
// //                 setProjectOptions(projectIds || []);
// //                 setActiveRegions(activeRegions || []);
// //                 setAvailableRegions(availableRegions || []);
// //                 setActiveServices(activeServices || []);
// //                 setAvailableServices(availableServices || []);
// //                 setLabelKeys(labelKeys || []);
// //                 setK8sClusters(k8sClusters || []);
// //                 setK8sNamespaces(k8sNamespaces || []);
// //             }
// //         }
// //     });

// //     const [getLabelValues] = useLazyQuery(GET_LABEL_VALUES, {
// //         onCompleted: (data) => {
// //             if (loadingLabelKey && data?.getBudgetLabelValues?.values) {
// //                 setLabelValuesData(prev => ({ ...prev, [loadingLabelKey]: data.getBudgetLabelValues.values }));
// //             }
// //             setLoadingLabelKey(null);
// //         }
// //     });

// //     useEffect(() => {
// //         if (!provider || !currentTenant?.id || !period) return;
// //         const allSelectedDataSources = JSON.parse(dataSourceDependency);
// //         getOptions({
// //             variables: {
// //                 input: {
// //                     tenantId: currentTenant.id,
// //                     provider,
// //                     period: period.toUpperCase(),
// //                     dataSourceIds: allSelectedDataSources,
// //                 }
// //             }
// //         });
// //     }, [provider, currentTenant, period, dataSourceDependency, getOptions]);

// //     useEffect(() => {
// //         if (provider === 'GCP' && services.length === 1 && services[0].dataSources.length === 0 &&
// //             !disabled && !optionsLoading && !hasOpenedAutomatically.current && dataSourceOptions.length > 0) {
// //             const firstServiceId = services[0].id;
// //             const dataSourceButton = buttonRefs.current[`${firstServiceId}-dataSources`];
// //             if (dataSourceButton) {
// //                 setPopperAnchor(dataSourceButton);
// //                 setTempSelections(services[0].dataSources);
// //                 setOpenPopper({ type: 'dataSources', serviceId: firstServiceId });
// //                 hasOpenedAutomatically.current = true;
// //             }
// //         }
// //         if (provider !== 'GCP' || services.length === 0) {
// //             hasOpenedAutomatically.current = false;
// //         }
// //     }, [provider, services, disabled, optionsLoading, dataSourceOptions]);

// //     const providerBoxStyle = (isSelected: boolean, isDisabled: boolean) => ({
// //       p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
// //       backgroundColor: isSelected ? '#F2F7FF' : (isDisabled ? '#F3F3F3' : '#FFFFFF'),
// //       border: `1px solid ${isSelected ? '#0064FF' : '#E0E0E0'}`,
// //       borderRadius: '8px', cursor: isDisabled ? 'not-allowed' : 'pointer', flex: 1, height: '36px',
// //       opacity: isDisabled ? 0.6 : 1,
// //       '&:hover': { borderColor: isDisabled ? '#E0E0E0' : '#0064FF' }
// //     });

// //     const handlePopperOpen = (event: React.MouseEvent<HTMLButtonElement>, type: 'dataSources' | 'projectIds' | 'regions', serviceId: string, currentValues: string[]) => {
// //       setPopperAnchor(event.currentTarget);
// //       setTempSelections(currentValues);
// //       setOpenPopper({ type, serviceId });
// //     };

// //     const handlePopperClose = () => {
// //       setOpenPopper(null);
// //       setRegionSearchTerm('');
// //       setPopperAnchor(null);
// //     };

// //     const handleUpdateService = useCallback((serviceId: string, field: keyof Omit<ServiceState, 'id'>, value: any) => {
// //         setServices(prev => prev.map(s => {
// //             if (s.id === serviceId) {
// //                 const updatedService = { ...s, [field]: value };
// //                 if (field === 'dataSources') {
// //                     updatedService.projectIds = [];
// //                     updatedService.regions = [];
// //                     updatedService.serviceTypes = [];
// //                     updatedService.label = [];
// //                     updatedService.cluster = [];
// //                     updatedService.namespace = [];
// //                 }
// //                 if (field === 'projectIds') {
// //                     updatedService.regions = [];
// //                     updatedService.serviceTypes = [];
// //                     updatedService.label = [];
// //                     updatedService.cluster = [];
// //                     updatedService.namespace = [];
// //                 }
// //                 if (field === 'regions') {
// //                     updatedService.serviceTypes = [];
// //                     updatedService.label = [];
// //                     updatedService.cluster = [];
// //                     updatedService.namespace = [];
// //                 }
// //                 if (field === 'serviceTypes') {
// //                     updatedService.label = [];
// //                     if (!value.includes('Kubernetes Engine')) {
// //                        updatedService.cluster = [];
// //                        updatedService.namespace = [];
// //                     }
// //                 }
// //                 return updatedService;
// //             }
// //             return s;
// //         }));
// //     }, [setServices]);

// //     const handleApply = () => {
// //         if (!openPopper) return;
// //         const { serviceId, type } = openPopper;
// //         handleUpdateService(serviceId, type as any, tempSelections);
// //         handlePopperClose();
// //     };

// //     const handleFetchLabelValues = useCallback((key: string) => {
// //         if (!key || labelValuesData[key] || loadingLabelKey) return;
// //         setLoadingLabelKey(key);
// //         const allDataSourceIds = Array.from(new Set(services.flatMap(s => s.dataSources)));
// //         getLabelValues({
// //             variables: {
// //                 input: {
// //                     tenantId: currentTenant?.id,
// //                     provider,
// //                     labelKey: key,
// //                     period: period.toUpperCase(),
// //                     dataSourceIds: allDataSourceIds,
// //                 }
// //             }
// //         });
// //     }, [getLabelValues, provider, currentTenant, services, labelValuesData, loadingLabelKey, period]);

// //     const handleDeleteLabelChip = (serviceId: string, keyToDelete: string, valueToDelete: string) => {
// //         setServices(prevServices =>
// //           prevServices.map(service => {
// //             if (service.id === serviceId) {
// //               const updatedLabels = service.label
// //                 .map(label => {
// //                   if (label.key === keyToDelete) {
// //                     const newValues = label.values.filter(v => v !== valueToDelete);
// //                     if (newValues.length > 0) {
// //                       return { ...label, values: newValues };
// //                     }
// //                     return null;
// //                   }
// //                   return label;
// //                 })
// //                 .filter((label): label is LabelFilterPair => label !== null);

// //               return { ...service, label: updatedLabels };
// //             }
// //             return service;
// //           })
// //         );
// //       };

// //       const handleAddService = useCallback(() => {
// //         const newService = { id: `service_${Date.now()}`, ...getInitialService() };
// //         setServices(prev => [...prev, newService]);
// //       }, [getInitialService, setServices]);

// //       const handleDeleteService = useCallback((idToDelete: string) => {
// //         setServices(p => p.filter(s => s.id !== idToDelete));
// //       }, [setServices]);

// //       const handleLabelMenuOpen = (event: React.MouseEvent<HTMLElement>, serviceId: string) => {
// //           setLabelMenuAnchor(event.currentTarget);
// //           setOpenPopper({ type: 'label', serviceId });
// //       };

// //       const handleLabelMenuClose = () => {
// //         setOpenPopper(null);
// //         setLabelMenuAnchor(null);
// //       };

// //       const handleLabelApply = (newFilters: LabelFilterPair[]) => {
// //           if (openPopper?.type === 'label') {
// //               handleUpdateService(openPopper.serviceId, 'label', newFilters);
// //           }
// //           handleLabelMenuClose();
// //       };

// //       const activeServiceForLabel = services.find(s => s.id === openPopper?.serviceId);

// //     const renderMultiSelectPopper = () => {
// //       if (!openPopper || !popperAnchor) return null;
// //       const checkboxStyle = { '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: '#0064FF' } };

// //       if (openPopper.type === 'dataSources') {
// //         const options = dataSourceOptions.map(s => s.id);
// //         const optionLabels = dataSourceOptions;
// //         const isAllSelected = tempSelections.length === options.length && options.length > 0;
// //         const isSomeSelected = tempSelections.length > 0 && !isAllSelected;
// //         const handleSelectAll = () => setTempSelections(isAllSelected ? [] : options);
// //         return (
// //           <Popper open={true} anchorEl={popperAnchor} placement="bottom-start" transition sx={{zIndex: 1200}} modifiers={[{name: 'offset', options: {offset: [0, 8]}}]}>
// //             {({ TransitionProps }) => (
// //                 <Grow {...TransitionProps} style={{ transformOrigin: 'top left' }}>
// //                     <Paper ref={popperPaperRef} sx={{ width: popperAnchor?.offsetWidth, borderRadius: '12px', backgroundColor: '#FFFFFF', boxShadow: '0 4px 20px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
// //                         <ClickAwayListener onClickAway={handlePopperClose}>
// //                             <Box>
// //                                 <FormControlLabel
// //                                     label={<Typography sx={{ fontSize: '13px', color: isAllSelected ? '#333333' : '#666666' }}>All</Typography>}
// //                                     sx={{width: '100%', m:0, px: 2, py: 0.5}}
// //                                     control={ <Checkbox size="small" checked={isAllSelected} indeterminate={isSomeSelected} onChange={handleSelectAll} sx={checkboxStyle}/> }
// //                                 />
// //                                 <Box sx={{ maxHeight: '140px', overflow: 'auto' }}>
// //                                   {optionLabels.map(option => (
// //                                     <Tooltip
// //                                       key={option.id}
// //                                       title={option.isSyncing ? "This data source is currently syncing and cannot be selected." : ""}
// //                                       placement="right"
// //                                       arrow
// //                                     >
// //                                       <div>
// //                                         <FormControlLabel
// //                                             sx={{width: '100%', m:0, px:2, py:0.5}}
// //                                             disabled={option.isSyncing}
// //                                             control={
// //                                               <Checkbox
// //                                                 size="small"
// //                                                 checked={tempSelections.includes(option.id)}
// //                                                 onChange={() => setTempSelections(p => p.includes(option.id) ? p.filter(i => i !== option.id) : [...p, option.id])}
// //                                                 sx={checkboxStyle}
// //                                               />
// //                                             }
// //                                             label={
// //                                               <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
// //                                                 <Typography sx={{ fontSize: '13px', color: tempSelections.includes(option.id) ? '#333333' : '#666666' }}>
// //                                                   {option.name}
// //                                                 </Typography>
// //                                                 {option.isSyncing && (
// //                                                   <Box component="span" sx={{display: 'inline-flex', alignItems: 'center', height: '18px', px: 0.8, borderRadius: '10px', backgroundColor: 'rgba(255, 193, 7, 0.2)', color: 'rgba(180, 120, 0)', fontSize: '11px', fontWeight: 500 }}>
// //                                                     Syncing
// //                                                   </Box>
// //                                                 )}
// //                                               </Box>
// //                                             }
// //                                         />
// //                                       </div>
// //                                     </Tooltip>
// //                                   ))}
// //                                 </Box>
// //                                 <Divider />
// //                                 <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', p: 1 }}>
// //                                     <Button onClick={() => setTempSelections([])} sx={{ textTransform: 'none', color: '#999999' }}>Clear</Button>
// //                                     <Button onClick={handleApply} sx={{ textTransform: 'none', color: '#0064FF' }}>Apply</Button>
// //                                 </Box>
// //                             </Box>
// //                         </ClickAwayListener>
// //                     </Paper>
// //                 </Grow>
// //             )}
// //         </Popper>
// //         );
// //       }

// //     if (openPopper.type === 'projectIds') {
// //           const options = projectOptions;
// //           const isAllSelected = tempSelections.length === options.length && options.length > 0;
// //           const isSomeSelected = tempSelections.length > 0 && !isAllSelected;
// //           const handleSelectAll = () => setTempSelections(isAllSelected ? [] : options);
// //           return (
// //               <Popper open={true} anchorEl={popperAnchor} placement="bottom-start" transition sx={{zIndex: 1200}} modifiers={[{name: 'offset', options: {offset: [0, 8]}}]}>
// //                   {({ TransitionProps }) => (
// //                       <Grow {...TransitionProps} style={{ transformOrigin: 'top left' }}>
// //                           <Paper ref={popperPaperRef} sx={{ width: popperAnchor?.offsetWidth, borderRadius: '12px', backgroundColor: '#FFFFFF', boxShadow: '0 4px 20px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
// //                               <ClickAwayListener onClickAway={handlePopperClose}>
// //                                   <Box>
// //                                       <FormControlLabel
// //                                           label={<Typography sx={{ fontSize: '13px', color: isAllSelected ? '#333333' : '#666666' }}>All</Typography>}
// //                                           sx={{width: '100%', m:0, px: 2, py: 0.5}}
// //                                           control={ <Checkbox size="small" checked={isAllSelected} indeterminate={isSomeSelected} onChange={handleSelectAll} sx={checkboxStyle}/> }
// //                                       />
// //                                       <Box sx={{ maxHeight: '140px', overflow: 'auto' }}>
// //                                           {options.map(option => (
// //                                               <FormControlLabel
// //                                                   key={option}
// //                                                   sx={{width: '100%', m:0, px:2, py:0.5}}
// //                                                   control={ <Checkbox size="small" checked={tempSelections.includes(option)} onChange={() => setTempSelections(p => p.includes(option) ? p.filter(i => i !== option) : [...p, option])} sx={checkboxStyle}/> }
// //                                                   label={<Typography sx={{ fontSize: '13px', color: tempSelections.includes(option) ? '#333333' : '#666666' }}>{option}</Typography>}
// //                                               />
// //                                           ))}
// //                                       </Box>
// //                                       <Divider />
// //                                       <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', p: 1 }}>
// //                                           <Button onClick={() => setTempSelections([])} sx={{ textTransform: 'none', color: '#999999' }}>Clear</Button>
// //                                           <Button onClick={handleApply} sx={{ textTransform: 'none', color: '#0064FF' }}>Apply</Button>
// //                                       </Box>
// //                                   </Box>
// //                               </ClickAwayListener>
// //                           </Paper>
// //                       </Grow>
// //                   )}
// //               </Popper>
// //           );
// //       }

// //     if (openPopper.type === 'regions') {
// //         const filteredActive = activeRegions.filter(r => r.toLowerCase().includes(regionSearchTerm.toLowerCase()));
// //         const filteredAvailable = availableRegions.filter(r => r.toLowerCase().includes(regionSearchTerm.toLowerCase()));
// //         const handleRegionToggle = (value: string) => {
// //             const newSelection = tempSelections.includes(value)
// //                 ? tempSelections.filter(v => v !== value)
// //                 : [...tempSelections, value];
// //             setTempSelections(newSelection);
// //         };
// //         const handleSelectAll = (itemsToToggle: string[], shouldSelect: boolean) => {
// //             const otherSelections = tempSelections.filter(s => !itemsToToggle.includes(s));
// //             const newSelections = shouldSelect
// //                 ? Array.from(new Set([...otherSelections, ...itemsToToggle]))
// //                 : otherSelections;
// //             setTempSelections(newSelections);
// //         };
// //         const isAllActiveSelected = filteredActive.length > 0 && filteredActive.every(r => tempSelections.includes(r));
// //         const isSomeActiveSelected = filteredActive.some(r => tempSelections.includes(r)) && !isAllActiveSelected;
// //         const isAllAvailableSelected = filteredAvailable.length > 0 && filteredAvailable.every(r => tempSelections.includes(r));
// //         const isSomeAvailableSelected = filteredAvailable.some(r => tempSelections.includes(r)) && !isAllAvailableSelected;
// //         const renderRegionOption = (option: string) => (
// //             <FormControlLabel
// //                 key={option}
// //                 sx={{ width: '100%', m: 0, px: 2, py: 0.5 }}
// //                 control={<Checkbox size="small" checked={tempSelections.includes(option)} onChange={() => handleRegionToggle(option)} sx={checkboxStyle}/>}
// //                 label={<Typography sx={{ fontSize: '13px', color: tempSelections.includes(option) ? '#333333' : '#666666' }}>{option}</Typography>}
// //             />
// //         );
// //         return (
// //             <Popper open={true} anchorEl={popperAnchor} placement="bottom-start" transition sx={{zIndex: 1200}} modifiers={[{name: 'offset', options: {offset: [0, 8]}}]}>
// //                 {({ TransitionProps }) => (
// //                     <Grow {...TransitionProps} style={{ transformOrigin: 'top left' }}>
// //                         <Paper ref={popperPaperRef} sx={{ width: popperAnchor?.offsetWidth, borderRadius: '12px', backgroundColor: '#FFFFFF', boxShadow: '0 4px 20px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
// //                             <ClickAwayListener onClickAway={handlePopperClose}>
// //                                 <Box>
// //                                     <Box sx={{ p: 2, borderBottom: '1px solid #f0f0f0' }}>
// //                                         <TextField
// //                                             autoFocus placeholder="Search by region" value={regionSearchTerm}
// //                                             onChange={(e) => setRegionSearchTerm(e.target.value)}
// //                                             onKeyDown={(e) => e.stopPropagation()} fullWidth variant="outlined"
// //                                             InputProps={{
// //                                                 startAdornment: (
// //                                                     <InputAdornment position="start" sx={{ ml: -0.5 }}>
// //                                                     <img src="/icons/search_icon.svg" alt="Search" style={{ width: '16px', height: '16px' }} />
// //                                                     </InputAdornment>
// //                                                 ),
// //                                                 sx: { height: '32px', borderRadius: '10px', backgroundColor: 'rgba(0, 0, 0, 0.04)', '& .MuiOutlinedInput-notchedOutline': { border: 'none' }, '& .MuiInputBase-input::placeholder': { fontSize: '14px', fontWeight: 300 } },
// //                                             }}
// //                                         />
// //                                     </Box>
// //                                     <Box sx={{ maxHeight: '200px', overflowY: 'auto' }}>
// //                                         {filteredActive.length > 0 && (
// //                                             <>
// //                                                 <ListSubheader sx={{ bgcolor: '#F9FAFB', lineHeight: '32px', fontSize: '13px', fontWeight: 600 }}>Active Regions</ListSubheader>
// //                                                 <FormControlLabel
// //                                                     sx={{ width: '100%', m: 0, px: 2, py: 0.5 }}
// //                                                     label={<Typography sx={{ fontSize: '13px', fontWeight: 500 }}>All</Typography>}
// //                                                     control={<Checkbox size="small" checked={isAllActiveSelected} indeterminate={isSomeActiveSelected} onChange={(e) => handleSelectAll(filteredActive, e.target.checked)} sx={checkboxStyle}/>}
// //                                                 />
// //                                                 {filteredActive.map(renderRegionOption)}
// //                                             </>
// //                                         )}
// //                                         {filteredAvailable.length > 0 && (
// //                                             <>
// //                                                 <ListSubheader sx={{ bgcolor: '#F9FAFB', lineHeight: '32px', fontSize: '13px', fontWeight: 600 }}>Available Regions</ListSubheader>
// //                                                 <FormControlLabel
// //                                                     sx={{ width: '100%', m: 0, px: 2, py: 0.5 }}
// //                                                     label={<Typography sx={{ fontSize: '13px', fontWeight: 500 }}>All</Typography>}
// //                                                     control={<Checkbox size="small" checked={isAllAvailableSelected} indeterminate={isSomeAvailableSelected} onChange={(e) => handleSelectAll(filteredAvailable, e.target.checked)} sx={checkboxStyle}/>}
// //                                                 />
// //                                                 {filteredAvailable.map(renderRegionOption)}
// //                                             </>
// //                                         )}
// //                                     </Box>
// //                                     <Divider />
// //                                     <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', p: 1 }}>
// //                                         <Button onClick={() => setTempSelections([])} sx={{ textTransform: 'none', color: '#999999' }}>Clear</Button>
// //                                         <Button onClick={handleApply} sx={{ textTransform: 'none', color: '#0064FF' }}>Apply</Button>
// //                                     </Box>
// //                                 </Box>
// //                             </ClickAwayListener>
// //                         </Paper>
// //                     </Grow>
// //                 )}
// //             </Popper>
// //         );
// //     }

// //       return null;
// //     };

// //     return (
// //         <Tooltip title={disabled ? "Please complete Budget Details before configuring scope." : ""}>
// //             <Box sx={{ p: { xs: 1, sm: 2 }, bgcolor: '#FFFFFF', borderRadius: '10px', border: '1px solid #F3F3F3', position: 'relative' }}>
// //                 {disabled && (
// //                   <Box sx={{
// //                       position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
// //                       backgroundColor: 'rgba(255, 255, 255, 0.7)', zIndex: 1, cursor: 'not-allowed'
// //                   }} />
// //               )}
// //             <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
// //               <img src="/icons/Scope-Config.svg" alt="Scope Configuration" style={{ marginRight: '12px', width: '28px', height: '28px' }} />
// //               <Typography variant="h6" sx={{ fontWeight: 500, fontSize: '14px', color: '#333333', fontFamily: 'Inter' }}>Scope Configuration</Typography>
// //             </Box>
// //             <Divider sx={{ borderColor: '#F3F3F3', mx: -3 }} />

// //             <Box sx={{ display: 'flex', gap: 2, mt: 2, px: 1 }}>
// //               <Box
// //                 sx={providerBoxStyle(provider === 'GCP', false)}
// //                 onClick={() => {
// //                   if (disabled) return;
// //                   setProvider('GCP');
// //                   if(services.length === 0) {
// //                     setServices([ { id: `service_${Date.now()}`, ...getInitialService() } ])
// //                   };
// //                 }}
// //               >
// //                 <Box sx={{ display: 'flex', alignItems: 'center' }}><img src="/icons/google-cloud-icon-2048x1646-7admxejz.svg" alt="GCP" style={{ marginRight: 8, width: 20, height: 20 }} /><Typography variant="body2" sx={{ fontFamily: 'Inter', fontSize: '13px', fontWeight: 400 }}>Google Cloud Platform</Typography></Box>
// //                 <Radio checked={provider === 'GCP'} size="small" sx={{ p: 0, transform: 'scale(0.85)', '&.Mui-checked': { color: '#0064FF' } }}/>
// //               </Box>
// //               <Box sx={providerBoxStyle(false, true)}><Box sx={{ display: 'flex', alignItems: 'center' }}><img src="/icons/aws.svg" alt="AWS" style={{ marginRight: 8, width: 20, height: 20 }}/><Typography variant="body2" sx={{ fontFamily: 'Inter', fontSize: '13px', fontWeight: 400 }}>Amazon Web Services</Typography></Box><Radio checked={false} size="small" disabled={true} sx={{ p: 0, transform: 'scale(0.85)' }}/></Box>
// //               <Box sx={providerBoxStyle(false, true)}><Box sx={{ display: 'flex', alignItems: 'center' }}><img src="/icons/azure2.svg" alt="Azure" style={{ marginRight: 8, width: 20, height: 20 }}/><Typography variant="body2" sx={{ fontFamily: 'Inter', fontSize: '13px', fontWeight: 400 }}>Microsoft Azure</Typography></Box><Radio checked={false} size="small" disabled={true} sx={{ p: 0, transform: 'scale(0.85)' }}/></Box>
// //             </Box>
// //             {errors.provider && <Typography color="error" variant="caption" sx={{ display: 'block', mt: 1, px: 1 }}>{errors.provider}</Typography>}

// //             {provider === 'GCP' && (
// //               <Box sx={{ width: '100%', bgcolor: '#F9FAFB', borderRadius: '10px', mt: 2, p: 2 }}>
// //                 <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
// //                   <Button onClick={handleAddService} disabled={services.length > 0 && services[services.length-1].serviceTypes.length === 0} sx={{ textTransform: 'none', color: '#0064FF', fontFamily: 'Inter', fontWeight: 500, fontSize: '14px' }}>
// //                     + Add More
// //                   </Button>
// //                 </Box>

// //                 {services.map((service, index) => {
// //                   const isProjectDisabled = service.dataSources.length === 0 || optionsLoading;
// //                   const isRegionDisabled = service.projectIds.length === 0 || optionsLoading;
// //                   const isServiceDisabled = service.regions.length === 0 || optionsLoading;

// //                   const dataSourceButtonText = service.dataSources.length === dataSourceOptions.length && dataSourceOptions.length > 0 ? 'All' : service.dataSources.length > 0 ? `${service.dataSources.length} selected` : 'Select data sources';
// //                   const projectButtonText = service.projectIds.length === projectOptions.length && projectOptions.length > 0 ? 'All' : service.projectIds.length > 0 ? `${service.projectIds.length} selected` : 'Select projects';
// //                   const allRegionsCount = activeRegions.length + availableRegions.length;
// //                   const regionButtonText = service.regions.length === allRegionsCount && allRegionsCount > 0 ? 'All' : service.regions.length > 0 ? `${service.regions.length} selected` : 'Select regions';
// //                   const labelButtonText = service.label.length === 1 ? '1 filter applied' : service.label.length > 0 ? `${service.label.length} filters applied` : 'Select Label';

// //                   const getButtonSx = (hasSelection: boolean) => ({
// //                       width: '100%', height: '36px', backgroundColor: '#FFFFFF', border: `1px solid #E0E0E0`, borderRadius: '8px',
// //                       fontFamily: 'Inter', fontWeight: hasSelection ? 500 : 400, fontSize: '13px',
// //                       color: hasSelection ? '#333333' : '#999999', lineHeight: '18px', letterSpacing: '0%',
// //                       textTransform: 'none', justifyContent: 'space-between', '&:hover': { borderColor: '#0064FF' },
// //                       '&.Mui-disabled': { backgroundColor: '#F3F3F3', border: '1px solid #D3D3D3' }
// //                   });

// //                   const showLabelButton = service.serviceTypes.length > 0;
// //                   const isKubeSelected = service.serviceTypes.includes('Kubernetes Engine');
// //                   const showFullKubeFields = isKubeSelected && activeServices.includes('Kubernetes Engine');
// //                   return (
// //                     <Box key={service.id}>
// //                       <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: 2, alignItems: 'flex-end' }}>
// //                         <Box>
// //                           <Typography sx={{ fontFamily: 'Inter', fontWeight: 400, fontSize: '13px', color: optionsLoading ? '#A0A0A0' : '#333333', mb: 0.5 }}>Data Source<span style={{ color: 'red' }}>*</span></Typography>
// //                           <Button ref={el => buttonRefs.current[`${service.id}-dataSources`] = el} onClick={(e) => handlePopperOpen(e, 'dataSources', service.id, service.dataSources)} endIcon={<ExpandMoreIcon />} sx={getButtonSx(service.dataSources.length > 0)} disabled={optionsLoading}>{dataSourceButtonText}</Button>
// //                         </Box>

// //                         <Box>
// //                           <Typography sx={{ fontFamily: 'Inter', fontWeight: 400, fontSize: '13px', color: isProjectDisabled ? '#A0A0A0' : '#333333', mb: 0.5 }}>Project<span style={{ color: 'red' }}>*</span></Typography>
// //                           <Button
// //                             ref={el => buttonRefs.current[`${service.id}-projectIds`] = el}
// //                             disabled={isProjectDisabled}
// //                             onClick={(e) => handlePopperOpen(e, 'projectIds', service.id, service.projectIds)}
// //                             endIcon={<ExpandMoreIcon />}
// //                             sx={getButtonSx(service.projectIds.length > 0)}
// //                           >
// //                             {projectButtonText}
// //                           </Button>
// //                         </Box>

// //                         <Box>
// //                           <Typography sx={{ fontFamily: 'Inter', fontWeight: 400, fontSize: '13px', color: isRegionDisabled ? '#A0A0A0' : '#333333', mb: 0.5 }}>Region<span style={{ color: 'red' }}>*</span></Typography>
// //                           <Button ref={el => buttonRefs.current[`${service.id}-regions`] = el} disabled={isRegionDisabled} onClick={(e) => handlePopperOpen(e, 'regions', service.id, service.regions)} endIcon={<ExpandMoreIcon />} sx={getButtonSx(service.regions.length > 0)}>{regionButtonText}</Button>
// //                         </Box>

// //                         <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
// //                            <Box sx={{flexGrow: 1}}>
// //                               <SectionedMultiSelectDropdown
// //                                   ref={el => buttonRefs.current[`${service.id}-service`] = el}
// //                                   label="Service"
// //                                   options={{ active: activeServices, available: availableServices }}
// //                                   selectedValues={service.serviceTypes}
// //                                   onChange={(newValues) => handleUpdateService(service.id, 'serviceTypes', newValues)}
// //                                   disabled={isServiceDisabled}
// //                               />
// //                            </Box>
// //                           <IconButton onClick={() => handleDeleteService(service.id)} disabled={services.length <= 1} sx={{ mb: '4px' }}><img src={(services.length <= 1) ? "/icons/delete_icon_disabled.svg" : "/icons/delete_icon.svg"} alt="Delete Service" style={{ width: '17px', height: '17px' }} /></IconButton>
// //                         </Box>

// //                         {showLabelButton && (
// //                           <Box>
// //                             <Typography sx={{ fontFamily: 'Inter', fontWeight: 400, fontSize: '13px', color: isServiceDisabled ? '#A0A0A0' : '#333333', mb: 0.5 }}>Label</Typography>
// //                             <Button onClick={(e) => handleLabelMenuOpen(e, service.id)} disabled={isServiceDisabled} endIcon={<ExpandMoreIcon />} variant="outlined" sx={getButtonSx(service.label.length > 0)}>{labelButtonText}</Button>
// //                           </Box>
// //                         )}

// //                         {showFullKubeFields && (
// //                           <>
// //                             <Box>
// //                               <MultiSelectDropdown label="Cluster" options={k8sClusters} selectedValues={service.cluster} onChange={(newValues: string[]) => handleUpdateService(service.id, 'cluster', newValues)} disabled={isServiceDisabled} width="100%" />
// //                             </Box>
// //                             <Box>
// //                               <MultiSelectDropdown label="Namespace" options={k8sNamespaces} selectedValues={service.namespace} onChange={(newValues: string[]) => handleUpdateService(service.id, 'namespace', newValues)} disabled={isServiceDisabled} width="100%" />
// //                             </Box>
// //                           </>
// //                         )}
// //                       </Box>
// //                       {service.label.length > 0 && (
// //                         <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
// //                           {service.label.flatMap(labelItem =>
// //                             labelItem.values.map(value => (
// //                               <Chip
// //                                 key={`${labelItem.key}-${value}`}
// //                                 label={`${labelItem.key}: ${value}`}
// //                                 size="small"
// //                                 onDelete={() => handleDeleteLabelChip(service.id, labelItem.key, value)}
// //                                 sx={{
// //                                   backgroundColor: ' #F3F8FE',
// //                                   color: '#333333',
// //                                   fontFamily: 'Inter',
// //                                   fontSize: '12px',
// //                                   fontWeight: 500,
// //                                   borderRadius: '8px',
// //                                   '.MuiChip-deleteIcon': {
// //                                       color: '#333333',
// //                                       fontSize: '16px',
// //                                       '&:hover': {
// //                                         color: 'red',
// //                                       },
// //                                   },
// //                                 }}
// //                               />
// //                             ))
// //                           )}
// //                         </Box>
// //                       )}
// //                       {index < services.length - 1 && <Divider sx={{ my: 2, mx: -2 }} />}
// //                     </Box>
// //                   )
// //                 })}

// //                 {renderMultiSelectPopper()}

// //                 <LabelFilterMenu
// //                   open={openPopper?.type === 'label'}
// //                   anchorEl={labelMenuAnchor}
// //                   onClose={handleLabelMenuClose}
// //                   onApply={handleLabelApply}
// //                   appliedFilters={activeServiceForLabel?.label || []}
// //                   labelKeys={labelKeys}
// //                   labelValues={labelValuesData}
// //                   onFetchLabelValues={handleFetchLabelValues}
// //                   loadingLabelKey={loadingLabelKey}
// //                 />
// //               </Box>
// //             )}
// //             </Box>
// //         </Tooltip>
// //     );
// // };

// // export default ScopeConfig;



// import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
// import { useLazyQuery, gql } from '@apollo/client';
// import { useOutletContext } from 'react-router-dom';
// import {
//   Box, Typography, Radio, Divider, Button, Popper, Grow, Paper, Checkbox, FormControlLabel,
//   ClickAwayListener, IconButton, ListSubheader, TextField, InputAdornment, Tooltip, Chip
// } from '@mui/material';
// import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
// import ExpandLessIcon from '@mui/icons-material/ExpandLess';
// import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
// import LabelFilterMenu, { LabelFilterPair } from './LabelFilterMenu';

// // --- GraphQL Query Definitions ---
// const GET_BUDGET_FILTER_OPTIONS = gql`
//   query GetBudgetFilterOptions($input: BudgetFilterOptionsInput!) {
//     getBudgetFilterOptions(input: $input) {
//       dataSources { id, name, providerType, isSyncing }
//       activeRegions, availableRegions, activeServices, availableServices, projectIds
//       labelKeys, k8sClusters, k8sNamespaces
//     }
//   }
// `;

// const GET_LABEL_VALUES = gql`
//   query GetBudgetLabelValues($input: BudgetLabelValuesInput!) {
//     getBudgetLabelValues(input: $input) {
//       values
//     }
//   }
// `;

// // --- Interfaces and Helper Components ---
// export interface ServiceState {
//   id: string;
//   dataSources: string[];
//   projectIds: string[];
//   regions: string[];
//   serviceTypes: string[];
//   label: LabelFilterPair[];
//   cluster: string[];
//   namespace: string[];
//   deployment: string[];
// }

// interface ScopeConfigProps {
//     provider: string;
//     setProvider: (provider: string) => void;
//     services: ServiceState[];
//     setServices: React.Dispatch<React.SetStateAction<ServiceState[]>>;
//     getInitialService: () => Omit<ServiceState, 'id' | 'serviceTypes'> & { serviceTypes: string[] };
//     errors: { [key: string]: string };
//     disabled?: boolean;
//     period: 'Monthly' | 'Quarterly' | 'Yearly';
// }

// interface AppOutletContext {
//   currentTenant?: { id: string; name: string; };
// }

// interface DataSourceOption {
//   id: string;
//   name: string;
//   providerType: string;
//   isSyncing: boolean;
// }

// const SectionedMultiSelectDropdown = React.forwardRef<
//   HTMLButtonElement,
//   {
//     label: string;
//     options: { active: string[]; available: string[] };
//     selectedValues: string[];
//     onChange: (selected: string[]) => void;
//     disabled?: boolean;
//   }
// >(({ label, options, selectedValues, onChange, disabled = false }, ref) => {
//   const [isOpen, setIsOpen] = useState(false);
//   const [searchTerm, setSearchTerm] = useState('');
//   const anchorRef = useRef<HTMLButtonElement>(null);

//   const handleRef = useCallback((node: HTMLButtonElement) => {
//     (anchorRef as React.MutableRefObject<HTMLButtonElement | null>).current = node;
//     if (typeof ref === 'function') {
//       ref(node);
//     } else if (ref) {
//       (ref as React.MutableRefObject<HTMLButtonElement | null>).current = node;
//     }
//   }, [ref]);

//   const handleOpen = () => setIsOpen(true);
//   const handleClose = () => {
//     setIsOpen(false);
//     setSearchTerm('');
//   };

//   const filteredActive = useMemo(() => options.active.filter(opt => opt.toLowerCase().includes(searchTerm.toLowerCase())), [options.active, searchTerm]);
//   const filteredAvailable = useMemo(() => options.available.filter(opt => opt.toLowerCase().includes(searchTerm.toLowerCase())), [options.available, searchTerm]);
  
//   const isKubeSelected = useMemo(() => selectedValues.includes('Kubernetes Engine'), [selectedValues]);

//   // Check if any *non-Kube* active service is selected.
//   const isAnyActiveSelected = useMemo(() => options.active.some(opt => selectedValues.includes(opt) && opt !== 'Kubernetes Engine'), [options.active, selectedValues]);
//   // Check if any *non-Kube* available service is selected.
//   const isAnyAvailableSelected = useMemo(() => options.available.some(opt => selectedValues.includes(opt) && opt !== 'Kubernetes Engine'), [options.available, selectedValues]);

//   const nonKubeFilteredActive = useMemo(() => filteredActive.filter(s => s !== 'Kubernetes Engine'), [filteredActive]);
//   const nonKubeFilteredAvailable = useMemo(() => filteredAvailable.filter(s => s !== 'Kubernetes Engine'), [filteredAvailable]);

//   const isAllActiveSelected = nonKubeFilteredActive.length > 0 && nonKubeFilteredActive.every(s => selectedValues.includes(s));
//   const isSomeActiveSelected = nonKubeFilteredActive.some(s => selectedValues.includes(s)) && !isAllActiveSelected;

//   const isAllAvailableSelected = nonKubeFilteredAvailable.length > 0 && nonKubeFilteredAvailable.every(s => selectedValues.includes(s));
//   const isSomeAvailableSelected = nonKubeFilteredAvailable.some(s => selectedValues.includes(s)) && !isAllAvailableSelected;

//   const handleToggle = (value: string) => {
//     // Rule 1: Kubernetes Engine is exclusive.
//     if (value === 'Kubernetes Engine') {
//       onChange(isKubeSelected ? [] : ['Kubernetes Engine']);
//       return;
//     }
//     if (isKubeSelected) return; // If K8s is selected, no other service can be selected.

//     const isTogglingActive = options.active.includes(value);

//     // Rule 2: If selecting from one group while the other is active, reset with the new selection.
//     if (isTogglingActive && isAnyAvailableSelected) {
//       onChange([value]);
//       return;
//     }
//     if (!isTogglingActive && isAnyActiveSelected) {
//       onChange([value]);
//       return;
//     }

//     // Rule 3: Standard toggle within the same group.
//     const newSelected = selectedValues.includes(value)
//       ? selectedValues.filter(v => v !== value)
//       : [...selectedValues, value];
//     onChange(newSelected);
//   };

//   const handleSelectAll = (itemsToToggle: string[], isSelecting: boolean) => {
//     // This logic correctly replaces the selection with the chosen group or clears it.
//     if (isSelecting) {
//       const nonKubeItems = itemsToToggle.filter(v => v !== 'Kubernetes Engine');
//       onChange(nonKubeItems);
//     } else {
//       onChange([]);
//     }
//   };

//   const allOptions = [...options.active, ...options.available];
  
//   const buttonText =
//     selectedValues.length === 0 ? `Select ${label.toLowerCase()}`
//     : selectedValues.length === allOptions.length && allOptions.length > 0 ? 'All selected'
//     : `${selectedValues.length} selected`;

//     const renderOption = (option: string) => {
//         const isThisOptionActive = options.active.includes(option);
//         const isThisOptionAvailable = options.available.includes(option);
        
//         // --- FIX: Corrected the logic to allow multi-selection within a group ---
//         const isDisabled = 
//             // K8s is selected, disable others.
//             (isKubeSelected && option !== 'Kubernetes Engine') || 
//             // Another service is selected, disable K8s.
//             ((isAnyActiveSelected || isAnyAvailableSelected) && option === 'Kubernetes Engine') ||
//             // An active service is selected, disable all available services.
//             (isThisOptionAvailable && isAnyActiveSelected) ||
//             // An available service is selected, disable all active services. (This was the bug)
//             (isThisOptionActive && isAnyAvailableSelected);

//         return (
//           <FormControlLabel
//               key={option}
//               sx={{ width: '100%', m: 0, px: 2, py: 0.5 }}
//               control={
//                   <Checkbox
//                       size="small"
//                       checked={selectedValues.includes(option)}
//                       onChange={() => handleToggle(option)}
//                       disabled={isDisabled}
//                       sx={{ '&.Mui-checked': { color: '#0064FF' } }}
//                   />
//               }
//               label={
//                 <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
//                   <Typography sx={{ fontSize: '13px', color: isDisabled ? '#B0B0B0' : selectedValues.includes(option) ? '#333333' : '#666666' }}>
//                     {option}
//                   </Typography>
//                   {option === 'Kubernetes Engine' && isDisabled && (
//                     <Tooltip
//                       title="As you have selected other services, Kubernetes Engine is disabled. To select it, please clear other services."
//                       arrow
//                       componentsProps={{
//                         tooltip: {
//                           sx: { backgroundColor: '#FFFFFF', color: '#000000', border: '1px solid #E0E0E0', fontSize: '12px', },
//                         },
//                         arrow: { sx: { color: '#FFFFFF', }, },
//                       }}
//                     >
//                       <InfoOutlinedIcon sx={{ color: '#0064FF', ml: 1, fontSize: '16px', }} />
//                     </Tooltip>
//                   )}
//                 </Box>
//               }
//           />
//         );
//       };

//   return (
//     <Box sx={{ width: '100%' }}>
//       <Typography sx={{ fontFamily: 'Inter', fontWeight: 400, fontSize: '13px', color: disabled ? '#A0A0A0' : '#333333', mb: 0.5 }}>
//         {label}<span style={{ color: 'red' }}>*</span>
//       </Typography>
//       <Button
//         ref={handleRef} variant="outlined" disabled={disabled} onClick={handleOpen}
//         endIcon={isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
//         sx={{
//           width: '100%', height: '36px', backgroundColor: disabled ? '#F3F3F3' : '#FFFFFF',
//           border: `1px solid ${disabled ? '#D3D3D3' : '#E0E0E0'}`, borderRadius: '8px',
//           fontFamily: 'Inter', fontWeight: selectedValues.length > 0 ? 500 : 400, fontSize: '13px',
//           color: selectedValues.length > 0 ? '#333333' : '#999999', lineHeight: '18px',
//           letterSpacing: '0%', textTransform: 'none', justifyContent: 'space-between',
//           '&:hover': { borderColor: disabled ? '#D3D3D3' : '#0064FF' },
//         }}
//       >
//         {buttonText}
//       </Button>
//       <Popper
//         open={isOpen} anchorEl={anchorRef.current} placement="bottom-start"
//         transition sx={{ zIndex: 1300, width: anchorRef.current?.offsetWidth }}
//       >
//         {({ TransitionProps }) => (
//           <ClickAwayListener onClickAway={handleClose}>
//             <Grow {...TransitionProps} style={{ transformOrigin: 'top left' }}>
//               <Paper sx={{ borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.1)', maxHeight: '300px', display: 'flex', flexDirection: 'column' }}>
//                 <Box sx={{ p: 2, borderBottom: '1px solid #f0f0f0' }}>
//                     <TextField
//                         autoFocus placeholder="Search by service name.." value={searchTerm}
//                         onChange={(e) => setSearchTerm(e.target.value)}
//                         onKeyDown={(e) => e.stopPropagation()} fullWidth variant="outlined"
//                         InputProps={{
//                             startAdornment: (
//                                 <InputAdornment position="start" sx={{ ml: -0.5 }}>
//                                 <img src="/icons/search_icon.svg" alt="Search" style={{ width: '16px', height: '16px' }} />
//                                 </InputAdornment>
//                             ),
//                             sx: { height: '32px', borderRadius: '10px', backgroundColor: 'rgba(0, 0, 0, 0.04)', '& .MuiOutlinedInput-notchedOutline': { border: 'none' }, '& .MuiInputBase-input::placeholder': { fontSize: '14px', fontWeight: 300 } },
//                         }}
//                     />
//                 </Box>
//                 <Box sx={{ overflowY: 'auto' }}>
//                     {filteredActive.length === 0 && filteredAvailable.length === 0 ? (
//                         <Typography sx={{ p: 2, color: '#999', fontSize: '13px', textAlign: 'center' }}>No results found</Typography>
//                     ) : (
//                         <>
//                             {filteredActive.length > 0 && (
//                                 <>
//                                     <ListSubheader sx={{ bgcolor: '#F9FAFB', lineHeight: '32px', fontSize: '13px', fontWeight: 600 }}>Active Services</ListSubheader>
//                                     <FormControlLabel
//                                         sx={{ width: '100%', m: 0, px: 2, py: 0.5 }}
//                                         label={<Typography sx={{ fontSize: '13px' }}>All</Typography>}
//                                         control={<Checkbox size="small" checked={isAllActiveSelected} indeterminate={isSomeActiveSelected} onChange={(e) => handleSelectAll(filteredActive, e.target.checked)} disabled={isKubeSelected || isAnyAvailableSelected} sx={{'&.Mui-checked, &.MuiCheckbox-indeterminate': { color: '#0064FF' }}} />}
//                                     />
//                                     {filteredActive.map(renderOption)}
//                                 </>
//                             )}
//                             {filteredAvailable.length > 0 && (
//                                 <>
//                                     <ListSubheader sx={{ bgcolor: '#F9FAFB', lineHeight: '32px', fontSize: '13px', fontWeight: 600 }}>Available Services</ListSubheader>
//                                     <FormControlLabel
//                                         sx={{ width: '100%', m: 0, px: 2, py: 0.5 }}
//                                         label={<Typography sx={{ fontSize: '13px' }}>All</Typography>}
//                                         control={<Checkbox size="small" checked={isAllAvailableSelected} indeterminate={isSomeAvailableSelected} onChange={(e) => handleSelectAll(filteredAvailable, e.target.checked)} disabled={isKubeSelected || isAnyActiveSelected} sx={{'&.Mui-checked, &.MuiCheckbox-indeterminate': { color: '#0064FF' }}} />}
//                                     />
//                                     {filteredAvailable.map(renderOption)}
//                                 </>
//                             )}
//                         </>
//                     )}
//                 </Box>
//                 <Divider />
//                 <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1, flexShrink: 0 }}>
//                   <Button onClick={() => onChange([])} sx={{ textTransform: 'none', color: '#999999' }}>Clear</Button>
//                   <Button onClick={handleClose} sx={{ textTransform: 'none', color: '#0064FF' }}>Apply</Button>
//                 </Box>
//               </Paper>
//             </Grow>
//           </ClickAwayListener>
//         )}
//       </Popper>
//     </Box>
//   );
// });

// // --- Your other components (MultiSelectDropdown, ScopeConfig, etc.) remain here without changes ---
// const MultiSelectDropdown: React.FC<{
//   label: string;
//   options: string[];
//   selectedValues: string[];
//   onChange: (selected: string[]) => void;
//   disabled?: boolean;
//   width?: string | number;
// }> = ({ label, options, selectedValues, onChange, disabled = false, width = '100%' }) => {
//   const [isOpen, setIsOpen] = useState(false);
//   const anchorRef = useRef<HTMLButtonElement>(null);

//   const handleOpen = () => setIsOpen(true);
//   const handleClose = () => setIsOpen(false);

//   const handleToggle = (value: string) => {
//     const newSelected = selectedValues.includes(value)
//       ? selectedValues.filter((v) => v !== value)
//       : [...selectedValues, value];
//     onChange(newSelected);
//   };

//   const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
//     onChange(event.target.checked ? options : []);
//   };

//   const isAllSelected = options.length > 0 && selectedValues.length === options.length;
//   const buttonText =
//     selectedValues.length === 0 ? `Select ${label.toLowerCase()}`
//     : selectedValues.length === options.length ? 'All selected'
//     : `${selectedValues.length} selected`;

//   return (
//     <Box sx={{ width }}>
//       <Typography sx={{ fontFamily: 'Inter', fontWeight: 400, fontSize: '13px', color: disabled ? '#A0A0A0' : '#333333', mb: 0.5 }}>
//         {label}<span style={{ color: 'red' }}>*</span>
//       </Typography>
//       <Button
//         ref={anchorRef}
//         variant="outlined"
//         disabled={disabled}
//         onClick={handleOpen}
//         endIcon={isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
//         sx={{
//           width: '100%', height: '36px', backgroundColor: disabled ? '#F3F3F3' : '#FFFFFF',
//           border: `1px solid ${disabled ? '#D3D3D3' : '#E0E0E0'}`, borderRadius: '8px',
//           fontFamily: 'Inter',
//           fontWeight: selectedValues.length > 0 ? 500 : 400,
//           fontSize: '13px',
//           color: selectedValues.length > 0 ? '#333333' : '#999999',
//           lineHeight: '18px',
//           letterSpacing: '0%',
//           textTransform: 'none',
//           justifyContent: 'space-between',
//           '&:hover': { borderColor: disabled ? '#D3D3D3' : '#0064FF' },
//         }}
//       >
//         {buttonText}
//       </Button>
//       <Popper
//         open={isOpen}
//         anchorEl={anchorRef.current}
//         placement="bottom-start"
//         transition
//         sx={{ zIndex: 1300, width: anchorRef.current?.offsetWidth }}
//       >
//         {({ TransitionProps }) => (
//           <ClickAwayListener onClickAway={handleClose}>
//             <Grow {...TransitionProps} style={{ transformOrigin: 'top left' }}>
//               <Paper sx={{ borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
//                 <FormControlLabel
//                   label={<Typography sx={{ fontSize: '13px', color: isAllSelected ? '#333333' : '#666666' }}>All</Typography>}
//                   sx={{ width: '100%', m: 0, px: 2, py: 0.5 }}
//                   control={
//                     <Checkbox size="small" checked={isAllSelected}
//                       indeterminate={selectedValues.length > 0 && !isAllSelected}
//                       onChange={handleSelectAll}
//                       sx={{ '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: '#0064FF' } }}
//                     />
//                   }
//                 />
//                 <Box sx={{ maxHeight: '200px', overflowY: 'auto' }}>
//                   {options.map((option) => (
//                     <FormControlLabel
//                       key={option} sx={{ width: '100%', m: 0, px: 2, py: 0.5 }}
//                       control={
//                         <Checkbox size="small" checked={selectedValues.includes(option)}
//                           onChange={() => handleToggle(option)}
//                           sx={{ '&.Mui-checked': { color: '#0064FF' } }}
//                         />
//                       }
//                       label={<Typography sx={{ fontSize: '13px', color: selectedValues.includes(option) ? '#333333' : '#666666' }}>{option}</Typography>}
//                     />
//                   ))}
//                 </Box>
//                 <Divider />
//                 <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
//                   <Button onClick={() => onChange([])} sx={{ textTransform: 'none', color: '#999999' }}>Clear</Button>
//                   <Button onClick={handleClose} sx={{ textTransform: 'none', color: '#0064FF' }}>Apply</Button>
//                 </Box>
//               </Paper>
//             </Grow>
//           </ClickAwayListener>
//         )}
//       </Popper>
//     </Box>
//   );
// };


// // --- Main ScopeConfig Component ---
// const ScopeConfig: React.FC<ScopeConfigProps> = ({
//   provider, setProvider, services, setServices, getInitialService,
//   errors, disabled = false, period,
// }) => {
//     const { currentTenant } = useOutletContext<AppOutletContext>();
//     const [openPopper, setOpenPopper] = useState<{ type: 'dataSources' | 'projectIds' | 'regions' | 'label', serviceId: string } | null>(null);
//     const [tempSelections, setTempSelections] = useState<string[]>([]);
//     const [regionSearchTerm, setRegionSearchTerm] = useState('');

//     const buttonRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({});
//     const [popperAnchor, setPopperAnchor] = useState<HTMLButtonElement | null>(null);
//     const [labelMenuAnchor, setLabelMenuAnchor] = useState<HTMLElement | null>(null);
//     const popperPaperRef = useRef(null);
//     const hasOpenedAutomatically = useRef(false);

//     const [dataSourceOptions, setDataSourceOptions] = useState<DataSourceOption[]>([]);
//     const [projectOptions, setProjectOptions] = useState<string[]>([]);
//     const [activeRegions, setActiveRegions] = useState<string[]>([]);
//     const [availableRegions, setAvailableRegions] = useState<string[]>([]);
//     const [activeServices, setActiveServices] = useState<string[]>([]);
//     const [availableServices, setAvailableServices] = useState<string[]>([]);
//     const [labelKeys, setLabelKeys] = useState<string[]>([]);
//     const [k8sClusters, setK8sClusters] = useState<string[]>([]);
//     const [k8sNamespaces, setK8sNamespaces] = useState<string[]>([]);
//     const [labelValuesData, setLabelValuesData] = useState<{ [key: string]: string[] }>({});
//     const [loadingLabelKey, setLoadingLabelKey] = useState<string | null>(null);

//     const dataSourceDependency = useMemo(() => {
//         const sortedIds = Array.from(new Set(services.flatMap(s => s.dataSources))).sort();
//         return JSON.stringify(sortedIds);
//     }, [services]);

//     const [getOptions, { loading: optionsLoading }] = useLazyQuery(GET_BUDGET_FILTER_OPTIONS, {
//         onCompleted: (data) => {
//             if (data?.getBudgetFilterOptions) {
//                 const {
//                     dataSources,
//                     projectIds,
//                     activeRegions,
//                     availableRegions,
//                     activeServices,
//                     availableServices,
//                     labelKeys,
//                     k8sClusters,
//                     k8sNamespaces
//                 } = data.getBudgetFilterOptions;

//                 setDataSourceOptions(dataSources || []);
//                 setProjectOptions(projectIds || []);
//                 setActiveRegions(activeRegions || []);
//                 setAvailableRegions(availableRegions || []);
//                 setActiveServices(activeServices || []);
//                 setAvailableServices(availableServices || []);
//                 setLabelKeys(labelKeys || []);
//                 setK8sClusters(k8sClusters || []);
//                 setK8sNamespaces(k8sNamespaces || []);
//             }
//         }
//     });

//     const [getLabelValues] = useLazyQuery(GET_LABEL_VALUES, {
//         onCompleted: (data) => {
//             if (loadingLabelKey && data?.getBudgetLabelValues?.values) {
//                 setLabelValuesData(prev => ({ ...prev, [loadingLabelKey]: data.getBudgetLabelValues.values }));
//             }
//             setLoadingLabelKey(null);
//         }
//     });

//     useEffect(() => {
//         if (!provider || !currentTenant?.id || !period) return;
//         const allSelectedDataSources = JSON.parse(dataSourceDependency);
//         getOptions({
//             variables: {
//                 input: {
//                     tenantId: currentTenant.id,
//                     provider,
//                     period: period.toUpperCase(),
//                     dataSourceIds: allSelectedDataSources,
//                 }
//             }
//         });
//     }, [provider, currentTenant, period, dataSourceDependency, getOptions]);

//     useEffect(() => {
//         if (provider === 'GCP' && services.length === 1 && services[0].dataSources.length === 0 &&
//             !disabled && !optionsLoading && !hasOpenedAutomatically.current && dataSourceOptions.length > 0) {
//             const firstServiceId = services[0].id;
//             const dataSourceButton = buttonRefs.current[`${firstServiceId}-dataSources`];
//             if (dataSourceButton) {
//                 setPopperAnchor(dataSourceButton);
//                 setTempSelections(services[0].dataSources);
//                 setOpenPopper({ type: 'dataSources', serviceId: firstServiceId });
//                 hasOpenedAutomatically.current = true;
//             }
//         }
//         if (provider !== 'GCP' || services.length === 0) {
//             hasOpenedAutomatically.current = false;
//         }
//     }, [provider, services, disabled, optionsLoading, dataSourceOptions]);

//     const providerBoxStyle = (isSelected: boolean, isDisabled: boolean) => ({
//       p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
//       backgroundColor: isSelected ? '#F2F7FF' : (isDisabled ? '#F3F3F3' : '#FFFFFF'),
//       border: `1px solid ${isSelected ? '#0064FF' : '#E0E0E0'}`,
//       borderRadius: '8px', cursor: isDisabled ? 'not-allowed' : 'pointer', flex: 1, height: '36px',
//       opacity: isDisabled ? 0.6 : 1,
//       '&:hover': { borderColor: isDisabled ? '#E0E0E0' : '#0064FF' }
//     });

//     const handlePopperOpen = (event: React.MouseEvent<HTMLButtonElement>, type: 'dataSources' | 'projectIds' | 'regions', serviceId: string, currentValues: string[]) => {
//       setPopperAnchor(event.currentTarget);
//       setTempSelections(currentValues);
//       setOpenPopper({ type, serviceId });
//     };

//     const handlePopperClose = () => {
//       setOpenPopper(null);
//       setRegionSearchTerm('');
//       setPopperAnchor(null);
//     };

//     const handleUpdateService = useCallback((serviceId: string, field: keyof Omit<ServiceState, 'id'>, value: any) => {
//         setServices(prev => prev.map(s => {
//             if (s.id === serviceId) {
//                 const updatedService = { ...s, [field]: value };
//                 if (field === 'dataSources') {
//                     updatedService.projectIds = [];
//                     updatedService.regions = [];
//                     updatedService.serviceTypes = [];
//                     updatedService.label = [];
//                     updatedService.cluster = [];
//                     updatedService.namespace = [];
//                 }
//                 if (field === 'projectIds') {
//                     updatedService.regions = [];
//                     updatedService.serviceTypes = [];
//                     updatedService.label = [];
//                     updatedService.cluster = [];
//                     updatedService.namespace = [];
//                 }
//                 if (field === 'regions') {
//                     updatedService.serviceTypes = [];
//                     updatedService.label = [];
//                     updatedService.cluster = [];
//                     updatedService.namespace = [];
//                 }
//                 if (field === 'serviceTypes') {
//                     updatedService.label = [];
//                     if (!value.includes('Kubernetes Engine')) {
//                        updatedService.cluster = [];
//                        updatedService.namespace = [];
//                     }
//                 }
//                 return updatedService;
//             }
//             return s;
//         }));
//     }, [setServices]);

//     const handleApply = () => {
//         if (!openPopper) return;
//         const { serviceId, type } = openPopper;
//         handleUpdateService(serviceId, type as any, tempSelections);
//         handlePopperClose();
//     };

//     const handleFetchLabelValues = useCallback((key: string) => {
//         if (!key || labelValuesData[key] || loadingLabelKey) return;
//         setLoadingLabelKey(key);
//         const allDataSourceIds = Array.from(new Set(services.flatMap(s => s.dataSources)));
//         getLabelValues({
//             variables: {
//                 input: {
//                     tenantId: currentTenant?.id,
//                     provider,
//                     labelKey: key,
//                     period: period.toUpperCase(),
//                     dataSourceIds: allDataSourceIds,
//                 }
//             }
//         });
//     }, [getLabelValues, provider, currentTenant, services, labelValuesData, loadingLabelKey, period]);

//     const handleDeleteLabelChip = (serviceId: string, keyToDelete: string, valueToDelete: string) => {
//         setServices(prevServices =>
//           prevServices.map(service => {
//             if (service.id === serviceId) {
//               const updatedLabels = service.label
//                 .map(label => {
//                   if (label.key === keyToDelete) {
//                     const newValues = label.values.filter(v => v !== valueToDelete);
//                     if (newValues.length > 0) {
//                       return { ...label, values: newValues };
//                     }
//                     return null;
//                   }
//                   return label;
//                 })
//                 .filter((label): label is LabelFilterPair => label !== null);

//               return { ...service, label: updatedLabels };
//             }
//             return service;
//           })
//         );
//       };

//       const handleAddService = useCallback(() => {
//         const newService = { id: `service_${Date.now()}`, ...getInitialService() };
//         setServices(prev => [...prev, newService]);
//       }, [getInitialService, setServices]);

//       const handleDeleteService = useCallback((idToDelete: string) => {
//         setServices(p => p.filter(s => s.id !== idToDelete));
//       }, [setServices]);

//       const handleLabelMenuOpen = (event: React.MouseEvent<HTMLElement>, serviceId: string) => {
//           setLabelMenuAnchor(event.currentTarget);
//           setOpenPopper({ type: 'label', serviceId });
//       };

//       const handleLabelMenuClose = () => {
//         setOpenPopper(null);
//         setLabelMenuAnchor(null);
//       };

//       const handleLabelApply = (newFilters: LabelFilterPair[]) => {
//           if (openPopper?.type === 'label') {
//               handleUpdateService(openPopper.serviceId, 'label', newFilters);
//           }
//           handleLabelMenuClose();
//       };

//       const activeServiceForLabel = services.find(s => s.id === openPopper?.serviceId);

//     const renderMultiSelectPopper = () => {
//       if (!openPopper || !popperAnchor) return null;
//       const checkboxStyle = { '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: '#0064FF' } };

//       if (openPopper.type === 'dataSources') {
//         const options = dataSourceOptions.map(s => s.id);
//         const optionLabels = dataSourceOptions;
//         const isAllSelected = tempSelections.length === options.length && options.length > 0;
//         const isSomeSelected = tempSelections.length > 0 && !isAllSelected;
//         const handleSelectAll = () => setTempSelections(isAllSelected ? [] : options);
//         return (
//           <Popper open={true} anchorEl={popperAnchor} placement="bottom-start" transition sx={{zIndex: 1200}} modifiers={[{name: 'offset', options: {offset: [0, 8]}}]}>
//             {({ TransitionProps }) => (
//                 <Grow {...TransitionProps} style={{ transformOrigin: 'top left' }}>
//                     <Paper ref={popperPaperRef} sx={{ width: popperAnchor?.offsetWidth, borderRadius: '12px', backgroundColor: '#FFFFFF', boxShadow: '0 4px 20px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
//                         <ClickAwayListener onClickAway={handlePopperClose}>
//                             <Box>
//                                 <FormControlLabel
//                                     label={<Typography sx={{ fontSize: '13px', color: isAllSelected ? '#333333' : '#666666' }}>All</Typography>}
//                                     sx={{width: '100%', m:0, px: 2, py: 0.5}}
//                                     control={ <Checkbox size="small" checked={isAllSelected} indeterminate={isSomeSelected} onChange={handleSelectAll} sx={checkboxStyle}/> }
//                                 />
//                                 <Box sx={{ maxHeight: '140px', overflow: 'auto' }}>
//                                   {optionLabels.map(option => (
//                                     <Tooltip
//                                       key={option.id}
//                                       title={option.isSyncing ? "This data source is currently syncing and cannot be selected." : ""}
//                                       placement="right"
//                                       arrow
//                                     >
//                                       <div>
//                                         <FormControlLabel
//                                             sx={{width: '100%', m:0, px:2, py:0.5}}
//                                             disabled={option.isSyncing}
//                                             control={
//                                               <Checkbox
//                                                 size="small"
//                                                 checked={tempSelections.includes(option.id)}
//                                                 onChange={() => setTempSelections(p => p.includes(option.id) ? p.filter(i => i !== option.id) : [...p, option.id])}
//                                                 sx={checkboxStyle}
//                                               />
//                                             }
//                                             label={
//                                               <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
//                                                 <Typography sx={{ fontSize: '13px', color: tempSelections.includes(option.id) ? '#333333' : '#666666' }}>
//                                                   {option.name}
//                                                 </Typography>
//                                                 {option.isSyncing && (
//                                                   <Box component="span" sx={{display: 'inline-flex', alignItems: 'center', height: '18px', px: 0.8, borderRadius: '10px', backgroundColor: 'rgba(255, 193, 7, 0.2)', color: 'rgba(180, 120, 0)', fontSize: '11px', fontWeight: 500 }}>
//                                                     Syncing
//                                                   </Box>
//                                                 )}
//                                               </Box>
//                                             }
//                                         />
//                                       </div>
//                                     </Tooltip>
//                                   ))}
//                                 </Box>
//                                 <Divider />
//                                 <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', p: 1 }}>
//                                     <Button onClick={() => setTempSelections([])} sx={{ textTransform: 'none', color: '#999999' }}>Clear</Button>
//                                     <Button onClick={handleApply} sx={{ textTransform: 'none', color: '#0064FF' }}>Apply</Button>
//                                 </Box>
//                             </Box>
//                         </ClickAwayListener>
//                     </Paper>
//                 </Grow>
//             )}
//         </Popper>
//         );
//       }

//     if (openPopper.type === 'projectIds') {
//           const options = projectOptions;
//           const isAllSelected = tempSelections.length === options.length && options.length > 0;
//           const isSomeSelected = tempSelections.length > 0 && !isAllSelected;
//           const handleSelectAll = () => setTempSelections(isAllSelected ? [] : options);
//           return (
//               <Popper open={true} anchorEl={popperAnchor} placement="bottom-start" transition sx={{zIndex: 1200}} modifiers={[{name: 'offset', options: {offset: [0, 8]}}]}>
//                   {({ TransitionProps }) => (
//                       <Grow {...TransitionProps} style={{ transformOrigin: 'top left' }}>
//                           <Paper ref={popperPaperRef} sx={{ width: popperAnchor?.offsetWidth, borderRadius: '12px', backgroundColor: '#FFFFFF', boxShadow: '0 4px 20px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
//                               <ClickAwayListener onClickAway={handlePopperClose}>
//                                   <Box>
//                                       <FormControlLabel
//                                           label={<Typography sx={{ fontSize: '13px', color: isAllSelected ? '#333333' : '#666666' }}>All</Typography>}
//                                           sx={{width: '100%', m:0, px: 2, py: 0.5}}
//                                           control={ <Checkbox size="small" checked={isAllSelected} indeterminate={isSomeSelected} onChange={handleSelectAll} sx={checkboxStyle}/> }
//                                       />
//                                       <Box sx={{ maxHeight: '140px', overflow: 'auto' }}>
//                                           {options.map(option => (
//                                               <FormControlLabel
//                                                   key={option}
//                                                   sx={{width: '100%', m:0, px:2, py:0.5}}
//                                                   control={ <Checkbox size="small" checked={tempSelections.includes(option)} onChange={() => setTempSelections(p => p.includes(option) ? p.filter(i => i !== option) : [...p, option])} sx={checkboxStyle}/> }
//                                                   label={<Typography sx={{ fontSize: '13px', color: tempSelections.includes(option) ? '#333333' : '#666666' }}>{option}</Typography>}
//                                               />
//                                           ))}
//                                       </Box>
//                                       <Divider />
//                                       <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', p: 1 }}>
//                                           <Button onClick={() => setTempSelections([])} sx={{ textTransform: 'none', color: '#999999' }}>Clear</Button>
//                                           <Button onClick={handleApply} sx={{ textTransform: 'none', color: '#0064FF' }}>Apply</Button>
//                                       </Box>
//                                   </Box>
//                               </ClickAwayListener>
//                           </Paper>
//                       </Grow>
//                   )}
//               </Popper>
//           );
//       }

//     if (openPopper.type === 'regions') {
//         const filteredActive = activeRegions.filter(r => r.toLowerCase().includes(regionSearchTerm.toLowerCase()));
//         const filteredAvailable = availableRegions.filter(r => r.toLowerCase().includes(regionSearchTerm.toLowerCase()));
//         const handleRegionToggle = (value: string) => {
//             const newSelection = tempSelections.includes(value)
//                 ? tempSelections.filter(v => v !== value)
//                 : [...tempSelections, value];
//             setTempSelections(newSelection);
//         };
//         const handleSelectAll = (itemsToToggle: string[], shouldSelect: boolean) => {
//             const otherSelections = tempSelections.filter(s => !itemsToToggle.includes(s));
//             const newSelections = shouldSelect
//                 ? Array.from(new Set([...otherSelections, ...itemsToToggle]))
//                 : otherSelections;
//             setTempSelections(newSelections);
//         };
//         const isAllActiveSelected = filteredActive.length > 0 && filteredActive.every(r => tempSelections.includes(r));
//         const isSomeActiveSelected = filteredActive.some(r => tempSelections.includes(r)) && !isAllActiveSelected;
//         const isAllAvailableSelected = filteredAvailable.length > 0 && filteredAvailable.every(r => tempSelections.includes(r));
//         const isSomeAvailableSelected = filteredAvailable.some(r => tempSelections.includes(r)) && !isAllAvailableSelected;
//         const renderRegionOption = (option: string) => (
//             <FormControlLabel
//                 key={option}
//                 sx={{ width: '100%', m: 0, px: 2, py: 0.5 }}
//                 control={<Checkbox size="small" checked={tempSelections.includes(option)} onChange={() => handleRegionToggle(option)} sx={checkboxStyle}/>}
//                 label={<Typography sx={{ fontSize: '13px', color: tempSelections.includes(option) ? '#333333' : '#666666' }}>{option}</Typography>}
//             />
//         );
//         return (
//             <Popper open={true} anchorEl={popperAnchor} placement="bottom-start" transition sx={{zIndex: 1200}} modifiers={[{name: 'offset', options: {offset: [0, 8]}}]}>
//                 {({ TransitionProps }) => (
//                     <Grow {...TransitionProps} style={{ transformOrigin: 'top left' }}>
//                         <Paper ref={popperPaperRef} sx={{ width: popperAnchor?.offsetWidth, borderRadius: '12px', backgroundColor: '#FFFFFF', boxShadow: '0 4px 20px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
//                             <ClickAwayListener onClickAway={handlePopperClose}>
//                                 <Box>
//                                     <Box sx={{ p: 2, borderBottom: '1px solid #f0f0f0' }}>
//                                         <TextField
//                                             autoFocus placeholder="Search by region" value={regionSearchTerm}
//                                             onChange={(e) => setRegionSearchTerm(e.target.value)}
//                                             onKeyDown={(e) => e.stopPropagation()} fullWidth variant="outlined"
//                                             InputProps={{
//                                                 startAdornment: (
//                                                     <InputAdornment position="start" sx={{ ml: -0.5 }}>
//                                                     <img src="/icons/search_icon.svg" alt="Search" style={{ width: '16px', height: '16px' }} />
//                                                     </InputAdornment>
//                                                 ),
//                                                 sx: { height: '32px', borderRadius: '10px', backgroundColor: 'rgba(0, 0, 0, 0.04)', '& .MuiOutlinedInput-notchedOutline': { border: 'none' }, '& .MuiInputBase-input::placeholder': { fontSize: '14px', fontWeight: 300 } },
//                                             }}
//                                         />
//                                     </Box>
//                                     <Box sx={{ maxHeight: '200px', overflowY: 'auto' }}>
//                                         {filteredActive.length > 0 && (
//                                             <>
//                                                 <ListSubheader sx={{ bgcolor: '#F9FAFB', lineHeight: '32px', fontSize: '13px', fontWeight: 600 }}>Active Regions</ListSubheader>
//                                                 <FormControlLabel
//                                                     sx={{ width: '100%', m: 0, px: 2, py: 0.5 }}
//                                                     label={<Typography sx={{ fontSize: '13px', fontWeight: 500 }}>All</Typography>}
//                                                     control={<Checkbox size="small" checked={isAllActiveSelected} indeterminate={isSomeActiveSelected} onChange={(e) => handleSelectAll(filteredActive, e.target.checked)} sx={checkboxStyle}/>}
//                                                 />
//                                                 {filteredActive.map(renderRegionOption)}
//                                             </>
//                                         )}
//                                         {filteredAvailable.length > 0 && (
//                                             <>
//                                                 <ListSubheader sx={{ bgcolor: '#F9FAFB', lineHeight: '32px', fontSize: '13px', fontWeight: 600 }}>Available Regions</ListSubheader>
//                                                 <FormControlLabel
//                                                     sx={{ width: '100%', m: 0, px: 2, py: 0.5 }}
//                                                     label={<Typography sx={{ fontSize: '13px', fontWeight: 500 }}>All</Typography>}
//                                                     control={<Checkbox size="small" checked={isAllAvailableSelected} indeterminate={isSomeAvailableSelected} onChange={(e) => handleSelectAll(filteredAvailable, e.target.checked)} sx={checkboxStyle}/>}
//                                                 />
//                                                 {filteredAvailable.map(renderRegionOption)}
//                                             </>
//                                         )}
//                                     </Box>
//                                     <Divider />
//                                     <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', p: 1 }}>
//                                         <Button onClick={() => setTempSelections([])} sx={{ textTransform: 'none', color: '#999999' }}>Clear</Button>
//                                         <Button onClick={handleApply} sx={{ textTransform: 'none', color: '#0064FF' }}>Apply</Button>
//                                     </Box>
//                                 </Box>
//                             </ClickAwayListener>
//                         </Paper>
//                     </Grow>
//                 )}
//             </Popper>
//         );
//     }

//       return null;
//     };

//     return (
//         <Tooltip title={disabled ? "Please complete Budget Details before configuring scope." : ""}>
//             <Box sx={{ p: { xs: 1, sm: 2 }, bgcolor: '#FFFFFF', borderRadius: '10px', border: '1px solid #F3F3F3', position: 'relative' }}>
//                 {disabled && (
//                   <Box sx={{
//                       position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
//                       backgroundColor: 'rgba(255, 255, 255, 0.7)', zIndex: 1, cursor: 'not-allowed'
//                   }} />
//               )}
//             <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
//               <img src="/icons/Scope-Config.svg" alt="Scope Configuration" style={{ marginRight: '12px', width: '28px', height: '28px' }} />
//               <Typography variant="h6" sx={{ fontWeight: 500, fontSize: '14px', color: '#333333', fontFamily: 'Inter' }}>Scope Configuration</Typography>
//             </Box>
//             <Divider sx={{ borderColor: '#F3F3F3', mx: -3 }} />

//             <Box sx={{ display: 'flex', gap: 2, mt: 2, px: 1 }}>
//               <Box
//                 sx={providerBoxStyle(provider === 'GCP', false)}
//                 onClick={() => {
//                   if (disabled) return;
//                   setProvider('GCP');
//                   if(services.length === 0) {
//                     setServices([ { id: `service_${Date.now()}`, ...getInitialService() } ])
//                   };
//                 }}
//               >
//                 <Box sx={{ display: 'flex', alignItems: 'center' }}><img src="/icons/google-cloud-icon-2048x1646-7admxejz.svg" alt="GCP" style={{ marginRight: 8, width: 20, height: 20 }} /><Typography variant="body2" sx={{ fontFamily: 'Inter', fontSize: '13px', fontWeight: 400 }}>Google Cloud Platform</Typography></Box>
//                 <Radio checked={provider === 'GCP'} size="small" sx={{ p: 0, transform: 'scale(0.85)', '&.Mui-checked': { color: '#0064FF' } }}/>
//               </Box>
//               <Box sx={providerBoxStyle(false, true)}><Box sx={{ display: 'flex', alignItems: 'center' }}><img src="/icons/aws.svg" alt="AWS" style={{ marginRight: 8, width: 20, height: 20 }}/><Typography variant="body2" sx={{ fontFamily: 'Inter', fontSize: '13px', fontWeight: 400 }}>Amazon Web Services</Typography></Box><Radio checked={false} size="small" disabled={true} sx={{ p: 0, transform: 'scale(0.85)' }}/></Box>
//               <Box sx={providerBoxStyle(false, true)}><Box sx={{ display: 'flex', alignItems: 'center' }}><img src="/icons/azure2.svg" alt="Azure" style={{ marginRight: 8, width: 20, height: 20 }}/><Typography variant="body2" sx={{ fontFamily: 'Inter', fontSize: '13px', fontWeight: 400 }}>Microsoft Azure</Typography></Box><Radio checked={false} size="small" disabled={true} sx={{ p: 0, transform: 'scale(0.85)' }}/></Box>
//             </Box>
//             {errors.provider && <Typography color="error" variant="caption" sx={{ display: 'block', mt: 1, px: 1 }}>{errors.provider}</Typography>}

//             {provider === 'GCP' && (
//               <Box sx={{ width: '100%', bgcolor: '#F9FAFB', borderRadius: '10px', mt: 2, p: 2 }}>
//                 <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
//                   <Button onClick={handleAddService} disabled={services.length > 0 && services[services.length-1].serviceTypes.length === 0} sx={{ textTransform: 'none', color: '#0064FF', fontFamily: 'Inter', fontWeight: 500, fontSize: '14px' }}>
//                     + Add More
//                   </Button>
//                 </Box>

//                 {services.map((service, index) => {
//                   const isProjectDisabled = service.dataSources.length === 0 || optionsLoading;
//                   const isRegionDisabled = service.projectIds.length === 0 || optionsLoading;
//                   const isServiceDisabled = service.regions.length === 0 || optionsLoading;

//                   const dataSourceButtonText = service.dataSources.length === dataSourceOptions.length && dataSourceOptions.length > 0 ? 'All' : service.dataSources.length > 0 ? `${service.dataSources.length} selected` : 'Select data sources';
//                   const projectButtonText = service.projectIds.length === projectOptions.length && projectOptions.length > 0 ? 'All' : service.projectIds.length > 0 ? `${service.projectIds.length} selected` : 'Select projects';
//                   const allRegionsCount = activeRegions.length + availableRegions.length;
//                   const regionButtonText = service.regions.length === allRegionsCount && allRegionsCount > 0 ? 'All' : service.regions.length > 0 ? `${service.regions.length} selected` : 'Select regions';
//                   const labelButtonText = service.label.length === 1 ? '1 filter applied' : service.label.length > 0 ? `${service.label.length} filters applied` : 'Select Label';

//                   const getButtonSx = (hasSelection: boolean) => ({
//                       width: '100%', height: '36px', backgroundColor: '#FFFFFF', border: `1px solid #E0E0E0`, borderRadius: '8px',
//                       fontFamily: 'Inter', fontWeight: hasSelection ? 500 : 400, fontSize: '13px',
//                       color: hasSelection ? '#333333' : '#999999', lineHeight: '18px', letterSpacing: '0%',
//                       textTransform: 'none', justifyContent: 'space-between', '&:hover': { borderColor: '#0064FF' },
//                       '&.Mui-disabled': { backgroundColor: '#F3F3F3', border: '1px solid #D3D3D3' }
//                   });

//                   const showLabelButton = service.serviceTypes.length > 0;
//                   const isKubeSelected = service.serviceTypes.includes('Kubernetes Engine');
//                   const showFullKubeFields = isKubeSelected && activeServices.includes('Kubernetes Engine');
//                   return (
//                     <Box key={service.id}>
//                       <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: 2, alignItems: 'flex-end' }}>
//                         <Box>
//                           <Typography sx={{ fontFamily: 'Inter', fontWeight: 400, fontSize: '13px', color: optionsLoading ? '#A0A0A0' : '#333333', mb: 0.5 }}>Data Source<span style={{ color: 'red' }}>*</span></Typography>
//                           <Button ref={el => buttonRefs.current[`${service.id}-dataSources`] = el} onClick={(e) => handlePopperOpen(e, 'dataSources', service.id, service.dataSources)} endIcon={<ExpandMoreIcon />} sx={getButtonSx(service.dataSources.length > 0)} disabled={optionsLoading}>{dataSourceButtonText}</Button>
//                         </Box>

//                         <Box>
//                           <Typography sx={{ fontFamily: 'Inter', fontWeight: 400, fontSize: '13px', color: isProjectDisabled ? '#A0A0A0' : '#333333', mb: 0.5 }}>Project<span style={{ color: 'red' }}>*</span></Typography>
//                           <Button
//                             ref={el => buttonRefs.current[`${service.id}-projectIds`] = el}
//                             disabled={isProjectDisabled}
//                             onClick={(e) => handlePopperOpen(e, 'projectIds', service.id, service.projectIds)}
//                             endIcon={<ExpandMoreIcon />}
//                             sx={getButtonSx(service.projectIds.length > 0)}
//                           >
//                             {projectButtonText}
//                           </Button>
//                         </Box>

//                         <Box>
//                           <Typography sx={{ fontFamily: 'Inter', fontWeight: 400, fontSize: '13px', color: isRegionDisabled ? '#A0A0A0' : '#333333', mb: 0.5 }}>Region<span style={{ color: 'red' }}>*</span></Typography>
//                           <Button ref={el => buttonRefs.current[`${service.id}-regions`] = el} disabled={isRegionDisabled} onClick={(e) => handlePopperOpen(e, 'regions', service.id, service.regions)} endIcon={<ExpandMoreIcon />} sx={getButtonSx(service.regions.length > 0)}>{regionButtonText}</Button>
//                         </Box>

//                         <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
//                            <Box sx={{flexGrow: 1}}>
//                               <SectionedMultiSelectDropdown
//                                   ref={el => buttonRefs.current[`${service.id}-service`] = el}
//                                   label="Service"
//                                   options={{ active: activeServices, available: availableServices }}
//                                   selectedValues={service.serviceTypes}
//                                   onChange={(newValues) => handleUpdateService(service.id, 'serviceTypes', newValues)}
//                                   disabled={isServiceDisabled}
//                               />
//                            </Box>
//                           <IconButton onClick={() => handleDeleteService(service.id)} disabled={services.length <= 1} sx={{ mb: '4px' }}><img src={(services.length <= 1) ? "/icons/delete_icon_disabled.svg" : "/icons/delete_icon.svg"} alt="Delete Service" style={{ width: '17px', height: '17px' }} /></IconButton>
//                         </Box>

//                         {showLabelButton && (
//                           <Box>
//                             <Typography sx={{ fontFamily: 'Inter', fontWeight: 400, fontSize: '13px', color: isServiceDisabled ? '#A0A0A0' : '#333333', mb: 0.5 }}>Label</Typography>
//                             <Button onClick={(e) => handleLabelMenuOpen(e, service.id)} disabled={isServiceDisabled} endIcon={<ExpandMoreIcon />} variant="outlined" sx={getButtonSx(service.label.length > 0)}>{labelButtonText}</Button>
//                           </Box>
//                         )}

//                         {showFullKubeFields && (
//                           <>
//                             <Box>
//                               <MultiSelectDropdown label="Cluster" options={k8sClusters} selectedValues={service.cluster} onChange={(newValues: string[]) => handleUpdateService(service.id, 'cluster', newValues)} disabled={isServiceDisabled} width="100%" />
//                             </Box>
//                             <Box>
//                               <MultiSelectDropdown label="Namespace" options={k8sNamespaces} selectedValues={service.namespace} onChange={(newValues: string[]) => handleUpdateService(service.id, 'namespace', newValues)} disabled={isServiceDisabled} width="100%" />
//                             </Box>
//                           </>
//                         )}
//                       </Box>
//                       {service.label.length > 0 && (
//                         <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
//                           {service.label.flatMap(labelItem =>
//                             labelItem.values.map(value => (
//                               <Chip
//                                 key={`${labelItem.key}-${value}`}
//                                 label={`${labelItem.key}: ${value}`}
//                                 size="small"
//                                 onDelete={() => handleDeleteLabelChip(service.id, labelItem.key, value)}
//                                 sx={{
//                                   backgroundColor: ' #F3F8FE',
//                                   color: '#333333',
//                                   fontFamily: 'Inter',
//                                   fontSize: '12px',
//                                   fontWeight: 500,
//                                   borderRadius: '8px',
//                                   '.MuiChip-deleteIcon': {
//                                       color: '#333333',
//                                       fontSize: '16px',
//                                       '&:hover': {
//                                         color: 'red',
//                                       },
//                                   },
//                                 }}
//                               />
//                             ))
//                           )}
//                         </Box>
//                       )}
//                       {index < services.length - 1 && <Divider sx={{ my: 2, mx: -2 }} />}
//                     </Box>
//                   )
//                 })}

//                 {renderMultiSelectPopper()}

//                 <LabelFilterMenu
//                   open={openPopper?.type === 'label'}
//                   anchorEl={labelMenuAnchor}
//                   onClose={handleLabelMenuClose}
//                   onApply={handleLabelApply}
//                   appliedFilters={activeServiceForLabel?.label || []}
//                   labelKeys={labelKeys}
//                   labelValues={labelValuesData}
//                   onFetchLabelValues={handleFetchLabelValues}
//                   loadingLabelKey={loadingLabelKey}
//                 />
//               </Box>
//             )}
//             </Box>
//         </Tooltip>
//     );
// };

// export default ScopeConfig;